import { isOwnPayerViewer } from './payerViewerIdentity';
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { LaunchMemberMixCard } from "./LaunchMemberMixCard";
import {
  Archive,
  Activity,
  ArrowDown,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  Bell,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Cloud,
  Copy,
  Database,
  Download,
  Edit3,
  Eye,
  FileArchive,
  FileText,
  FileSpreadsheet,
  HardDrive,
  Link2,
  LogOut,
  Mail,
  MailCheck,
  Menu,
  RefreshCw,
  Rocket,
  Search,
  Settings,
  Send,
  Shield,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { hasSupabaseConfig, publicSupabaseUrl, supabase } from "./supabase";
import { readableErrorMessage } from "./errorMessage";
import { MadeiraOverview } from "./MadeiraOverview";
import { NotificationSettingsPage } from "./NotificationSettingsPage";
import { DashboardMailingLists } from "./DashboardMailingLists";
import type {
  AuditEvent,
  AnnualRevenueRow,
  Backup,
  BillingSwitch,
  BillingSubscription,
  BillingInvoice,
  CustomerBillingImportCandidate,
  CustomerSubscriptionImportCandidate,
  Customer,
  CustomerSubscription,
  DashboardMemberChange,
  DashboardTotals,
  EscapeSubscriberMetrics,
  EmailSyncLog,
  ExistingBillingCandidate,
  GrowthMetrics,
  IntegrationJob,
  IntegrationLog,
  Launch,
  LaunchOverview,
  LaunchParticipant,
  LaunchSignup,
  LaunchSignupMetrics,
  Plan,
  ProductMetric,
  ProductRetentionMetric,
  Profile,
  Viewer,
} from "./types";
import { displayName, formatCurrency, formatDate, formatDateTime, formatDateTimeSeconds, formatNumber } from "./format";
import { LaunchInsights } from "./LaunchInsights";
import { SubscriptionCenter } from "./SubscriptionCenter";
import { ArrearsOverview } from './ArrearsOverview';
import { ArrearsReviewQueue, type ReviewQueueData } from './ArrearsReviewQueue';
import { ArrearsFollowUp, type ArrearsFollowUpCase } from './ArrearsFollowUp';
import type { SubscriptionEntry } from "./subscriptionModel";
import { SubscriptionPlanningModal, SubscriptionInvoiceModal } from "./SubscriptionManagementModals";
import { isLaunchSignupTab, launchSignupTabCounts, matchesLaunchSignupTab } from "./launchSignupTabs";

const AUTH_PROFILE_TIMEOUT_MS = 10_000;
const DASHBOARD_QUERY_TIMEOUT_MS = 10_000;
const DASHBOARD_DETAILS_TIMEOUT_MS = 20_000;

type PageKey =
  | "dashboard"
  | "customers"
  | "launches"
  | "cancellations"
  | "viewers"
  | "plans"
  | "arrears"
  | "exports"
  | "backups"
  | "email-sync"
  | "activity"
  | "notifications"
  | "connections"
  | "users"
  | "settings";

const menuItems: Array<{ key: PageKey; label: string; icon: typeof BarChart3; superOnly?: boolean }> = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "customers", label: "Klanten", icon: Users },
  { key: "launches", label: "Lanceringen", icon: Rocket },
  { key: "cancellations", label: "Opzeggingen", icon: CalendarClock },
  { key: "viewers", label: "Meekijkers", icon: Eye },
  { key: "plans", label: "Abonnementen", icon: FileSpreadsheet },
  { key: "arrears", label: "Betaalachterstand", icon: CalendarClock },
  { key: "exports", label: "Klanten export", icon: Download },
  { key: "backups", label: "Backups", icon: HardDrive, superOnly: true },
  { key: "email-sync", label: "E-mail sync", icon: MailCheck },
  { key: "activity", label: "Activiteitenlog", icon: Activity },
  { key: "notifications", label: "Meldingen", icon: Bell },
  { key: "connections", label: "Koppelingen", icon: Link2, superOnly: true },
  { key: "users", label: "Gebruikers", icon: Shield, superOnly: true },
  { key: "settings", label: "Instellingen", icon: Settings, superOnly: true },
];

const chartColors = ["#2D6A4F", "#8B6F5E", "#C4854C", "#5B8C5A", "#6B5E57", "#C45B4A"];
const MEMBER_LIST_LIMIT = 2000;
const OUTSIDE_LAUNCH_NAME = "Buiten lancering – heractivatie";
const STOPPED_DUE_TO_UNPAID_INVOICE_LABEL = "STOPGEZET = Facturatie (InvoiceNinja of WeFact) uitgezet vanwege betaalachterstand";
const pageSlugs: Record<PageKey, string> = {
  dashboard: "dashboard",
  customers: "klanten",
  launches: "lanceringen",
  cancellations: "opzeggingen",
  viewers: "meekijkers",
  plans: "abonnementen",
  arrears: "betaalachterstand",
  exports: "exports",
  backups: "backups",
  "email-sync": "email-sync",
  activity: "activiteitenlog",
  notifications: "meldingen",
  connections: "koppelingen",
  users: "gebruikers",
  settings: "instellingen",
};
const pagesBySlug = Object.fromEntries(Object.entries(pageSlugs).map(([key, slug]) => [slug, key])) as Record<string, PageKey>;

type LogRow = {
  id: string;
  when: string;
  personName: string;
  personEmail: string;
  title: string;
  status: string;
  detail: string;
  details?: string[];
  meta?: string;
};

type DashboardProductRow = ProductMetric & {
  memberPercent: number;
  revenuePercent: number;
  arr_excl_vat: number;
  cashflowAmount: number;
};

type RevenueForecastRow = {
  monthKey: string;
  monthLabel: string;
  monthlyPayments: number;
  quarterlyPayments: number;
  yearlyPayments: number;
  total: number;
};

type RevenueMix = {
  total: number;
  euro: number;
  bitcoin: number;
  euroPercent: number;
  bitcoinPercent: number;
};

type DemographicRow = {
  key: string;
  label: string;
  count: number;
  percent: number;
};

type RevenueProductSortKey = "product" | "monthly" | "yearly";
type RevenueProductSort = { key: RevenueProductSortKey; direction: SortDirection };
type RetentionSortKey = "members" | "product" | "months" | "retention";
type RetentionSort = { key: RetentionSortKey; direction: SortDirection };
type DemographicSortKey = "label" | "count" | "percent";
type DemographicSort = { key: DemographicSortKey; direction: SortDirection };
type AnnualRevenueSortKey = "year" | "members" | "revenue";
type AnnualRevenueSort = { key: AnnualRevenueSortKey; direction: SortDirection };

type EmailRole = "factuur e-mail" | "mailing e-mail" | "Thinkific e-mail";

type EmailConflict = {
  customer: Customer;
  email: string;
  enteredRole: EmailRole;
  existingRole: EmailRole;
};

type ViewerEmailConflict = {
  viewer: Viewer;
  email: string;
  enteredRole: EmailRole;
};

type ViewerAddConflictState = {
  activeCustomer: { customer: Customer; role: EmailRole } | null;
  activeViewer: Viewer | null;
  warnings: Array<{ type: "customer" | "viewer"; customer?: Customer; viewer?: Viewer; role?: EmailRole }>;
};

type EmailConflictState = {
  billingConflict: EmailConflict | null;
  warnings: EmailConflict[];
  activeViewerConflict: ViewerEmailConflict | null;
  viewerWarnings: ViewerEmailConflict[];
};

type EditingCustomerState = {
  customer: Customer;
  reactivatePreset?: boolean;
};

type CustomerProfileSection = "contact" | "address" | "administration" | "membership" | "payment" | "notes";

type EditCustomerSaveResult = {
  kind: "saved" | "payment_arrears_resolved";
  startDate?: string | null;
  syncError?: string | null;
};

export function App() {
  const [sessionReady, setSessionReady] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [passwordSetupRequired, setPasswordSetupRequired] = useState(false);
  const [page, setPage] = useState<PageKey>(() => pageFromHash() || "dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      setSessionReady(true);
      return;
    }

    const hasPasswordSetupUrl = isPasswordSetupUrl();
    let sessionReadyFallback: number | undefined;
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      const email = session?.user.email ?? null;
      setUserEmail(email);
      if (session?.user.id && (event === "PASSWORD_RECOVERY" || hasPasswordSetupUrl)) {
        setPasswordSetupRequired(true);
      }
      if (session?.user.id) {
        // Supabase holds its auth lock while this callback runs. Schedule the
        // profile query after the callback returns to avoid an auth/query deadlock.
        window.setTimeout(() => {
          void withTimeout(loadProfile(session.user.id, email), AUTH_PROFILE_TIMEOUT_MS, "Profiel laden")
            .catch((error) => setLoadError(error instanceof Error ? error.message : "Profiel laden mislukt"));
        }, 0);
      } else {
        setProfile(null);
      }
      if (sessionReadyFallback !== undefined) window.clearTimeout(sessionReadyFallback);
      setSessionReady(true);
    });

    // INITIAL_SESSION is emitted by Supabase after the listener is registered.
    // Keep a bounded fallback so a stalled auth request cannot leave a full-page
    // spinner forever.
    sessionReadyFallback = window.setTimeout(() => setSessionReady(true), AUTH_PROFILE_TIMEOUT_MS);

    return () => {
      if (sessionReadyFallback !== undefined) window.clearTimeout(sessionReadyFallback);
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    function clearBadgeWhenVisible() {
      if (document.visibilityState === "visible") void clearAppNotificationBadge();
    }

    function handleServiceWorkerMessage(event: MessageEvent) {
      if (event.data?.type === "PUSH_NOTIFICATION_RECEIVED") clearBadgeWhenVisible();
    }

    clearBadgeWhenVisible();
    window.addEventListener("focus", clearBadgeWhenVisible);
    document.addEventListener("visibilitychange", clearBadgeWhenVisible);
    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);
    return () => {
      window.removeEventListener("focus", clearBadgeWhenVisible);
      document.removeEventListener("visibilitychange", clearBadgeWhenVisible);
      navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, []);

  useEffect(() => {
    function onHashChange() {
      const nextPage = pageFromHash();
      if (nextPage) setPage(nextPage);
    }
    window.addEventListener("hashchange", onHashChange);
    onHashChange();
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  async function loadProfile(userId: string, email: string | null) {
    if (!supabase) return;
    const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
    if (error) {
      setLoadError(error.message);
      return;
    }
    if (data) {
      setProfile(data as Profile);
      return;
    }
    setProfile({
      id: userId,
      email: email ?? "",
      display_name: null,
      role: "readonly",
      can_view_financials: false,
      is_active: true,
      last_seen_at: null,
    });
  }

  const canViewFinancials = Boolean(profile?.role === "super_admin" || profile?.can_view_financials);
  const canViewMemberExports = Boolean(canViewFinancials || profile?.can_view_member_exports);
  const canMutateMembers = Boolean(profile?.role === "super_admin" || profile?.role === "admin");
  const isSuperAdmin = profile?.role === "super_admin";
  const visibleMenu = menuItems.filter((item) => (!item.superOnly || profile?.role === "super_admin") && (item.key !== "exports" || canViewMemberExports));
  const activePage = !isSuperAdmin && menuItems.find((item) => item.key === page)?.superOnly ? "dashboard" : page;

  if (!sessionReady) {
    return <BootScreen label="Beheertool laden" />;
  }

  if (!hasSupabaseConfig) {
    return <ConfigMissing />;
  }

  if (!userEmail) {
    return <Login />;
  }

  if (passwordSetupRequired) {
    return <SetPassword userEmail={userEmail} onDone={() => setPasswordSetupRequired(false)} />;
  }

  return (
    <div className="app-shell">
      <header className="top-header">
        <button className="icon-button mobile-only" onClick={() => setSidebarOpen(true)} aria-label="Menu openen">
          <Menu size={19} />
        </button>
        <div className="top-brand">
          <img src="/brede-horizon-logo.png" alt="" />
          <span>Brede Horizon Beheertool</span>
        </div>
        <div />
      </header>

      {sidebarOpen && <button className="sidebar-overlay mobile-only" onClick={() => setSidebarOpen(false)} aria-label="Menu sluiten" />}

      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-head">
          <strong>Beheer</strong>
          <button className="icon-button mobile-only" onClick={() => setSidebarOpen(false)} aria-label="Menu sluiten">
            <X size={18} />
          </button>
        </div>
        <nav>
          {visibleMenu.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                className={`nav-item ${activePage === item.key ? "active" : ""}`}
                onClick={() => {
                  navigateToPage(item.key);
                  setSidebarOpen(false);
                }}
              >
                <Icon size={17} />
                <span>{item.label}</span>
              </button>
            );
          })}
          <button
            className="nav-item"
            onClick={() => {
              setSidebarOpen(false);
              void supabase?.auth.signOut({ scope: "local" });
            }}
          >
            <LogOut size={17} />
            <span>Uitloggen</span>
          </button>
        </nav>
      </aside>

      <main className="main-content">
        {loadError && <div className="banner error">{loadError}</div>}
        {activePage === "dashboard" && <Dashboard canViewFinancials={canViewFinancials} />}
        {activePage === "customers" && <CustomersPage canViewFinancials={canViewFinancials} canMutateMembers={canMutateMembers} />}
        {activePage === "launches" && <LaunchesPage canViewFinancials={canViewFinancials} canMutateMembers={canMutateMembers} />}
        {activePage === "cancellations" && <CancellationsPage canMutateMembers={canMutateMembers} />}
        {activePage === "viewers" && <ViewersPage canMutateMembers={canMutateMembers} />}
        {activePage === "plans" && <PlansPage canViewFinancials={canViewFinancials} canManagePlans={isSuperAdmin} />}
        {activePage === "arrears" && <ArrearsOverview canManage={canMutateMembers} canFinance={canMutateMembers || canViewFinancials} isSuperAdmin={isSuperAdmin}/>}
        {activePage === "exports" && <ExportsPage canViewFinancials={canViewMemberExports} isSuperAdmin={isSuperAdmin} />}
        {activePage === "backups" && <BackupsPage />}
        {activePage === "email-sync" && <EmailSyncPage />}
        {activePage === "activity" && <ActivityPage />}
        {activePage === "notifications" && <NotificationSettingsPage />}
        {activePage === "connections" && <ConnectionsPage canRunDiagnostics={isSuperAdmin} />}
        {activePage === "users" && <UsersPage />}
        {activePage === "settings" && <SettingsPage />}
      </main>
    </div>
  );
}

async function clearAppNotificationBadge() {
  const badgeNavigator = navigator as Navigator & {
    clearAppBadge?: () => Promise<void>;
  };

  if (badgeNavigator.clearAppBadge) {
    await badgeNavigator.clearAppBadge().catch(() => undefined);
  }

  if (!("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.register("/service-worker.js");
    const updatedRegistration = await registration.update().catch(() => registration);
    const workers = [
      navigator.serviceWorker.controller,
      updatedRegistration.active,
      updatedRegistration.waiting,
      updatedRegistration.installing,
    ];
    for (const worker of new Set(workers)) {
      worker?.postMessage({ type: "CLEAR_APP_BADGE" });
    }
  } catch {
    // Badges zijn een extra; de beheertool moet blijven werken zonder service worker.
  }
}

function withTimeout<T>(promise: PromiseLike<T>, timeoutMs: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error(`${label} duurt langer dan verwacht.`)), timeoutMs);
    Promise.resolve(promise).then(
      (value) => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

function isPasswordSetupUrl() {
  const value = `${window.location.search || ""}&${window.location.hash || ""}`.toLowerCase();
  return value.includes("type=invite") || value.includes("type=recovery");
}

function pageFromHash(): PageKey | null {
  const hash = window.location.hash || "";
  if (!hash.startsWith("#/")) return null;
  const slug = hash.slice(2).split(/[/?#]/)[0];
  return pagesBySlug[slug] || null;
}

function navigateToPage(page: PageKey) {
  window.location.hash = `/${pageSlugs[page]}`;
}

function clearAuthUrl() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

function BootScreen({ label }: { label: string }) {
  return (
    <div className="boot-screen">
      <RefreshCw className="spin" size={24} />
      <span>{label}</span>
    </div>
  );
}

function ConfigMissing() {
  return (
    <div className="boot-screen">
      <div className="config-card">
        <h1>Brede Horizon Beheertool</h1>
        <p>Supabase configuratie ontbreekt nog. Zet `VITE_SUPABASE_URL` en `VITE_SUPABASE_ANON_KEY` in Cloudflare Pages en lokaal in de webomgeving.</p>
      </div>
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"login" | "reset">("login");
  const [message, setMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    setSuccess(null);
    const { error } = await supabase!.auth.signInWithPassword({ email, password });
    if (error) setMessage(error.message);
    setBusy(false);
  }

  async function sendResetLink(event: React.FormEvent) {
    event.preventDefault();
    if (!email) {
      setMessage("Vul je e-mailadres in.");
      return;
    }
    setBusy(true);
    setMessage(null);
    setSuccess(null);
    const { error } = await supabase!.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) {
      setMessage(error.message);
    } else {
      setSuccess("Resetlink verzonden. Controleer je e-mail.");
    }
    setBusy(false);
  }

  if (mode === "reset") {
    return (
      <div className="login-page">
        <form className="login-card auth-card" onSubmit={sendResetLink}>
          <div className="auth-heading">
            <h1>Wachtwoord resetten</h1>
          </div>
          <label>
            E-mailadres
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required />
          </label>
          {message && <div className="banner error">{message}</div>}
          {success && <div className="banner success">{success}</div>}
          <div className="auth-actions">
            <button className="primary-button" disabled={busy}>
              {busy ? "Versturen..." : "Resetlink versturen"}
            </button>
            <button type="button" className="secondary-button" onClick={() => { setMode("login"); setMessage(null); setSuccess(null); }} disabled={busy}>
              Terug naar inloggen
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="login-page">
      <form className="login-card auth-card" onSubmit={submit}>
        <div className="auth-heading">
          <h1>Brede Horizon Beheertool</h1>
        </div>
        <label>
          E-mailadres
          <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required />
        </label>
        <label>
          Wachtwoord
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" required />
        </label>
        {message && <div className="banner error">{message}</div>}
        {success && <div className="banner success">{success}</div>}
        <button className="primary-button" disabled={busy}>
          {busy ? "Inloggen..." : "Inloggen"}
        </button>
        <button type="button" className="text-button auth-link" onClick={() => { setMode("reset"); setMessage(null); setSuccess(null); }} disabled={busy}>
          Wachtwoord vergeten?
        </button>
      </form>
    </div>
  );
}

function SetPassword({ userEmail, onDone }: { userEmail: string; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setMessage(null);
    setSuccess(null);
    if (password.length < 8) {
      setMessage("Gebruik minimaal 8 tekens.");
      return;
    }
    if (password !== confirmPassword) {
      setMessage("De wachtwoorden zijn niet gelijk.");
      return;
    }
    setBusy(true);
    const { error } = await supabase!.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      setMessage(error.message);
      return;
    }
    clearAuthUrl();
    setSuccess("Wachtwoord ingesteld.");
    window.setTimeout(onDone, 650);
  }

  return (
    <div className="login-page">
      <form className="login-card auth-card" onSubmit={submit}>
        <div className="auth-heading">
          <h1>Wachtwoord instellen</h1>
          <p className="form-help">{userEmail}</p>
        </div>
        <label>
          Nieuw wachtwoord
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="new-password" required />
        </label>
        <label>
          Herhaal wachtwoord
          <input value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} type="password" autoComplete="new-password" required />
        </label>
        {message && <div className="banner error">{message}</div>}
        {success && <div className="banner success">{success}</div>}
        <div className="auth-actions">
          <button className="primary-button" disabled={busy}>
            {busy ? "Opslaan..." : "Wachtwoord opslaan"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Dashboard({ canViewFinancials }: { canViewFinancials: boolean }) {
  const [totals, setTotals] = useState<DashboardTotals | null>(null);
  const [growth, setGrowth] = useState<GrowthMetrics | null>(null);
  const [escapeMetrics, setEscapeMetrics] = useState<EscapeSubscriberMetrics | null>(null);
  const [escapeMetricsError, setEscapeMetricsError] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductMetric[]>([]);
  const [retention, setRetention] = useState<ProductRetentionMetric[]>([]);
  const [annualRevenue, setAnnualRevenue] = useState<AnnualRevenueRow[]>([]);
  const [newMembers, setNewMembers] = useState<DashboardMemberChange[]>([]);
  const [cancelledMembers, setCancelledMembers] = useState<DashboardMemberChange[]>([]);
  const [recentViewers, setRecentViewers] = useState<Viewer[]>([]);
  const [dashboardCustomers, setDashboardCustomers] = useState<Customer[]>([]);
  const [revenueDetailsExpanded, setRevenueDetailsExpanded] = useState(false);
  const [cashflowDetailsExpanded, setCashflowDetailsExpanded] = useState(false);
  const [revenueSort, setRevenueSort] = useState<RevenueProductSort>({ key: "monthly", direction: "desc" });
  const [retentionSort, setRetentionSort] = useState<RetentionSort>({ key: "members", direction: "desc" });
  const [annualRevenueSort, setAnnualRevenueSort] = useState<AnnualRevenueSort>({ key: "year", direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [dashboardLoadError, setDashboardLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let metricsRefreshTimer: number | undefined;

    async function load() {
      if (!supabase) {
        setLoading(false);
        setDetailsLoading(false);
        return;
      }
      const metricsClient = supabase;
      setLoading(true);

      // Read the stored aggregate immediately. A server job refreshes it every
      // five minutes; opening this page never scans ActiveCampaign contacts.
      async function loadActiveCampaignMetrics(attempt = 1): Promise<void> {
        try {
          const { data, error } = await metricsClient.rpc("get_dashboard_mailing_metrics");
          if (error) throw error;
          const metrics = data as EscapeSubscriberMetrics;
          if (!Number.isInteger(metrics?.current_subscribers) || metrics.current_subscribers < 0) {
            throw new Error("ActiveCampaign gaf geen volledige dashboardmeting terug.");
          }
          if (cancelled) return;
          setEscapeMetrics(metrics);
          setEscapeMetricsError(null);
        } catch (error) {
          if (cancelled) return;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 750));
            if (!cancelled) await loadActiveCampaignMetrics(attempt + 1);
            return;
          }
          console.error("ActiveCampaign-dashboardmeting mislukt", error);
          setEscapeMetricsError(readableErrorMessage(error, "ActiveCampaign-meting mislukt"));
        }
      }

      void loadActiveCampaignMetrics();
      metricsRefreshTimer = window.setInterval(() => { void loadActiveCampaignMetrics(); }, 60_000);

      try {
        const [totalsResult, growthResult, productsResult] = await Promise.all([
          withTimeout(supabase.from(canViewFinancials ? "financial_dashboard_totals" : "operational_dashboard_totals").select("*").maybeSingle(), DASHBOARD_QUERY_TIMEOUT_MS, "Dashboardtotalen laden"),
          withTimeout(supabase.from("operational_growth_metrics").select("*").maybeSingle(), DASHBOARD_QUERY_TIMEOUT_MS, "Dashboardgroei laden"),
          canViewFinancials ? withTimeout(supabase.from("financial_product_metrics").select("*"), DASHBOARD_QUERY_TIMEOUT_MS, "Productgegevens laden") : Promise.resolve({ data: [], error: null }),
        ]);
        if (cancelled) return;
        if (totalsResult.data) setTotals(totalsResult.data as DashboardTotals);
        if (growthResult.data) setGrowth(growthResult.data as GrowthMetrics);
        if (productsResult.data) setProducts(sortProductsByMembers(productsResult.data as ProductMetric[]));
        setLoading(false);

        // The tables, forecast, retention and demographics are useful detail,
        // but should never keep the summary cards behind a full-page spinner.
        setDetailsLoading(true);
        const [retentionResult, annualRevenueResult, newMembersResult, cancelledMembersResult, viewersResult, customersResult] = await withTimeout(Promise.all([
          canViewFinancials ? supabase.from("operational_product_retention_metrics").select("*").order("plan_label") : Promise.resolve({ data: [], error: null }),
          canViewFinancials ? supabase.from("financial_annual_revenue").select("*").order("calendar_year", { ascending: true }) : Promise.resolve({ data: [], error: null }),
          supabase.from("operational_new_members_this_month").select("*").order("event_date", { ascending: false }).limit(50),
          supabase.from("operational_cancellations_this_month").select("*").order("event_date", { ascending: false }).limit(50),
          supabase.from("viewers").select("*, customers(display_name,billing_email)").order("updated_at", { ascending: false }).limit(12),
          supabase
            .from("customers")
            .select("id,display_name,first_name,last_name,company_name,billing_email,country,status,payment_arrears,stopped_due_to_unpaid_invoice,metadata,customer_subscriptions(id,status,start_date,invoice_date,access_end_date,ended_at,price_excl_vat_applied,counts_toward_revenue,created_at,metadata,plans(id,code,name,display_label,billing_period,payment_variant,counts_toward_revenue))")
            .in("status", ["active", "cancelled"])
            .limit(MEMBER_LIST_LIMIT),
        ]), DASHBOARD_DETAILS_TIMEOUT_MS, "Dashboarddetails laden");
        if (cancelled) return;
        if (retentionResult.data) setRetention(retentionResult.data as ProductRetentionMetric[]);
        if (annualRevenueResult.data) setAnnualRevenue(annualRevenueResult.data as AnnualRevenueRow[]);
        if (newMembersResult.data) setNewMembers(newMembersResult.data as DashboardMemberChange[]);
        if (cancelledMembersResult.data) setCancelledMembers(cancelledMembersResult.data as DashboardMemberChange[]);
        if (viewersResult.data) setRecentViewers(viewersResult.data as Viewer[]);
        if (customersResult.data) setDashboardCustomers(sortCustomersByStatus(customersResult.data as unknown as Customer[]));
      } catch (error) {
        // A failed detail query must not bring back the full-page loading state.
        console.error("Dashboardgegevens laden mislukt", error);
        if (!cancelled) setDashboardLoadError(error instanceof Error ? error.message : "Dashboardgegevens laden mislukt");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setDetailsLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (metricsRefreshTimer !== undefined) window.clearInterval(metricsRefreshTimer);
    };
  }, [canViewFinancials]);

  if (loading) return <BootScreen label="Dashboard laden" />;
  const paidProductRows = dashboardProductRows(sortProductsByMembers(products));
  const revenueProducts = sortRevenueProducts(paidProductRows, revenueSort);
  const revenueTotals = productRevenueTotals(revenueProducts);
  const monthlyRevenueMix = revenueMixByPeriod(revenueProducts, "monthly");
  const yearlyRevenueMix = revenueMixByPeriod(revenueProducts, "yearly");
  const cashflowRows = cashflowByBillingPeriod(paidProductRows);
  const paidRetentionRows = sortRetentionProducts(retention, retentionSort);
  const sortedAnnualRevenue = sortAnnualRevenueRows(annualRevenue, annualRevenueSort);
  const activePaidCustomers = dashboardCustomers.filter(isActivePayingCustomer);
  const demographics = demographicMetrics(activePaidCustomers);
  const dashboardErrorBanner = dashboardLoadError ? <div className="banner error">{dashboardLoadError}</div> : null;

  if (!canViewFinancials) {
    return (
      <section className="page">
        <PageHeader title="Dashboard" />
        {dashboardErrorBanner}
        <OpenLaunchDashboards canViewFinancials={canViewFinancials} />
        <DashboardMailingLists metrics={escapeMetrics} error={escapeMetricsError} />
        <div className="stats-grid priority">
          <ActivePaidMembersStat value={formatNumber(totals?.active_paid_members)} growth={growth} />
        </div>
        <div className="dashboard-grid">
          <div className="dashboard-full-row dashboard-member-grid">
            <DashboardMemberTable title="Nieuwe betalende leden deze maand" rows={newMembers} emptyLabel="Nog geen nieuwe betalende leden deze maand." />
            <DashboardMemberTable title="Opzeggingen deze maand" rows={cancelledMembers} emptyLabel="Geen opzeggingen deze maand." />
          </div>
          <DashboardViewerTable rows={recentViewers} />
          <DemographicsPanels genderRows={demographics.genderRows} countryRows={demographics.countryRows} total={activePaidCustomers.length} />
        </div>
      </section>
    );
  }

  return (
    <section className="page">
      <PageHeader title="Dashboard" />
      {dashboardErrorBanner}
      <OpenLaunchDashboards canViewFinancials={canViewFinancials} />
      <DashboardMailingLists metrics={escapeMetrics} error={escapeMetricsError} />
      <div className="stats-grid priority">
        <ActivePaidMembersStat value={formatNumber(totals?.active_paid_members)} growth={growth} />
        {canViewFinancials && <RevenueStatCard label="Maandelijkse omzet excl. btw" amount={totals?.mrr_excl_vat} mix={monthlyRevenueMix} tone="orange" expanded={revenueDetailsExpanded} onToggle={() => setRevenueDetailsExpanded((current) => !current)} />}
        {canViewFinancials && <RevenueStatCard label="Jaarlijkse omzet excl. btw" amount={totals?.arr_excl_vat} mix={yearlyRevenueMix} tone="green" expanded={revenueDetailsExpanded} onToggle={() => setRevenueDetailsExpanded((current) => !current)} />}
      </div>

      {canViewFinancials && (
        <RevenueForecastPanel customers={dashboardCustomers} loading={detailsLoading} />
      )}

      {canViewFinancials && (
        <section className="panel cashflow-panel">
          <div className="panel-title">Cashflow excl. btw</div>
          <div className="cashflow-grid">
            {cashflowRows.map((row) => (
              <CashflowCard
                key={row.key}
                label={row.label}
                amount={row.amount}
                annualPercent={row.annualPercent}
                mix={{ total: row.amount, euro: row.euroAmount, bitcoin: row.bitcoinAmount, euroPercent: row.euroPercent, bitcoinPercent: row.bitcoinPercent }}
                expanded={cashflowDetailsExpanded}
                onToggle={() => setCashflowDetailsExpanded((current) => !current)}
              />
            ))}
            <CashflowCard label="Totaal per jaar" amount={revenueTotals.yearly} mix={yearlyRevenueMix} total expanded={cashflowDetailsExpanded} onToggle={() => setCashflowDetailsExpanded((current) => !current)} />
          </div>
        </section>
      )}

      <div className="dashboard-grid">
        <section className="panel chart-panel">
          <div className="panel-title">Actieve leden per product</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={paidProductRows} margin={{ top: 44, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="plan_label" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={78} />
              <YAxis allowDecimals={false} />
              <Tooltip content={<ProductChartTooltip />} />
              <Bar dataKey="active_members" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="active_members" content={(props) => <ProductBarLabel {...props} products={paidProductRows} />} />
                {paidProductRows.map((_entry, index) => (
                  <Cell key={index} fill={chartColors[index % chartColors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </section>

        {canViewFinancials && (
          <section className="panel">
            <div className="panel-title">Omzet per product excl. btw</div>
            <div className="table-panel revenue-table-panel">
              <table className="data-table revenue-table">
                <thead><tr>
                  <GenericSortableHeader label="Product" sortKey="product" sort={revenueSort} onSort={setRevenueSort} />
                  <GenericSortableHeader label="Per maand" sortKey="monthly" sort={revenueSort} onSort={setRevenueSort} />
                  <GenericSortableHeader label="Per jaar" sortKey="yearly" sort={revenueSort} onSort={setRevenueSort} />
                </tr></thead>
                <tbody>
                  {revenueProducts.map((product) => (
                    <tr key={product.plan_code}>
                      <td>
                        <strong>{product.plan_label}</strong>
                        <span>{formatNumber(product.active_members)} leden · {billingPeriodLabel(product.billing_period)}</span>
                      </td>
                      <td>{formatCurrency(product.mrr_excl_vat)}</td>
                      <td>{formatCurrency(product.arr_excl_vat)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td>Totaal</td>
                    <td>{formatCurrency(revenueTotals.monthly)}</td>
                    <td>{formatCurrency(revenueTotals.yearly)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        )}

        <div className="dashboard-full-row dashboard-member-grid">
          <DashboardMemberTable title="Nieuwe betalende leden deze maand" rows={newMembers} emptyLabel="Nog geen nieuwe betalende leden deze maand." />
          <DashboardMemberTable title="Opzeggingen deze maand" rows={cancelledMembers} emptyLabel="Geen opzeggingen deze maand." />
        </div>

        <div className="dashboard-insight-grid">
          <section className="panel dashboard-table-panel annual-revenue-panel">
            <div className="panel-title">Omzet per jaar excl. btw</div>
            <div className="muted annual-revenue-note">Omzet op basis van bekende abonnementsperiodes en actieve dagen. Actief betalende leden = unieke leden met minimaal één actieve dag in dat jaar. 2026 loopt tot vandaag.</div>
            <div className="table-panel compact-table-panel">
              <table className="data-table annual-revenue-table">
                <thead><tr>
                  <GenericSortableHeader label="Jaar" sortKey="year" sort={annualRevenueSort} onSort={setAnnualRevenueSort} />
                  <GenericSortableHeader label="Actief betalende leden" sortKey="members" sort={annualRevenueSort} onSort={setAnnualRevenueSort} />
                  <GenericSortableHeader label="Omzet excl. btw" sortKey="revenue" sort={annualRevenueSort} onSort={setAnnualRevenueSort} />
                </tr></thead>
                <tbody>
                  {sortedAnnualRevenue.map((row) => (
                    <tr key={row.calendar_year}>
                      <td>{row.calendar_year}</td>
                      <td>{formatNumber(row.active_paying_members)}</td>
                      <td>{formatCurrency(row.revenue_excl_vat)}</td>
                    </tr>
                  ))}
                  {sortedAnnualRevenue.length === 0 && <tr><td colSpan={3} className="empty">Geen jaaromzet beschikbaar.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel dashboard-table-panel">
            <div className="panel-title">Retentie per product</div>
            <div className="muted retention-note">Berekend per unieke klant; dubbele abonnementsruns tellen niet dubbel mee.</div>
            <div className="table-panel compact-table-panel">
              <table className="data-table retention-table">
                <thead><tr>
                  <GenericSortableHeader label="Product" sortKey="product" sort={retentionSort} onSort={setRetentionSort} />
                  <GenericSortableHeader label="Gem. maanden lid" sortKey="months" sort={retentionSort} onSort={setRetentionSort} />
                  <GenericSortableHeader label="Nog lid van totaal" sortKey="retention" sort={retentionSort} onSort={setRetentionSort} />
                </tr></thead>
                <tbody>
                  {paidRetentionRows.map((item) => (
                    <tr key={item.plan_code}>
                      <td>{item.plan_label}</td>
                      <td>{formatDecimal(item.avg_membership_months, 1)}</td>
                      <td>{formatPercent(item.observed_retention_pct)}</td>
                    </tr>
                  ))}
                  {paidRetentionRows.length === 0 && <tr><td colSpan={3} className="empty">Geen betaalde producten gevonden.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <DemographicsPanels genderRows={demographics.genderRows} countryRows={demographics.countryRows} total={activePaidCustomers.length} />
      </div>
    </section>
  );
}

function OpenLaunchDashboards({ canViewFinancials }: { canViewFinancials: boolean }) {
  const [rows, setRows] = useState<{ launch: Launch; metrics: LaunchSignupMetrics | null }[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let busy = false;
    async function refresh() {
      if (!supabase || busy) return;
      busy = true;
      try {
        // An undated catch-all (outside a launch) is never a dashboard campaign.
        const launches = await withTimeout(supabase.from("launches")
          .select("id,name,status,launch_month").eq("status", "open")
          .not("launch_month", "is", null).order("launch_month", { ascending: false }),
        DASHBOARD_QUERY_TIMEOUT_MS, "Open lanceringen laden");
        if (launches.error) throw launches.error;
        const open = (launches.data || []) as Launch[];
        const metrics = open.length ? await withTimeout(supabase.from("launch_signup_metrics")
          .select("*").in("launch_id", open.map(launch => launch.id)),
        DASHBOARD_QUERY_TIMEOUT_MS, "Lanceringscijfers laden") : { data: [], error: null };
        if (metrics.error) throw metrics.error;
        if (!cancelled) {
          const byId = new Map(((metrics.data || []) as LaunchSignupMetrics[]).map(row => [row.launch_id, row]));
          setRows(open.map(launch => ({ launch, metrics: byId.get(launch.id) || null })));
          setError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setRows([]);
          setError(readableErrorMessage(error, "Open lanceringen konden niet worden geladen"));
        }
      } finally { busy = false; }
    }
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, 60_000);
    window.addEventListener("focus", refresh);
    return () => { cancelled = true; window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [canViewFinancials]);
  return <>
    <MadeiraOverview canViewFinancials={canViewFinancials} />
    {error && <div className="banner error" role="alert">{error}</div>}
    {rows.map(({ launch, metrics }) => <LaunchSignupDashboard key={launch.id} launch={launch} metrics={metrics}
      canViewFinancials={canViewFinancials} onOpenDetails={() => {
        window.location.hash = `/lanceringen?launch=${encodeURIComponent(launch.id)}`;
      }} />)}
  </>;
}

function LaunchSignupDashboard({ launch, metrics, canViewFinancials, onOpenDetails }: {
  launch: Pick<Launch, "name" | "status">;
  metrics: LaunchSignupMetrics | null;
  canViewFinancials: boolean;
  onOpenDetails?: () => void;
}) {
  const paidPaymentMix = launchPaymentMix(metrics, "paid");

  return (
    <section className="launch-signup-dashboard" aria-label={launch.name}>
      <div className="launch-signup-dashboard-title">
        <div>
          <strong>{launch.name}</strong>
          <span>{launch.status === "closed" || launch.status === "archived"
            ? "Afgesloten lancering. Latere betalingen blijven meetellen; vaste eventmomentopnamen blijven bewaard."
            : "Actuele website-aanmeldingen en betaalstatus; gemarkeerde tests tellen niet mee."}</span>
        </div>
        {onOpenDetails && <button className="secondary-button compact" type="button" onClick={onOpenDetails}>Lancering bekijken</button>}
      </div>
      <div className="stats-grid launch-signup-stats">
        <Stat label="Ter controle" value={metrics ? formatNumber(metrics.review_count) : "-"} tone="brown" />
        <Stat label="Gefactureerd" value={metrics ? formatNumber(metrics.invoiced_count) : "-"} tone="orange" />
        <LaunchMemberMixCard label="Betaald" total={metrics?.paid_count} newMembers={metrics?.paid_new_member_count} formerMembers={metrics?.paid_former_member_count} tone="green" />
        <LaunchMemberMixCard label="Totaal" total={metrics?.total_count} newMembers={metrics?.total_new_member_count} formerMembers={metrics?.total_former_member_count} tone="brown" />
        <article className="stat-card orange launch-period-card">
          <span>Verdeling betaald</span>
          <strong>{metrics ? `${formatNumber(metrics.paid_month_count)} / ${formatNumber(metrics.paid_quarter_count)} / ${formatNumber(metrics.paid_year_count)}` : "-"}</strong>
          <small>maand / kwartaal / jaar</small>
        </article>
        <LaunchPaymentMixCard label="Betaald: Euro / Bitcoin" mix={paidPaymentMix} tone="green" />
        {canViewFinancials && <Stat label="Jaaromzet betaald excl. btw" value={metrics ? formatCurrency(metrics.paid_annual_revenue_excl_vat) : "-"} tone="green" />}
      </div>
      <p className="launch-member-mix-note">Nieuw of voormalig lid is gebaseerd op betaald lidmaatschap vóór de aanmelding. Percentages zijn binnen Totaal of Betaald berekend.</p>
    </section>
  );
}

type LaunchPaymentMix = {
  euro: number;
  bitcoin: number;
  euroPercent: number;
  bitcoinPercent: number;
};

function launchPaymentMix(metrics: LaunchSignupMetrics | null, status: "registered" | "paid"): LaunchPaymentMix {
  const total = Number(status === "registered" ? metrics?.registered_count : metrics?.paid_count) || 0;
  const euro = Number(status === "registered" ? metrics?.registered_euro_count : metrics?.paid_euro_count) || 0;
  const bitcoin = Number(status === "registered" ? metrics?.registered_bitcoin_count : metrics?.paid_bitcoin_count) || 0;
  return {
    euro,
    bitcoin,
    euroPercent: total > 0 ? (euro / total) * 100 : 0,
    bitcoinPercent: total > 0 ? (bitcoin / total) * 100 : 0,
  };
}

function LaunchPaymentMixCard({ label, mix, tone }: { label: string; mix: LaunchPaymentMix; tone: "green" | "orange" }) {
  return (
    <article className={`stat-card ${tone} launch-payment-mix-card`}>
      <span>{label}</span>
      <div className="launch-payment-mix-rows" aria-label={`${label}: Euro en Bitcoin`}>
        <div className="launch-payment-mix-row">
          <strong>Euro</strong>
          <b>{formatNumber(mix.euro)}</b>
          <small>{formatShortPercent(mix.euroPercent)}</small>
        </div>
        <div className="launch-payment-mix-row">
          <strong>Bitcoin</strong>
          <b>{formatNumber(mix.bitcoin)}</b>
          <small>{formatShortPercent(mix.bitcoinPercent)}</small>
        </div>
      </div>
    </article>
  );
}

function RevenueForecastPanel({ customers, loading }: { customers: Customer[]; loading?: boolean }) {
  const rows = buildRevenueForecast(customers);
  const totals = rows.reduce((result, row) => ({
    monthlyPayments: result.monthlyPayments + row.monthlyPayments,
    quarterlyPayments: result.quarterlyPayments + row.quarterlyPayments,
    yearlyPayments: result.yearlyPayments + row.yearlyPayments,
    total: result.total + row.total,
  }), { monthlyPayments: 0, quarterlyPayments: 0, yearlyPayments: 0, total: 0 });

  return (
    <section className="panel revenue-forecast-panel">
      <div className="panel-title-row">
        <div>
          <div className="panel-title">Omzetprognose komende 12 maanden</div>
          <div className="muted revenue-forecast-note">Verwachte betaalmomenten excl. btw op basis van actieve abonnementen en bekende toegangs-einddata.</div>
        </div>
        <div className="revenue-forecast-total">
          <span>Totaal 12 maanden</span>
          <strong>{loading && customers.length === 0 ? "..." : formatCurrency(totals.total)}</strong>
        </div>
      </div>
      {loading && customers.length === 0 ? (
        <div className="muted revenue-forecast-loading">Prognosegegevens laden…</div>
      ) : (
        <div className="revenue-forecast-layout">
          <div className="revenue-forecast-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={rows} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tickFormatter={formatChartCurrency} />
                <Tooltip content={<RevenueForecastTooltip />} />
                <Bar dataKey="monthlyPayments" name="Maandbetalingen" stackId="forecast" fill="#2D6A4F" radius={[0, 0, 0, 0]} />
                <Bar dataKey="quarterlyPayments" name="Kwartaalbetalingen" stackId="forecast" fill="#8B6F5E" />
                <Bar dataKey="yearlyPayments" name="Jaarbetalingen" stackId="forecast" fill="#C4854C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="table-panel compact-table-panel revenue-forecast-table-panel">
            <table className="data-table">
              <thead><tr><th>Maand</th><th>Maandbetaling</th><th>Kwartaalbetaling</th><th>Jaarbetaling</th><th>Totaal</th></tr></thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.monthKey}>
                    <td><strong>{row.monthLabel}</strong></td>
                    <td>{formatCurrency(row.monthlyPayments)}</td>
                    <td>{formatCurrency(row.quarterlyPayments)}</td>
                    <td>{formatCurrency(row.yearlyPayments)}</td>
                    <td><strong>{formatCurrency(row.total)}</strong></td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td>Totaal</td>
                  <td>{formatCurrency(totals.monthlyPayments)}</td>
                  <td>{formatCurrency(totals.quarterlyPayments)}</td>
                  <td>{formatCurrency(totals.yearlyPayments)}</td>
                  <td>{formatCurrency(totals.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function RevenueForecastTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: RevenueForecastRow }> }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{row.monthLabel}</strong>
      <span>Maandbetalingen: {formatCurrency(row.monthlyPayments)}</span>
      <span>Kwartaalbetalingen: {formatCurrency(row.quarterlyPayments)}</span>
      <span>Jaarbetalingen: {formatCurrency(row.yearlyPayments)}</span>
      <strong>Totaal: {formatCurrency(row.total)}</strong>
    </div>
  );
}

function buildRevenueForecast(customers: Customer[], now = new Date()): RevenueForecastRow[] {
  const firstMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const months = Array.from({ length: 12 }, (_, index) => new Date(firstMonth.getFullYear(), firstMonth.getMonth() + index, 1));
  const horizonEnd = new Date(firstMonth.getFullYear(), firstMonth.getMonth() + 12, 1);
  const rows = months.map((month) => ({
    month,
    monthKey: month.toISOString().slice(0, 7),
    monthLabel: new Intl.DateTimeFormat("nl-NL", { month: "short", year: "2-digit" }).format(month),
    monthlyPayments: 0,
    quarterlyPayments: 0,
    yearlyPayments: 0,
    total: 0,
  }));

  for (const customer of customers) {
    const subscription = currentSubscription(customer);
    const plan = subscription?.plans;
    const period = plan?.billing_period;
    if (!subscription || !plan || !period || !["month", "quarter", "year"].includes(period)) continue;
    if (!subscription.counts_toward_revenue || !plan.counts_toward_revenue) continue;
    if (["free", "barter"].includes(plan.payment_variant)) continue;
    if (["ended", "stopped", "free", "barter"].includes(subscription.status) || ["ended", "stopped", "free", "barter"].includes(customer.status)) continue;
    if (subscription.status === "cancelled" && !subscription.access_end_date) continue;

    const anchor = dateOnly(subscription.invoice_date || subscription.start_date || subscription.created_at?.slice(0, 10));
    if (!anchor) continue;
    const accessEnd = dateOnly(subscription.access_end_date);
    if (accessEnd && accessEnd < firstMonth) continue;
    const paymentAmount = Number(subscription.price_excl_vat_applied || 0);
    if (paymentAmount <= 0) continue;

    const interval = period === "month" ? 1 : period === "quarter" ? 3 : 12;
    let dueDate = anchor;
    while (dueDate < firstMonth) dueDate = addMonthsClamped(dueDate, interval);
    while (dueDate < horizonEnd) {
      if ((!accessEnd || dueDate <= accessEnd) && dueDate >= anchor) {
        const row = rows.find((item) => item.month.getFullYear() === dueDate.getFullYear() && item.month.getMonth() === dueDate.getMonth());
        if (row) {
          if (period === "month") row.monthlyPayments += paymentAmount;
          if (period === "quarter") row.quarterlyPayments += paymentAmount;
          if (period === "year") row.yearlyPayments += paymentAmount;
          row.total += paymentAmount;
        }
      }
      dueDate = addMonthsClamped(dueDate, interval);
    }
  }

  return rows.map(({ month: _month, ...row }) => row);
}

function dateOnly(value?: string | null) {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (![year, month, day].every(Number.isFinite)) return null;
  return new Date(year, month - 1, day);
}

function addMonthsClamped(value: Date, months: number) {
  const result = new Date(value);
  const day = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDay));
  return result;
}

function formatChartCurrency(value: number) {
  return `${Math.round(value / 1000)}k`;
}

function DashboardMemberTable({ title, rows, emptyLabel }: { title: string; rows: DashboardMemberChange[]; emptyLabel: string }) {
  return (
    <section className="panel dashboard-table-panel dashboard-member-panel">
      <div className="panel-title">{title}</div>
      <div className="table-panel compact-table-panel dashboard-member-table-panel">
        <table className="data-table compact-dashboard-table dashboard-member-table">
          <thead><tr><th>Naam</th><th>E-mail</th><th>Abonnement</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.display_name || "-"}</td>
                <td>{row.billing_email || "-"}</td>
                <td>{row.plan_label || "-"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="empty">{emptyLabel}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DashboardViewerTable({ rows }: { rows: Viewer[] }) {
  return (
    <section className="panel dashboard-table-panel dashboard-full-row">
      <div className="panel-title">Recente meekijkers</div>
      <div className="table-panel compact-table-panel">
        <table className="data-table dashboard-viewer-table">
          <thead><tr><th>Meekijker</th><th>Hoofdklant</th><th>Status</th><th>Toegevoegd</th><th>Verwijderd</th></tr></thead>
          <tbody>
            {rows.map((viewer) => (
              <tr key={viewer.id}>
                <td>{viewer.email}</td>
                <td>
                  <div>{viewer.customers?.display_name || viewer.customers?.billing_email || "-"}</div>
                  {viewer.customers?.billing_email && <span>{viewer.customers.billing_email}</span>}
                </td>
                <td><Badge value={viewer.status} /></td>
                <td>{formatDateTimeSeconds(viewer.added_at)}</td>
                <td>{formatDateTimeSeconds(viewer.removed_at)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="empty">Geen recente meekijkerwijzigingen.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DemographicsPanels({ genderRows, countryRows, total }: { genderRows: DemographicRow[]; countryRows: DemographicRow[]; total: number }) {
  return (
    <div className="dashboard-full-row demographics-panels">
      <DemographicPanel title="M/V verdeling actieve betalende leden" rows={genderRows} total={total} emptyLabel="Geen M/V-gegevens gevonden." />
      <DemographicPanel title="Landen actieve betalende leden" rows={countryRows} total={total} emptyLabel="Geen landgegevens gevonden." colorOffset={2} labelLimit={2} />
    </div>
  );
}

type DemographicChartRow = DemographicRow & { showChartLabel: boolean };

function DemographicPanel({ title, rows, total, emptyLabel, colorOffset = 0, labelLimit }: { title: string; rows: DemographicRow[]; total: number; emptyLabel: string; colorOffset?: number; labelLimit?: number }) {
  const [sort, setSort] = useState<DemographicSort>({ key: "count", direction: "desc" });
  const chartRows: DemographicChartRow[] = rows.map((row, index) => ({ ...row, showChartLabel: labelLimit === undefined || index < labelLimit }));
  const tableRows = sortDemographicRows(rows, sort);
  return (
    <section className="panel dashboard-table-panel demographics-panel">
      <div className="panel-title">{title}</div>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={chartRows}
            dataKey="count"
            nameKey="label"
            innerRadius={52}
            outerRadius={82}
            paddingAngle={2}
            labelLine={false}
            label={renderDemographicPieLabel}
          >
            {chartRows.map((_row, index) => <Cell key={index} fill={demographicColor(index, colorOffset)} />)}
          </Pie>
          <Tooltip content={<DemographicTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <DemographicLegend rows={rows} colorOffset={colorOffset} />
      <DemographicTable rows={tableRows} total={total} emptyLabel={emptyLabel} sort={sort} onSort={setSort} />
    </section>
  );
}

function renderDemographicPieLabel(rawProps: unknown) {
  const props = rawProps as { cx?: number; cy?: number; midAngle?: number; innerRadius?: number; outerRadius?: number; payload?: DemographicChartRow };
  const row = props.payload;
  if (!row?.showChartLabel) return null;
  const radius = Number(props.innerRadius || 0) + (Number(props.outerRadius || 0) - Number(props.innerRadius || 0)) * 0.5;
  const angle = -Number(props.midAngle || 0) * (Math.PI / 180);
  const x = Number(props.cx || 0) + radius * Math.cos(angle);
  const y = Number(props.cy || 0) + radius * Math.sin(angle);
  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="central" className="pie-label">
      {formatWholePercent(row.percent)}
    </text>
  );
}

function DemographicLegend({ rows, colorOffset }: { rows: DemographicRow[]; colorOffset: number }) {
  if (rows.length === 0) return null;
  return (
    <div className="demographic-legend">
      {rows.map((row, index) => (
        <span key={row.key}>
          <i style={{ background: demographicColor(index, colorOffset) }} />
          {row.label}
        </span>
      ))}
    </div>
  );
}

function demographicColor(index: number, offset = 0) {
  return chartColors[(index + offset) % chartColors.length];
}

function DemographicTable({ rows, total, emptyLabel, sort, onSort }: { rows: DemographicRow[]; total: number; emptyLabel: string; sort: DemographicSort; onSort: (sort: DemographicSort) => void }) {
  return (
    <table className="data-table compact-dashboard-table demographics-table">
      <thead><tr>
        <GenericSortableHeader label="Groep" sortKey="label" sort={sort} onSort={onSort} />
        <GenericSortableHeader label="Aantal" sortKey="count" sort={sort} onSort={onSort} />
        <GenericSortableHeader label="Percentage" sortKey="percent" sort={sort} onSort={onSort} />
      </tr></thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td>{row.label}</td>
            <td>{formatNumber(row.count)}</td>
            <td>{formatWholePercent(row.percent)}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={3} className="empty">{emptyLabel}</td></tr>}
        {rows.length > 0 && <tr className="total-row"><td>Totaal</td><td>{formatNumber(total)}</td><td>100%</td></tr>}
      </tbody>
    </table>
  );
}

function DemographicTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DemographicRow }> }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{row.label}</strong>
      <span>Aantal: {formatNumber(row.count)}</span>
      <span>Percentage: {formatWholePercent(row.percent)}</span>
    </div>
  );
}

function ProductBarLabel(props: { x?: number | string; y?: number | string; width?: number | string; value?: unknown; index?: number; payload?: DashboardProductRow; products: DashboardProductRow[] }) {
  const x = Number(props.x || 0);
  const y = Number(props.y || 0);
  const width = Number(props.width || 0);
  const value = typeof props.value === "number" || typeof props.value === "string" ? Number(props.value) : 0;
  const product = props.payload || props.products[props.index || 0];
  if (!product) return null;
  return (
    <text x={x + width / 2} y={Math.max(16, y - 24)} textAnchor="middle" className="chart-bar-label">
      <tspan x={x + width / 2} dy="0">{formatNumber(value)} leden</tspan>
      <tspan x={x + width / 2} dy="13">{formatShortPercent(product.revenuePercent)} omzet</tspan>
    </text>
  );
}

function ProductChartTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: DashboardProductRow }> }) {
  if (!active || !payload?.length) return null;
  const product = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{product.plan_label}</strong>
      <span>Actieve leden: {formatNumber(product.active_members)}</span>
      <span>Percentage leden: {formatShortPercent(product.memberPercent)}</span>
      <span>Percentage omzet: {formatShortPercent(product.revenuePercent)}</span>
      <span>Omzet per maand excl. btw: {formatCurrency(product.mrr_excl_vat)}</span>
      <span>Omzet per jaar excl. btw: {formatCurrency(product.arr_excl_vat)}</span>
    </div>
  );
}

function ActivePaidMembersStat({ value, growth }: { value: string; growth: GrowthMetrics | null }) {
  return (
    <div className="stat-card green active-paid-members-card">
      <div className="metric-card-header">
        <span>Actieve betalende leden</span>
      </div>
      <strong>{value}</strong>
      <MonthlyGrowthSummary
        net={growth?.net_growth_this_month}
        newCount={growth?.new_members_this_month}
        cancellationCount={growth?.cancellations_this_month}
        loading={!growth}
      />
    </div>
  );
}

function MonthlyGrowthSummary({ net, newCount, cancellationCount, loading = false }: { net?: number; newCount?: number; cancellationCount?: number; loading?: boolean }) {
  const tone = net === undefined || net === null ? "neutral" : net > 0 ? "positive" : net < 0 ? "negative" : "neutral";
  return (
    <>
      <div className={`monthly-growth-summary ${tone}`}>
        <span>Nettogroei deze maand</span>
        {loading ? <LoadingDots /> : <b>{formatSignedNumber(net)}</b>}
      </div>
      <div className="monthly-growth-detail" aria-label="Nieuwe leden en opzeggingen deze maand">
        <div>
          <span>Nieuw:</span>
          {loading ? <LoadingDots compact /> : <b>{formatNumber(newCount)}</b>}
        </div>
        <div>
          <span>Opzeggingen:</span>
          {loading ? <LoadingDots compact /> : <b>{formatNumber(cancellationCount)}</b>}
        </div>
      </div>
    </>
  );
}

function LoadingDots({ compact = false }: { compact?: boolean }) {
  return (
    <span className={`loading-dots${compact ? " compact" : ""}`} role="status" aria-label="Laden">
      <i />
      <i />
      <i />
    </span>
  );
}

function sortProductsByMembers(products: ProductMetric[]) {
  return [...products].sort((a, b) => b.active_members - a.active_members || a.plan_label.localeCompare(b.plan_label));
}

function RevenueStatCard({ label, amount, mix, tone, expanded, onToggle }: { label: string; amount: number | null | undefined; mix: RevenueMix; tone: "green" | "orange"; expanded: boolean; onToggle: () => void }) {
  return (
    <div className={`stat-card revenue-stat-card ${tone}`}>
      <div className="metric-card-header">
        <span>{label}</span>
        <button type="button" className="metric-expand-button" onClick={onToggle} aria-expanded={expanded} aria-label={expanded ? "Omzetdetails inklappen" : "Omzetdetails uitklappen"} title={expanded ? "Omzetdetails inklappen" : "Omzetdetails uitklappen"}>
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </div>
      <strong>{formatCurrency(amount)}</strong>
      {expanded && (
        <div className="revenue-mix" aria-label={`Omzetverdeling ${label.toLowerCase()}`}>
          <div className="revenue-mix-caption">Verdeling Euro / Bitcoin</div>
          <div className="revenue-mix-item">
            <div className="revenue-mix-label"><span>Euro</span><strong>{formatCurrency(mix.euro)}</strong></div>
            <b>{formatShortPercent(mix.euroPercent)}</b>
          </div>
          <div className="revenue-mix-item">
            <div className="revenue-mix-label"><span>Bitcoin</span><strong>{formatCurrency(mix.bitcoin)}</strong></div>
            <b>{formatShortPercent(mix.bitcoinPercent)}</b>
          </div>
        </div>
      )}
    </div>
  );
}

function CashflowCard({ label, amount, annualPercent, mix, total = false, expanded, onToggle }: { label: string; amount: number; annualPercent?: number; mix: RevenueMix; total?: boolean; expanded: boolean; onToggle: () => void }) {
  return (
    <div className={`cashflow-card${total ? " total" : ""}`}>
      <div className="cashflow-card-header">
        <span>{label}</span>
        <button type="button" className="metric-expand-button" onClick={onToggle} aria-expanded={expanded} aria-label={expanded ? "Cashflowdetails inklappen" : "Cashflowdetails uitklappen"} title={expanded ? "Cashflowdetails inklappen" : "Cashflowdetails uitklappen"}>
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </div>
      <strong>{total ? "= " : ""}{formatCurrency(amount)}{!total && annualPercent !== undefined && <em> ({formatShortPercent(annualPercent)} per jaar)</em>}</strong>
      {expanded && <CashflowBreakdown mix={mix} />}
    </div>
  );
}

function CashflowBreakdown({ mix }: { mix: RevenueMix }) {
  return <div className="cashflow-breakdown" aria-label="Verdeling Euro en Bitcoin">
    <div className="cashflow-breakdown-row"><span>Euro</span><strong>{formatCurrency(mix.euro)}</strong><b>{formatShortPercent(mix.euroPercent)}</b></div>
    <div className="cashflow-breakdown-row"><span>Bitcoin</span><strong>{formatCurrency(mix.bitcoin)}</strong><b>{formatShortPercent(mix.bitcoinPercent)}</b></div>
  </div>;
}

function sortRevenueProducts(products: DashboardProductRow[], sort: RevenueProductSort) {
  return [...products].sort((a, b) => {
    const aValue = sort.key === "product" ? a.plan_label : sort.key === "monthly" ? a.mrr_excl_vat : a.arr_excl_vat;
    const bValue = sort.key === "product" ? b.plan_label : sort.key === "monthly" ? b.mrr_excl_vat : b.arr_excl_vat;
    return compareDashboardValues(aValue, bValue, sort.direction, a.plan_label, b.plan_label);
  });
}

function sortRetentionProducts(products: ProductRetentionMetric[], sort: RetentionSort) {
  return products
    .filter((product) => product.payment_variant !== "free" && product.payment_variant !== "barter")
    .sort((a, b) => {
      const aValue = sort.key === "product" ? a.plan_label : sort.key === "months" ? a.avg_membership_months : sort.key === "retention" ? a.observed_retention_pct : a.active_members;
      const bValue = sort.key === "product" ? b.plan_label : sort.key === "months" ? b.avg_membership_months : sort.key === "retention" ? b.observed_retention_pct : b.active_members;
      return compareDashboardValues(aValue, bValue, sort.direction, a.plan_label, b.plan_label);
    });
}

function sortAnnualRevenueRows(rows: AnnualRevenueRow[], sort: AnnualRevenueSort) {
  return [...rows].sort((a, b) => {
    const aValue = sort.key === "year" ? a.calendar_year : sort.key === "members" ? a.active_paying_members : a.revenue_excl_vat;
    const bValue = sort.key === "year" ? b.calendar_year : sort.key === "members" ? b.active_paying_members : b.revenue_excl_vat;
    return compareDashboardValues(aValue, bValue, sort.direction, String(a.calendar_year), String(b.calendar_year));
  });
}

function sortDemographicRows(rows: DemographicRow[], sort: DemographicSort) {
  return [...rows].sort((a, b) => {
    const aValue = sort.key === "label" ? a.label : sort.key === "count" ? a.count : a.percent;
    const bValue = sort.key === "label" ? b.label : sort.key === "count" ? b.count : b.percent;
    return compareDashboardValues(aValue, bValue, sort.direction, a.label, b.label);
  });
}

function compareDashboardValues(aValue: string | number | null | undefined, bValue: string | number | null | undefined, direction: SortDirection, aLabel: string, bLabel: string) {
  const aEmpty = aValue === null || aValue === undefined || aValue === "";
  const bEmpty = bValue === null || bValue === undefined || bValue === "";
  if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
  if (aEmpty && bEmpty) return aLabel.localeCompare(bLabel, "nl", { numeric: true, sensitivity: "base" });
  const comparison = typeof aValue === "number" && typeof bValue === "number"
    ? aValue - bValue
    : String(aValue).localeCompare(String(bValue), "nl", { numeric: true, sensitivity: "base" });
  if (comparison !== 0) return direction === "asc" ? comparison : -comparison;
  return aLabel.localeCompare(bLabel, "nl", { numeric: true, sensitivity: "base" });
}

function paidProducts(products: ProductMetric[]) {
  return products.filter((product) => product.payment_variant !== "free" && product.payment_variant !== "barter" && (product.mrr_excl_vat || 0) > 0);
}

function dashboardProductRows(products: ProductMetric[]): DashboardProductRow[] {
  const paid = paidProducts(products);
  const totalMembers = paid.reduce((sum, product) => sum + Number(product.active_members || 0), 0);
  const totalRevenue = paid.reduce((sum, product) => sum + Number(product.mrr_excl_vat || 0), 0);
  return paid.map((product) => ({
    ...product,
    memberPercent: totalMembers > 0 ? (Number(product.active_members || 0) / totalMembers) * 100 : 0,
    revenuePercent: totalRevenue > 0 ? (Number(product.mrr_excl_vat || 0) / totalRevenue) * 100 : 0,
    arr_excl_vat: Number(product.mrr_excl_vat || 0) * 12,
    cashflowAmount: cashflowAmountForProduct(product),
  }));
}

function cashflowAmountForProduct(product: ProductMetric) {
  const mrr = Number(product.mrr_excl_vat || 0);
  if (product.billing_period === "year") return mrr * 12;
  if (product.billing_period === "quarter") return mrr * 3;
  return mrr;
}

function cashflowByBillingPeriod(products: DashboardProductRow[]) {
  const totalYearly = products.reduce((sum, product) => sum + product.arr_excl_vat, 0);
  const annualPercent = (amount: number, multiplier: number) => (
    totalYearly > 0 ? ((amount * multiplier) / totalYearly) * 100 : 0
  );
  return [
    { key: "month", label: "Per maand", multiplier: 12 },
    { key: "quarter", label: "Per kwartaal", multiplier: 4 },
    { key: "year", label: "Per jaar", multiplier: 1 },
  ].map(({ key, label, multiplier }) => {
    const mix = revenueMixForProducts(products.filter((product) => product.billing_period === key), (product) => product.cashflowAmount);
    return {
      key,
      label,
      amount: mix.total,
      annualPercent: annualPercent(mix.total, multiplier),
      euroAmount: mix.euro,
      bitcoinAmount: mix.bitcoin,
      euroPercent: mix.euroPercent,
      bitcoinPercent: mix.bitcoinPercent,
    };
  });
}

function productRevenueTotals(products: DashboardProductRow[]) {
  return {
    monthly: products.reduce((sum, product) => sum + Number(product.mrr_excl_vat || 0), 0),
    yearly: products.reduce((sum, product) => sum + product.arr_excl_vat, 0),
  };
}

function revenueMixByPeriod(products: DashboardProductRow[], period: "monthly" | "yearly"): RevenueMix {
  return revenueMixForProducts(products, (product) => period === "monthly"
    ? Number(product.mrr_excl_vat || 0)
    : Number(product.arr_excl_vat || 0));
}

function revenueMixForProducts(products: DashboardProductRow[], amountFor: (product: DashboardProductRow) => number): RevenueMix {
  const total = products.reduce((sum, product) => sum + amountFor(product), 0);
  const euro = products.filter((product) => product.payment_variant === "eur").reduce((sum, product) => sum + amountFor(product), 0);
  const bitcoin = products.filter((product) => product.payment_variant === "btc").reduce((sum, product) => sum + amountFor(product), 0);
  return {
    total,
    euro,
    bitcoin,
    euroPercent: total > 0 ? (euro / total) * 100 : 0,
    bitcoinPercent: total > 0 ? (bitcoin / total) * 100 : 0,
  };
}

function demographicMetrics(customers: Customer[]) {
  return {
    genderRows: demographicRows(customers, (customer) => genderLabel(genderCode(customer, currentSubscription(customer)))),
    countryRows: demographicRows(customers, (customer) => normalizeCountryLabel(customer.country)),
  };
}

function demographicRows(customers: Customer[], labelFor: (customer: Customer) => string) {
  const total = customers.length || 1;
  const counts = new Map<string, number>();
  for (const customer of customers) {
    const label = labelFor(customer);
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, count]) => ({
      key: label.toLowerCase(),
      label,
      count,
      percent: (count / total) * 100,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "nl", { sensitivity: "base" }));
}

function normalizeCountryLabel(value?: string | null) {
  const normalized = (value || "").trim().toLowerCase();
  if (!normalized || normalized === "-") return "Onbekend";
  const countryLabels: Record<string, string> = {
    aw: "Aruba",
    aruba: "Aruba",
    be: "België",
    belgium: "België",
    belgie: "België",
    belgië: "België",
    belgique: "België",
    bq: "Caribisch Nederland",
    ch: "Zwitserland",
    switzerland: "Zwitserland",
    cw: "Curaçao",
    curacao: "Curaçao",
    curaçao: "Curaçao",
    cz: "Tsjechië",
    czechia: "Tsjechië",
    es: "Spanje",
    espana: "Spanje",
    españa: "Spanje",
    spain: "Spanje",
    spanje: "Spanje",
    fr: "Frankrijk",
    france: "Frankrijk",
    gr: "Griekenland",
    greece: "Griekenland",
    hu: "Hongarije",
    hungary: "Hongarije",
    nl: "Nederland",
    nederland: "Nederland",
    netherlands: "Nederland",
    "the netherlands": "Nederland",
    pt: "Portugal",
  };
  if (countryLabels[normalized]) return countryLabels[normalized];
  return normalized.replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
}

function formatShortPercent(value?: number) {
  if (value === undefined || value === null) return "-";
  return `${Number(value).toLocaleString("nl-NL", { maximumFractionDigits: 1 })}%`;
}

function formatWholePercent(value?: number) {
  if (value === undefined || value === null) return "-";
  return `${Number(value).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}%`;
}

function billingPeriodLabel(period: string) {
  if (period === "month") return "maand";
  if (period === "quarter") return "kwartaal";
  if (period === "year") return "jaar";
  return period || "-";
}

function billingProviderLabel(provider: string) {
  return provider === "invoice_ninja" ? "Invoice Ninja" : provider === "wefact" ? "WeFact" : provider || "-";
}

function billingAccountLabel(account: string) {
  if (account === "mark_daamen_llc") return "Mark Daamen LLC";
  if (account === "mark_daamen") return "Mark Daamen";
  if (account === "wefact") return "WeFact";
  return account || "-";
}

function currentBillingForCustomer(rows: BillingSubscription[], customerId: string, subscriptionId?: string | null) {
  const rank: Record<string, number> = { active: 0, switch_scheduled: 1, pending: 2, stopping: 3, failed: 4, stopped: 5 };
  return rows
    .filter((row) => row.customer_id === customerId && (!subscriptionId || row.subscription_id === subscriptionId))
    .sort((left, right) => (rank[left.status] ?? 99) - (rank[right.status] ?? 99) || String(right.created_at).localeCompare(String(left.created_at)))[0];
}

function launchSignupStatusLabel(status: string) {
  const labels: Record<string, string> = {
    received: "ontvangen", invoicing: "factuur maken", awaiting_payment: "wacht op betaling",
    billing_failed: "facturering mislukt", payment_received: "betaald", activation_pending: "toegang starten",
    active: "actief", manual_review: "handmatige controle", duplicate: "dubbel",
    cancellation_pending: "annulering bezig", cancelled: "geannuleerd",
  };
  return labels[status] || status;
}

function launchSignupStatusTone(status: string): BadgeTone {
  if (["active", "payment_received"].includes(status)) return "success";
  if (["billing_failed", "cancelled"].includes(status)) return "danger";
  if (["awaiting_payment", "activation_pending", "manual_review", "cancellation_pending"].includes(status)) return "warning";
  return "muted";
}

function sortCustomersByStatus(customers: Customer[]) {
  return customers
    .map((customer) => ({ ...customer, customer_subscriptions: sortSubscriptions(customer.customer_subscriptions || []) }))
    .sort((a, b) => statusRank(a.status) - statusRank(b.status) || (a.display_name || a.billing_email).localeCompare(b.display_name || b.billing_email));
}

function sortSubscriptions(subscriptions: CustomerSubscription[]) {
  return [...subscriptions].sort((a, b) => {
    const statusDelta = subscriptionRank(a.status) - subscriptionRank(b.status);
    if (statusDelta !== 0) return statusDelta;
    // Een klant kan meerdere abonnementsruns met dezelfde startdatum hebben.
    // Gebruik dan de aanmaakdatum (en tenslotte het id) zodat een latere
    // beëindigingsrun niet wordt overschreven door de oudere historische run.
    const startDelta = String(b.start_date || "").localeCompare(String(a.start_date || ""));
    if (startDelta !== 0) return startDelta;
    const createdDelta = String(b.created_at || "").localeCompare(String(a.created_at || ""));
    if (createdDelta !== 0) return createdDelta;
    return String(b.id || "").localeCompare(String(a.id || ""));
  });
}

function currentSubscription(customer: Customer) {
  return sortSubscriptions(customer.customer_subscriptions || [])[0];
}

function originalSubscriptionStartDate(customer: Customer) {
  return (customer.customer_subscriptions || [])
    .map((subscription) => subscription.start_date)
    .filter((date): date is string => Boolean(date))
    .sort((left, right) => left.localeCompare(right))[0] || null;
}

function compareCustomers(a: Customer, b: Customer, sort: CustomerSort, launchNameFor?: (customer: Customer) => string | null) {
  const aValue = customerSortValue(a, sort.key, launchNameFor);
  const bValue = customerSortValue(b, sort.key, launchNameFor);
  const aEmpty = aValue === null || aValue === undefined || aValue === "";
  const bEmpty = bValue === null || bValue === undefined || bValue === "";
  if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
  if (aEmpty && bEmpty) return customerLabel(a).localeCompare(customerLabel(b), "nl", { sensitivity: "base" });

  const comparison = typeof aValue === "number" && typeof bValue === "number"
    ? aValue - bValue
    : String(aValue).localeCompare(String(bValue), "nl", { sensitivity: "base", numeric: true });
  if (comparison !== 0) return sort.direction === "asc" ? comparison : -comparison;
  return customerLabel(a).localeCompare(customerLabel(b), "nl", { sensitivity: "base" });
}

function customerSortValue(customer: Customer, key: CustomerSortKey, launchNameFor?: (customer: Customer) => string | null) {
  const subscription = currentSubscription(customer);
  const values: Record<CustomerSortKey, string | number | null | undefined> = {
    customer: customerLabel(customer),
    email: customer.billing_email,
    activecampaign_email: effectiveMailingEmail(customer),
    thinkific_email: effectiveThinkificEmail(customer),
    gender: genderCode(customer, subscription),
    status: customer.status,
    payment_arrears: customer.payment_arrears ? 1 : 0,
    start_date: subscription?.start_date,
    cancellation_date: subscription?.cancellation_date,
    access_end_date: subscription?.access_end_date,
    launch: launchNameFor?.(customer),
    invoice_date: subscription?.invoice_date,
    plan: subscription?.plans?.display_label,
    price_incl: Number(subscription?.price_incl_vat_applied || 0),
    price_excl: Number(subscription?.price_excl_vat_applied || 0),
  };
  return values[key];
}

function isAccessStatusActive(status?: string | null) {
  return ["active", "free", "barter"].includes(status || "");
}

function rawGenderMarker(customer: Customer, subscription?: CustomerSubscription) {
  return metadataText(customer.metadata, "gender_marker") || metadataText(subscription?.metadata, "gender_marker");
}

function genderCode(customer: Customer, subscription?: CustomerSubscription) {
  return normalizeGenderMarker(rawGenderMarker(customer, subscription));
}

function normalizeGenderMarker(value?: string | null) {
  const normalized = (value || "").trim().toLowerCase();
  if (["1", "m", "man", "male", "heer"].includes(normalized)) return "M";
  if (["0", "v", "vrouw", "female", "mevrouw"].includes(normalized)) return "V";
  return "";
}

function genderLabel(value?: string | null) {
  const code = normalizeGenderMarker(value);
  if (code === "M") return "Man";
  if (code === "V") return "Vrouw";
  return "Onbekend";
}

function editableCustomerStatus(status?: string | null) {
  if (status === "stopped") return "ended";
  if (status === "cancelled") return "active";
  return status || "active";
}

function normalizeEmail(value?: string | null) {
  return (value || "").trim().toLowerCase();
}

function isValidEmailAddress(value?: string | null) {
  const normalized = normalizeEmail(value);
  return !normalized || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

function invalidEmailField(values: Array<{ label: string; value?: string | null; required?: boolean }>) {
  const invalid = values.find((item) => {
    const value = String(item.value || "").trim();
    return (item.required && !value) || (value && !isValidEmailAddress(value));
  });
  return invalid ? invalid.label : null;
}

function customerLabel(customer: Customer) {
  return displayName(customer.first_name, customer.last_name, customer.company_name, customer.display_name) || customer.billing_email;
}

function effectiveMailingEmail(customer: Customer) {
  return customer.mailing_email_override?.trim() || customer.billing_email;
}

function effectiveThinkificEmail(customer: Customer) {
  return customer.thinkific_email_override?.trim() || customer.billing_email;
}

function customerEmailRoles(customer: Customer): Array<{ role: EmailRole; value: string }> {
  const roles: Array<{ role: EmailRole; value: string }> = [
    { role: "factuur e-mail", value: customer.billing_email },
    { role: "mailing e-mail", value: customer.mailing_email_override || "" },
    { role: "Thinkific e-mail", value: customer.thinkific_email_override || "" },
  ];
  return roles.filter((item) => normalizeEmail(item.value));
}

function findEmailConflicts(
  emails: { billingEmail: string; mailingEmail?: string; thinkificEmail?: string },
  customers: Customer[],
  viewers: Viewer[] = [],
  excludeCustomerId?: string,
): EmailConflictState {
  const enteredEmailRoles: Array<{ role: EmailRole; value: string; blocksBillingDuplicate: boolean }> = [
    { role: "factuur e-mail", value: emails.billingEmail, blocksBillingDuplicate: true },
    { role: "mailing e-mail", value: emails.mailingEmail || "", blocksBillingDuplicate: false },
    { role: "Thinkific e-mail", value: emails.thinkificEmail || "", blocksBillingDuplicate: false },
  ];
  const enteredRoles = enteredEmailRoles.filter((item) => normalizeEmail(item.value));
  const warnings: EmailConflict[] = [];
  const viewerWarnings: ViewerEmailConflict[] = [];
  let billingConflict: EmailConflict | null = null;
  let activeViewerConflict: ViewerEmailConflict | null = null;
  const seenWarnings = new Set<string>();

  for (const entered of enteredRoles) {
    const normalizedEntered = normalizeEmail(entered.value);
    for (const customer of customers) {
      if (excludeCustomerId && customer.id === excludeCustomerId) continue;
      for (const existing of customerEmailRoles(customer)) {
        if (normalizeEmail(existing.value) !== normalizedEntered) continue;
        const conflict: EmailConflict = {
          customer,
          email: entered.value.trim(),
          enteredRole: entered.role,
          existingRole: existing.role,
        };
        if (entered.blocksBillingDuplicate && existing.role === "factuur e-mail") {
          billingConflict = billingConflict || conflict;
          continue;
        }
        const warningKey = `${customer.id}:${entered.role}:${existing.role}:${normalizedEntered}`;
        if (!seenWarnings.has(warningKey)) {
          warnings.push(conflict);
          seenWarnings.add(warningKey);
        }
      }
    }
  }

  for (const entered of enteredRoles) {
    const normalizedEntered = normalizeEmail(entered.value);
    for (const viewer of viewers) {
      if (normalizeEmail(viewer.email) !== normalizedEntered) continue;
      if (excludeCustomerId && isOwnPayerViewer({id: excludeCustomerId, billing_email: emails.billingEmail, mailing_email_override: emails.mailingEmail, thinkific_email_override: emails.thinkificEmail}, viewer)) continue;
      const conflict: ViewerEmailConflict = { viewer, email: entered.value.trim(), enteredRole: entered.role };
      if (viewer.status === "active") {
        activeViewerConflict = activeViewerConflict || conflict;
      }
    }
  }

  return { billingConflict, warnings, activeViewerConflict, viewerWarnings };
}

function findViewerAddConflicts(email: string, customers: Customer[], viewers: Viewer[], selectedCustomerId?: string): ViewerAddConflictState {
  const normalizedEmail = normalizeEmail(email);
  const warnings: ViewerAddConflictState["warnings"] = [];
  if (!normalizedEmail) return { activeCustomer: null, activeViewer: null, warnings };

  let activeCustomer: ViewerAddConflictState["activeCustomer"] = null;
  for (const customer of customers) {
    const isActiveCustomer = isAccessStatusActive(customer.status);
    if (selectedCustomerId && isOwnPayerViewer(customer, {customer_id: selectedCustomerId, email})) continue;
    for (const role of customerEmailRoles(customer)) {
      if (normalizeEmail(role.value) !== normalizedEmail) continue;
      if (isActiveCustomer) {
        activeCustomer = activeCustomer || { customer, role: role.role };
      } else {
        warnings.push({ type: "customer", customer, role: role.role });
      }
    }
  }

  let activeViewer: Viewer | null = null;
  for (const viewer of viewers) {
    if (normalizeEmail(viewer.email) !== normalizedEmail) continue;
    if (viewer.status === "active") {
      activeViewer = activeViewer || viewer;
    }
  }

  return { activeCustomer, activeViewer, warnings };
}

function EmailConflictPanel({
  conflicts,
  allowEmailOverlap,
  onAllowEmailOverlap,
  onOpenExisting,
  onReactivateExisting,
}: {
  conflicts: EmailConflictState;
  allowEmailOverlap: boolean;
  onAllowEmailOverlap: (allow: boolean) => void;
  onOpenExisting?: (customer: Customer) => void;
  onReactivateExisting?: (customer: Customer) => void;
}) {
  const conflict = conflicts.billingConflict;
  const viewerConflict = conflicts.activeViewerConflict;
  if (!conflict && !viewerConflict && conflicts.warnings.length === 0 && conflicts.viewerWarnings.length === 0) return null;
  const customer = conflict?.customer;
  const subscription = customer ? currentSubscription(customer) : null;
  const canReactivate = customer ? !isAccessStatusActive(customer.status) : false;
  return (
    <div className={`email-conflict-panel ${conflict || viewerConflict ? "blocking" : "warning"}`}>
      {conflict && customer && (
        <>
          <div className="conflict-title">Deze klant bestaat al</div>
          <div className="conflict-grid">
            <span>Naam</span><strong>{customerLabel(customer)}</strong>
            <span>Status</span><strong><Badge value={customer.status} /></strong>
            <span>Factuur e-mail</span><strong>{customer.billing_email}</strong>
            <span>Mailing e-mail</span><strong>{customer.mailing_email_override || "-"}</strong>
            <span>Thinkific e-mail</span><strong>{customer.thinkific_email_override || "-"}</strong>
            <span>Laatste abonnement</span><strong>{subscription?.plans?.display_label || "-"}</strong>
          </div>
          {(onOpenExisting || onReactivateExisting) && (
            <div className="conflict-actions">
              {onOpenExisting && <button type="button" className="secondary-button compact" onClick={() => onOpenExisting(customer)}>Open bestaande klant</button>}
              {onReactivateExisting && canReactivate && <button type="button" className="primary-button compact" onClick={() => onReactivateExisting(customer)}>Heractiveer klant</button>}
            </div>
          )}
        </>
      )}
      {viewerConflict && (
        <>
          <div className="conflict-title">E-mail bestaat als actieve meekijker</div>
          <div className="conflict-grid">
            <span>E-mail</span><strong>{viewerConflict.viewer.email}</strong>
            <span>Status</span><strong><Badge value={viewerConflict.viewer.status} /></strong>
            <span>Hoofdklant</span><strong>{viewerConflict.viewer.customers?.display_name || "-"}</strong>
            <span>Hoofdklant e-mail</span><strong>{viewerConflict.viewer.customers?.billing_email || "-"}</strong>
            <span>Toegevoegd</span><strong>{formatDateTimeSeconds(viewerConflict.viewer.added_at)}</strong>
            <span>Verwijderd</span><strong>{formatDateTimeSeconds(viewerConflict.viewer.removed_at)}</strong>
          </div>
        </>
      )}
      {(conflicts.warnings.length > 0 || conflicts.viewerWarnings.length > 0) && (
        <>
          <div className="conflict-title">E-mailoverlap gevonden</div>
          <div className="compact-list">
            {conflicts.warnings.map((warning) => (
              <div className="list-row" key={`${warning.customer.id}-${warning.enteredRole}-${warning.existingRole}-${warning.email}`}>
                <div>
                  <strong>{warning.email}</strong>
                  <span>{warning.enteredRole} overlapt met {warning.existingRole} bij {customerLabel(warning.customer)} ({warning.customer.status}).</span>
                </div>
                <Badge value="check" />
              </div>
            ))}
            {conflicts.viewerWarnings.map((warning) => (
              <div className="list-row" key={`${warning.viewer.id}-${warning.enteredRole}-${warning.email}`}>
                <div>
                  <strong>{warning.email}</strong>
                  <span>{warning.enteredRole} overlapt met meekijker {warning.viewer.email} ({warning.viewer.status}) bij {warning.viewer.customers?.display_name || warning.viewer.customers?.billing_email || "onbekende hoofdklant"}.</span>
                </div>
                <Badge value="check" />
              </div>
            ))}
          </div>
          <label className="checkbox-row conflict-checkbox">
            <input checked={allowEmailOverlap} onChange={(event) => onAllowEmailOverlap(event.target.checked)} type="checkbox" />
            Bewust doorgaan met deze e-mailoverlap
          </label>
        </>
      )}
    </div>
  );
}

function ViewerAddConflictPanel({ conflicts }: { conflicts: ViewerAddConflictState }) {
  if (!conflicts.activeCustomer && !conflicts.activeViewer && conflicts.warnings.length === 0) return null;
  const visibleWarnings = conflicts.warnings.slice(0, 3);
  const hiddenWarningCount = Math.max(conflicts.warnings.length - visibleWarnings.length, 0);
  return (
    <div className={`email-conflict-panel ${conflicts.activeCustomer || conflicts.activeViewer ? "blocking" : "warning"}`}>
      {conflicts.activeCustomer && (
        <>
          <div className="conflict-title">E-mail is al actieve klant</div>
          <p className="conflict-note">Opslaan is geblokkeerd: iemand kan niet tegelijk actieve klant en meekijker zijn.</p>
          <div className="conflict-grid">
            <span>Naam</span><strong>{customerLabel(conflicts.activeCustomer.customer)}</strong>
            <span>Status</span><strong><Badge value={conflicts.activeCustomer.customer.status} /></strong>
            <span>Rol</span><strong>{conflicts.activeCustomer.role}</strong>
            <span>Factuur e-mail</span><strong>{conflicts.activeCustomer.customer.billing_email}</strong>
            <span>Mailing e-mail</span><strong>{conflicts.activeCustomer.customer.mailing_email_override || "-"}</strong>
            <span>Thinkific e-mail</span><strong>{conflicts.activeCustomer.customer.thinkific_email_override || "-"}</strong>
          </div>
        </>
      )}
      {conflicts.activeViewer && (
        <>
          <div className="conflict-title">E-mail is al actieve meekijker</div>
          <p className="conflict-note">Opslaan is geblokkeerd: een actieve meekijker kan maar bij één hoofdklant staan.</p>
          <div className="conflict-grid">
            <span>E-mail</span><strong>{conflicts.activeViewer.email}</strong>
            <span>Status</span><strong><Badge value={conflicts.activeViewer.status} /></strong>
            <span>Hoofdklant</span><strong>{conflicts.activeViewer.customers?.display_name || "-"}</strong>
            <span>Hoofdklant e-mail</span><strong>{conflicts.activeViewer.customers?.billing_email || "-"}</strong>
            <span>Toegevoegd</span><strong>{formatDateTimeSeconds(conflicts.activeViewer.added_at)}</strong>
          </div>
        </>
      )}
      {conflicts.warnings.length > 0 && (
        <>
          <div className="conflict-title">Niet-actieve klant gevonden</div>
          <p className="conflict-note">Dit blokkeert opslaan niet. Controleer kort of dit bewust is; als dat klopt kun je de meekijker gewoon toevoegen.</p>
          <div className="compact-list">
            {visibleWarnings.map((warning, index) => (
              <div className="list-row" key={`${warning.type}-${warning.customer?.id || warning.viewer?.id || index}`}>
                <div>
                  <strong>{warning.customer ? customerLabel(warning.customer) : warning.viewer?.email}</strong>
                  <span>
                    {warning.customer
                      ? `Komt voor als ${warning.role} bij klantstatus ${warning.customer.status}.`
                      : `Komt voor als meekijker (${warning.viewer?.status}) bij ${warning.viewer?.customers?.display_name || warning.viewer?.customers?.billing_email || "onbekende hoofdklant"}.`}
                  </span>
                </div>
                <Badge value="info" />
              </div>
            ))}
            {hiddenWarningCount > 0 && <div className="muted inline-note">Nog {hiddenWarningCount} eerdere historische vermelding{hiddenWarningCount === 1 ? "" : "en"} verborgen.</div>}
          </div>
        </>
      )}
    </div>
  );
}

function subscriptionRank(status: string) {
  const order: Record<string, number> = {
    active: 0,
    free: 0,
    barter: 0,
    cancelled: 1,
    stopped: 2,
    ended: 3,
  };
  return order[status] ?? 99;
}

function statusRank(status: string) {
  const order: Record<string, number> = {
    active: 0,
    free: 1,
    barter: 2,
    cancelled: 3,
    ended: 4,
    stopped: 5,
  };
  return order[status] ?? 99;
}

function hasPaymentArrears(customer: Customer, reviewCustomerIds: Set<string>) {
  return (customer.payment_arrears && !customer.stopped_due_to_unpaid_invoice)
    || reviewCustomerIds.has(customer.id);
}

function countStatuses(customers: Customer[], reviewCustomerIds: Set<string>) {
  const counts: Record<string, number> = {
    all: customers.length,
    active: customers.filter(isActivePayingCustomer).length,
    payment_arrears: customers.filter((customer) => hasPaymentArrears(customer, reviewCustomerIds)).length,
    cancelled: customers.filter((customer) => isScheduledCancellation(currentSubscription(customer))).length,
    stopped: customers.filter((customer) => customer.status === "ended" && customer.stopped_due_to_unpaid_invoice).length,
  };
  for (const customer of customers) {
    if (["active", "cancelled", "stopped"].includes(customer.status)) continue;
    counts[customer.status] = (counts[customer.status] || 0) + 1;
  }
  return counts;
}

function isActivePayingCustomer(customer: Customer) {
  const subscription = currentSubscription(customer);
  return customer.status === "active"
    && subscription?.status === "active"
    && subscription.counts_toward_revenue
    && subscription.plans?.counts_toward_revenue
    && !["free", "barter"].includes(subscription.plans?.payment_variant || "");
}

function isScheduledCancellation(subscription?: CustomerSubscription) {
  if (!subscription?.cancellation_date || !subscription.access_end_date) return false;
  return isAccessStatusActive(subscription.status)
    && normalizeDateOnly(subscription.access_end_date) >= new Date().toISOString().slice(0, 10);
}

type CustomerSortKey =
  | "customer"
  | "email"
  | "gender"
  | "status"
  | "payment_arrears"
  | "start_date"
  | "cancellation_date"
  | "access_end_date"
  | "launch"
  | "activecampaign_email"
  | "thinkific_email"
  | "invoice_date"
  | "plan"
  | "price_incl"
  | "price_excl";

type CustomerSort = {
  key: CustomerSortKey;
  direction: "asc" | "desc";
};

type SortDirection = "asc" | "desc";

type CustomerLaunchAssignmentReference = {
  customer_id: string;
  subscription_id: string | null;
  launch_id: string;
};

type HistoricalSortKey = "customer" | "email" | "status" | "start_date" | "launch" | "plan";
type HistoricalSort = { key: HistoricalSortKey; direction: SortDirection };

function customerLaunchName(
  customer: Customer,
  launchNameBySubscriptionId: Map<string, string>,
  launchNameByCustomerId: Map<string, string>,
) {
  const subscriptionId = currentSubscription(customer)?.id;
  return (subscriptionId ? launchNameBySubscriptionId.get(subscriptionId) : undefined)
    || launchNameByCustomerId.get(customer.id)
    || null;
}

type HistoricalCustomerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string;
  signup_date: string | null;
  launch_names: string[];
  plan_labels: string[];
  statuses: string[];
  amounts: number[];
  source_sheets: string[];
  record_count: number;
};

function uniqueHistoricalCustomerRows(participants: LaunchParticipant[]): HistoricalCustomerRow[] {
  const grouped = new Map<string, LaunchParticipant[]>();
  for (const participant of participants) {
    const email = normalizeEmail(participant.email);
    if (!email) continue;
    const records = grouped.get(email) || [];
    records.push(participant);
    grouped.set(email, records);
  }

  return [...grouped.entries()].map(([email, records]) => {
    const representative = records.find((record) => record.first_name || record.last_name || record.company_name) || records[0];
    const dates = records.map((record) => record.signup_date).filter(Boolean).sort();
    const uniqueValues = (values: Array<string | null | undefined>) => [...new Set(values.map((value) => String(value || "").trim()).filter(Boolean))];
    const amounts = [...new Set(records.map((record) => Number(record.original_order_amount || 0)).filter((amount) => amount > 0))];
    return {
      id: `historical-${email}`,
      first_name: representative.first_name,
      last_name: representative.last_name,
      company_name: representative.company_name,
      email,
      signup_date: dates[0] || null,
      launch_names: uniqueValues(records.map((record) => record.launch_name)),
      plan_labels: uniqueValues(records.map((record) => record.original_plan_label || record.inferred_plan_label)),
      statuses: uniqueValues(records.map((record) => record.original_status)),
      amounts,
      source_sheets: uniqueValues(records.map((record) => record.source_sheet)),
      record_count: records.length,
    };
  });
}

function compareHistoricalCustomers(a: HistoricalCustomerRow, b: HistoricalCustomerRow, sort: HistoricalSort) {
  const valueFor = (row: HistoricalCustomerRow): string => ({
    customer: displayName(row.first_name, row.last_name, row.company_name, null) || row.email,
    email: row.email,
    status: row.statuses.join(", "),
    start_date: row.signup_date || "",
    launch: row.launch_names.join(", "),
    plan: row.plan_labels.join(", "),
  })[sort.key];
  const comparison = valueFor(a).localeCompare(valueFor(b), "nl", { numeric: true, sensitivity: "base" });
  if (comparison !== 0) return sort.direction === "asc" ? comparison : -comparison;
  return a.email.localeCompare(b.email, "nl", { sensitivity: "base" });
}

function HistoricalCustomersTable({
  rows,
  canViewFinancials,
  sort,
  onSort,
  heading = "Historische klanten",
}: {
  rows: HistoricalCustomerRow[];
  canViewFinancials: boolean;
  sort: HistoricalSort;
  onSort: (sort: HistoricalSort) => void;
  heading?: string;
}) {
  return (
    <section className="panel historical-customers-panel">
      <div className="panel-title-row">
        <div>
          <h2>{heading} ({formatNumber(rows.length)})</h2>
          <p className="muted">Historische aanmeldingen zonder huidig klantprofiel. Deze lijst is alleen-lezen en maakt geen nieuwe klanten aan.</p>
        </div>
      </div>
      <div className="table-shell">
        <table className="data-table customer-table historical-customers-table">
          <thead>
            <tr>
              <GenericSortableHeader label="Klant" sortKey="customer" sort={sort} onSort={onSort} className="customer-col" />
              <GenericSortableHeader label="E-mail factuur" sortKey="email" sort={sort} onSort={onSort} className="email-col" />
              <th className="tiny-col">M/V</th>
              <GenericSortableHeader label="Status" sortKey="status" sort={sort} onSort={onSort} className="status-col" />
              <th className="arrears-col">Betaalachterstand</th>
              <GenericSortableHeader label="Start" sortKey="start_date" sort={sort} onSort={onSort} className="date-col" />
              <th className="date-col">Opzegdatum</th>
              <th className="date-col">Einddatum toegang</th>
              <GenericSortableHeader label="Lancering" sortKey="launch" sort={sort} onSort={onSort} />
              <th className="integration-email-col">ActiveCampaign e-mail</th>
              <th className="integration-email-col">Thinkific e-mail</th>
              <th>Adres</th>
              <th>Postcode</th>
              <th>Stad</th>
              <th>Land</th>
              <th>Btw-nr.</th>
              <GenericSortableHeader label="Abonnement" sortKey="plan" sort={sort} onSort={onSort} />
              <th>Btw</th>
              {canViewFinancials && <th>Prijs incl.</th>}
              {canViewFinancials && <th>Prijs excl.</th>}
              {canViewFinancials && <th>Excel bedrag</th>}
              <th>Excel termijn</th>
              <th>Facturering</th>
              <th>Factuuraccount</th>
              <th>Factuur ID</th>
              <th className="date-col">Factuurdatum</th>
              <th>Ninja ID</th>
              <th>Controle</th>
              <th className="sticky-actions">Acties</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="historical-name-cell">
                  <strong>{displayName(row.first_name, row.last_name, row.company_name, null) || row.email}</strong>
                  {row.record_count > 1 && <span className="cell-subtext">{row.record_count} historische aanmeldingen</span>}
                </td>
                <td>{row.email}</td>
                <td className="tiny-col">-</td>
                <td className="status-col"><Badge value={row.statuses.join(", ") || "historisch"} /></td>
                <td className="arrears-col">-</td>
                  <td>{formatDate(row.signup_date)}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>{row.launch_names.join(", ") || "-"}</td>
                  <td className="integration-email-col">-</td>
                  <td className="integration-email-col">-</td>
                  <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td>{row.plan_labels.join(", ") || "-"}</td>
                <td>-</td>
                {canViewFinancials && <td>-</td>}
                {canViewFinancials && <td>-</td>}
                {canViewFinancials && <td>{row.amounts.length === 1 ? formatCurrency(row.amounts[0]) : row.amounts.length > 1 ? `${row.amounts.length} bedragen` : "-"}</td>}
                <td>-</td>
                <td>-</td>
                <td className="date-col">-</td>
                <td>-</td>
                <td><Badge value="historisch" />{row.source_sheets.length > 0 && <span className="cell-subtext">{row.source_sheets.join(", ")}</span>}</td>
                <td className="sticky-actions"><span className="muted">Geen profiel</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canViewFinancials ? 27 : 24} className="empty">Geen historische klanten gevonden.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CustomersPage({ canViewFinancials, canMutateMembers }: { canViewFinancials: boolean; canMutateMembers: boolean }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [historicalParticipants, setHistoricalParticipants] = useState<LaunchParticipant[]>([]);
  const [launchAssignments, setLaunchAssignments] = useState<CustomerLaunchAssignmentReference[]>([]);
  const [billingSubscriptions, setBillingSubscriptions] = useState<BillingSubscription[]>([]);
  const [arrearsCases, setArrearsCases] = useState<ArrearsFollowUpCase[] | null>(null);
  const [arrearsEnabled, setArrearsEnabled] = useState<boolean | null>(null);
  const [arrearsLoadError, setArrearsLoadError] = useState(false);
  const [reviewQueue, setReviewQueue] = useState<ReviewQueueData | null>(null);
  const [reviewQueueError, setReviewQueueError] = useState('');
  const [reviewCustomerId, setReviewCustomerId] = useState<string | null>(null);
  const [launchSignups, setLaunchSignups] = useState<LaunchSignup[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [launches, setLaunches] = useState<Launch[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [sort, setSort] = useState<CustomerSort>({ key: "customer", direction: "asc" });
  const [historicalSort, setHistoricalSort] = useState<HistoricalSort>({ key: "customer", direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<EditingCustomerState | null>(null);
  const [detailCustomer, setDetailCustomer] = useState<Customer | null>(null);
  const [cancellationCustomer, setCancellationCustomer] = useState<Customer | null>(null);
  const [signupMessage, setSignupMessage] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signupActionBusy, setSignupActionBusy] = useState<string | null>(null);
  const [reviewingSignup, setReviewingSignup] = useState<LaunchSignup | null>(null);
  const [linkingSignup, setLinkingSignup] = useState<LaunchSignup | null>(null);
  const [cancellingSignup, setCancellingSignup] = useState<LaunchSignup | null>(null);

  useEffect(() => {
    loadCustomers();
    supabase!.from("plans").select("*").eq("active", true).order("sort_order").then(({ data }) => setPlans((data || []) as Plan[]));
    supabase!.from("launches").select("id,name,launch_month,start_date,end_date,status,notes").order("launch_month", { ascending: false }).then(({ data }) => setLaunches((data || []) as Launch[]));
  }, []);

  useEffect(() => {
    setSort(status === "cancelled"
      ? { key: "access_end_date", direction: "asc" }
      : { key: "customer", direction: "asc" });
  }, [status]);

  useEffect(() => {
    if (!supabase || !canViewFinancials || loading) return;
    let current = true;
    setArrearsCases(null);
    setArrearsEnabled(null);
    setArrearsLoadError(false);
    Promise.all([
      supabase.from("arrears_cases").select("customer_id,subscription_id,customer_subscriptions(status),reviewed_handling,phase,review_reason,last_error,pause_until,pause_reason,last_success_at,import_hold,import_review_kind,import_review_reason").limit(MEMBER_LIST_LIMIT),
      supabase.from("arrears_policy").select("enabled").eq("id", true).single(),
    ]).then(([cases, policy]) => {
      if (!current) return;
      setArrearsCases(cases.error ? null : cases.data);
      setArrearsEnabled(policy.error ? null : policy.data?.enabled ?? null);
      setArrearsLoadError(Boolean(cases.error || policy.error));
    }).catch(() => { if (current) setArrearsLoadError(true); });
    return () => { current = false; };
  }, [canViewFinancials, customers, loading]);

  async function loadReviewQueue() {
    if (!supabase || !(canMutateMembers || canViewFinancials)) return;
    const result = await supabase.rpc('get_arrears_review_queue');
    if (result.error) { setReviewQueueError(result.error.message); return; }
    setReviewQueue(result.data); setReviewQueueError('');
  }
  useEffect(() => {
    if (!loading) loadReviewQueue().catch(e => setReviewQueueError(e.message));
  }, [canMutateMembers, canViewFinancials, loading]);

  async function loadCustomers() {
    if (!supabase) return;
    setLoading(true);
    const { data } = await supabase
      .from("customers")
      .select("*, customer_subscriptions(*, plans(*))")
      .order("created_at", { ascending: false })
      .limit(MEMBER_LIST_LIMIT);
    const { data: viewerData } = await supabase
      .from("viewers")
      .select("*, customers(display_name,billing_email)")
      .order("added_at", { ascending: false })
      .limit(MEMBER_LIST_LIMIT);
    const { data: historicalData } = await supabase
      .from("launch_participant_details")
      .select("*")
      .is("matched_customer_id", null)
      .order("signup_date", { ascending: false, nullsFirst: false })
      .limit(MEMBER_LIST_LIMIT);
    const { data: launchAssignmentData } = await supabase
      .from("customer_launch_assignments")
      .select("customer_id,subscription_id,launch_id")
      .limit(MEMBER_LIST_LIMIT);
    const [{ data: billingData }, { data: signupData }] = await Promise.all([
      supabase.from("billing_subscriptions").select("*, plans(*)").order("created_at", { ascending: false }).limit(MEMBER_LIST_LIMIT),
      supabase.from("launch_signup_details").select("*").eq("launch_month", "2026-09-01").order("submitted_at", { ascending: false }).limit(MEMBER_LIST_LIMIT),
    ]);
    setCustomers(sortCustomersByStatus((data || []) as Customer[]));
    setViewers((viewerData || []) as Viewer[]);
    setHistoricalParticipants((historicalData || []) as LaunchParticipant[]);
    setLaunchAssignments((launchAssignmentData || []) as CustomerLaunchAssignmentReference[]);
    setBillingSubscriptions((billingData || []) as BillingSubscription[]);
    setLaunchSignups((signupData || []) as LaunchSignup[]);
    setLoading(false);
  }

  async function resolveSignup(signup: LaunchSignup, action: "retry") {
    setSignupMessage(null);
    setSignupError(null);
    setSignupActionBusy(signup.id);
    try {
      const { error } = await supabase!.rpc("resolve_launch_signup", { p_signup_id: signup.id, p_action: action });
      if (error) throw error;
      if (action === "retry") await processBillingNow({ signupId: signup.id });
      setSignupMessage("Facturering opnieuw gestart.");
      await loadCustomers();
    } catch (actionError) {
      setSignupError(actionError instanceof Error ? actionError.message : String(actionError));
    } finally {
      setSignupActionBusy(null);
    }
  }

  const launchNameById = useMemo(() => new Map(launches.map((launch) => [launch.id, launch.name])), [launches]);
  const launchNameBySubscriptionId = useMemo(() => {
    const map = new Map<string, string>();
    for (const assignment of launchAssignments) {
      const launchName = launchNameById.get(assignment.launch_id);
      if (assignment.subscription_id && launchName) map.set(assignment.subscription_id, launchName);
    }
    return map;
  }, [launchAssignments, launchNameById]);
  const launchNameByCustomerId = useMemo(() => {
    const map = new Map<string, string>();
    for (const assignment of launchAssignments) {
      const launchName = launchNameById.get(assignment.launch_id);
      if (launchName && !map.has(assignment.customer_id)) map.set(assignment.customer_id, launchName);
    }
    return map;
  }, [launchAssignments, launchNameById]);

  const reviewCustomerIds = useMemo(() => new Set(reviewQueue?.rows.map(row => row.customer_id)), [reviewQueue]);
  const filtered = useMemo(() => {
    if (status === "historical") return [];
    const currentStatus = status === "all_with_history" ? "all" : status;
    const matches = customers.filter((customer) => {
      const subscription = currentSubscription(customer);
      const haystack = [
        customer.display_name,
        customer.first_name,
        customer.last_name,
        customer.company_name,
        customer.billing_email,
        effectiveMailingEmail(customer),
        effectiveThinkificEmail(customer),
        customer.address,
        customer.postcode,
        customer.city,
        customer.country,
        customer.tax_type,
        customer.tax_number,
        metadataText(customer.metadata, "invoice_customer_id"),
        metadataText(customer.metadata, "ninja_customer_id"),
        genderCode(customer, subscription),
        metadataText(subscription?.metadata, "excel_amount"),
        metadataText(subscription?.metadata, "excel_billing_period"),
        metadataText(subscription?.metadata, "excel_term"),
        customerLaunchName(customer, launchNameBySubscriptionId, launchNameByCustomerId),
      ].filter(Boolean).join(" ").toLowerCase();
      const matchesStatus = currentStatus === "all"
        || (currentStatus === "payment_arrears"
          ? hasPaymentArrears(customer, reviewCustomerIds)
          : customer.status === currentStatus);
      if (currentStatus === "active") return isActivePayingCustomer(customer) && haystack.includes(query.toLowerCase());
      if (currentStatus === "cancelled") return isScheduledCancellation(subscription) && haystack.includes(query.toLowerCase());
      if (currentStatus === "stopped") return customer.status === "ended" && customer.stopped_due_to_unpaid_invoice && haystack.includes(query.toLowerCase());
      return matchesStatus && haystack.includes(query.toLowerCase());
    });
    return matches.sort((a, b) => compareCustomers(a, b, sort, (customer) => customerLaunchName(customer, launchNameBySubscriptionId, launchNameByCustomerId)));
  }, [customers, launchNameByCustomerId, launchNameBySubscriptionId, query, sort, status, reviewCustomerIds]);
  const historicalRows = useMemo(() => uniqueHistoricalCustomerRows(historicalParticipants), [historicalParticipants]);
  const filteredLaunchSignups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return launchSignups.filter((signup) => {
      if (!isLaunchSignupTab(status) || !matchesLaunchSignupTab(signup, status)) return false;
      return !normalizedQuery || [signup.first_name, signup.last_name, signup.company_name, signup.email, signup.plan_label,
        signup.billing_provider, signup.provider_account, signup.external_invoice_number, signup.status]
        .filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery);
    });
  }, [launchSignups, query, status]);
  const filteredHistoricalRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return historicalRows
      .filter((row) => !normalizedQuery || [
        row.first_name,
        row.last_name,
        row.company_name,
        row.email,
        row.launch_names.join(" "),
        row.plan_labels.join(" "),
        row.statuses.join(" "),
        row.source_sheets.join(" "),
      ].filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery))
      .sort((a, b) => compareHistoricalCustomers(a, b, historicalSort));
  }, [historicalRows, historicalSort, query]);
  const statusCounts = useMemo(() => ({ ...countStatuses(customers, reviewCustomerIds), historical: historicalRows.length, all_with_history: customers.length + historicalRows.length }), [customers, historicalRows, reviewCustomerIds]);
  const launchStatusCounts = useMemo(() => launchSignupTabCounts(launchSignups), [launchSignups]);
  const customerStatuses = ["active", "payment_arrears", "all", "cancelled", "ended", "stopped", "free", "barter", "all_with_history", "historical"];
  const launchSignupTab = isLaunchSignupTab(status);
  const showArrearsFollowUp = status === "payment_arrears" && (canViewFinancials || canMutateMembers);

  return (
    <section className="page full-width-page">
      <PageHeader
        title="Klanten"
        action={canMutateMembers ? <button className="primary-button" onClick={() => setAddOpen(true)}><Users size={16} /> Klant toevoegen</button> : undefined}
      />
      <MadeiraOverview mode="customers" canViewFinancials={canViewFinancials} canMutateMembers={canMutateMembers} />
      <div className="launch-customer-tabs">
        <strong>Lancering september 2026:</strong>
        <SegmentedTabs values={["launch_review", "launch_invoiced", "launch_paid", "launch_total", "launch_cancelled"]} value={status} onChange={setStatus} counts={launchStatusCounts} />
      </div>
      <Toolbar query={query} onQuery={setQuery} status={status} onStatus={setStatus} statuses={customerStatuses} counts={statusCounts} />
      {showArrearsFollowUp && <p className="muted">Bij ‘Handmatige controle nodig’ volg je het dossier zelf op. ‘Automatisch’ betekent dat de beheertool de opvolging verzorgt; nieuwe uitzonderingen worden hier gemarkeerd.</p>}
      {showArrearsFollowUp && arrearsLoadError && <div role="alert" className="banner warning">De opvolging kon niet volledig worden opgehaald. Vernieuw de pagina om de actuele status te bekijken.</div>}
      {showArrearsFollowUp && reviewQueueError && <div role="alert" className="banner warning">De betaalcontroles konden niet volledig worden opgehaald: {reviewQueueError}</div>}
      {showArrearsFollowUp && reviewQueue && (reviewQueue.unchecked > 0 || reviewQueue.unavailable > 0) && <div className="banner warning">{reviewQueue.unchecked > 0 && <span>{reviewQueue.unchecked} dossiers wachten nog op hun eerste betaalcontrole. </span>}{reviewQueue.unavailable > 0 && <span>Bij {reviewQueue.unavailable} dossiers konden de betaalgegevens niet volledig worden gecontroleerd. Deze dossiers gelden niet als betaald.</span>}</div>}
      {signupMessage && <div className="banner success">{signupMessage}</div>}
      {signupError && <div className="banner error">{signupError}</div>}
      {launchSignupTab && <LaunchSignupTable signups={filteredLaunchSignups} customers={customers} canViewFinancials={canMutateMembers || canViewFinancials} canMutateMembers={canMutateMembers} busyId={signupActionBusy} onResolve={resolveSignup} onCancel={setCancellingSignup} onReview={setReviewingSignup} onLinkExisting={setLinkingSignup} onOpenCustomer={setDetailCustomer} />}
      {!launchSignupTab && status !== "historical" && <div className="table-shell">
        <table className="data-table customer-table">
          <thead>
            <tr>
              <SortableHeader label="Klant" sortKey="customer" sort={sort} onSort={setSort} className="customer-col" />
              <SortableHeader label="E-mail factuur" sortKey="email" sort={sort} onSort={setSort} className="email-col" />
              {showArrearsFollowUp && <th className="arrears-follow-up-col">Opvolging</th>}
              <SortableHeader label="M/V" sortKey="gender" sort={sort} onSort={setSort} className="tiny-col" />
              <SortableHeader label="Status" sortKey="status" sort={sort} onSort={setSort} className="status-col" />
              <SortableHeader label="Betaalachterstand" sortKey="payment_arrears" sort={sort} onSort={setSort} className="arrears-col" />
              <SortableHeader label="Start" sortKey="start_date" sort={sort} onSort={setSort} className="date-col" />
              <SortableHeader label="Opzegdatum" sortKey="cancellation_date" sort={sort} onSort={setSort} className="date-col" />
              <SortableHeader label="Einddatum toegang" sortKey="access_end_date" sort={sort} onSort={setSort} className="date-col" />
              <SortableHeader label="Lancering" sortKey="launch" sort={sort} onSort={setSort} />
              <SortableHeader label="ActiveCampaign e-mail" sortKey="activecampaign_email" sort={sort} onSort={setSort} className="integration-email-col" />
              <SortableHeader label="Thinkific e-mail" sortKey="thinkific_email" sort={sort} onSort={setSort} className="integration-email-col" />
              <th>Adres</th>
              <th>Postcode</th>
              <th>Stad</th>
              <th>Land</th>
              <th>Btw-nr.</th>
              <SortableHeader label="Abonnement" sortKey="plan" sort={sort} onSort={setSort} />
              <th>Btw</th>
              {canViewFinancials && <SortableHeader label="Prijs incl." sortKey="price_incl" sort={sort} onSort={setSort} />}
              {canViewFinancials && <SortableHeader label="Prijs excl." sortKey="price_excl" sort={sort} onSort={setSort} />}
              {canViewFinancials && <th>Excel bedrag</th>}
              <th>Excel termijn</th>
              <th>Factuur ID</th>
              <SortableHeader label="Factuurdatum" sortKey="invoice_date" sort={sort} onSort={setSort} className="date-col" />
              <th>Ninja ID</th>
              <th>Controle</th>
              <th className="sticky-actions">Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading && <TableLoading colSpan={(canViewFinancials ? 29 : 26) + (showArrearsFollowUp ? 1 : 0)} />}
            {!loading && filtered.map((customer) => {
              const subscription = currentSubscription(customer);
              const billing = currentBillingForCustomer(billingSubscriptions, customer.id);
              const paymentReviews = reviewQueue?.rows.filter(row => row.customer_id === customer.id) || [];
              const followUpCases = paymentReviews.length
                ? paymentReviews.map(row => ({ review_reason: row.reason || "Beoordeel de gesignaleerde openstaande factuur." }))
                : arrearsCases?.filter(row => row.customer_id === customer.id && (row.subscription_id === subscription?.id || customer.customer_subscriptions?.some(run => run.id === row.subscription_id && run.status === "active"))) ?? null;
              return (
                <tr key={customer.id}>
                  <td className="customer-col" title={customerLabel(customer)}>
                    <strong>{displayName(customer.first_name, customer.last_name, customer.company_name, customer.display_name)}</strong>
                    {customer.company_name && <span>{customer.first_name} {customer.last_name}</span>}
                  </td>
                  <td className="email-col" title={customer.billing_email}>{customer.billing_email}</td>
                  {showArrearsFollowUp && <td className="arrears-follow-up-col"><ArrearsFollowUp rows={followUpCases} enabled={arrearsEnabled} showReason />{paymentReviews.length > 0 && <button className="secondary-button compact" onClick={() => setReviewCustomerId(customer.id)}>Betaalcontrole bekijken</button>}</td>}
                  <td className="tiny-col">{genderCode(customer, subscription) || "-"}</td>
                  <td className="status-col"><Badge value={customer.status} /></td>
                  <td className="arrears-col"><Badge value={customer.payment_arrears ? "Ja" : paymentReviews.length ? "Ter controle" : "Nee"} tone={!customer.payment_arrears && paymentReviews.length ? "warning" : undefined} /></td>
                  <td className="date-col">{formatDate(subscription?.start_date)}</td>
                  <td className="date-col">{formatDate(subscription?.cancellation_date)}</td>
                  <td className="date-col">{formatDate(subscription?.access_end_date)}</td>
                  <td>{customerLaunchName(customer, launchNameBySubscriptionId, launchNameByCustomerId) || "-"}</td>
                  <td className="integration-email-col" title={effectiveMailingEmail(customer)}>{effectiveMailingEmail(customer)}</td>
                  <td className="integration-email-col" title={effectiveThinkificEmail(customer)}>{effectiveThinkificEmail(customer)}</td>
                  <td>{customer.address || "-"}</td>
                  <td>{customer.postcode || "-"}</td>
                  <td>{customer.city || "-"}</td>
                  <td>{customer.country || "-"}</td>
                  <td>{customer.tax_number || "-"}</td>
                  <td>{subscription?.plans?.display_label || "-"}</td>
                  <td>{subscription?.vat_regime || "-"}</td>
                  {canViewFinancials && <td>{formatCurrency(subscription?.price_incl_vat_applied)}</td>}
                  {canViewFinancials && <td>{formatCurrency(subscription?.price_excl_vat_applied)}</td>}
                  {canViewFinancials && <td>{metadataText(subscription?.metadata, "excel_amount") || "-"}</td>}
                  <td>{metadataText(subscription?.metadata, "excel_billing_period") || metadataText(subscription?.metadata, "excel_term") || "-"}</td>
                  <td>{billing ? billingProviderLabel(billing.provider) : "-"}</td>
                  <td>{billing ? billingAccountLabel(billing.provider_account) : "-"}</td>
                  <td>{metadataText(customer.metadata, "invoice_customer_id") || metadataText(subscription?.metadata, "invoice_customer_id") || "-"}</td>
                  <td className="date-col">{formatDate(subscription?.invoice_date)}</td>
                  <td>{metadataText(customer.metadata, "ninja_customer_id") || metadataText(subscription?.metadata, "ninja_customer_id") || "-"}</td>
                  <td>{customer.data_check_required ? <Badge value="check" /> : "OK"}</td>
                  <td className="sticky-actions">
                    <div className="row-actions">
                      <button className="icon-button" onClick={() => setDetailCustomer(customer)} title="Bekijk klantprofiel" aria-label={`Bekijk klantprofiel van ${customerLabel(customer)}`}><Eye size={16} /></button>
                      {canMutateMembers && subscription && isAccessStatusActive(subscription.status) && (
                        <button className="icon-button" onClick={() => setCancellationCustomer(customer)} title="Opzegging inplannen" aria-label={`Opzegging inplannen voor ${customerLabel(customer)}`}><CalendarClock size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>}
      {reviewCustomerId && <div className="modal-backdrop" onClick={() => setReviewCustomerId(null)}><div className="modal detail-modal" role="dialog" aria-modal="true" aria-labelledby="payment-review-title" onClick={event => event.stopPropagation()}>
        <div className="modal-header"><h2 id="payment-review-title">Betaalcontrole</h2><button className="icon-button" aria-label="Sluiten" onClick={() => setReviewCustomerId(null)}><X size={16} /></button></div>
        <ArrearsReviewQueue data={reviewQueue ? { ...reviewQueue, rows: reviewQueue.rows.filter(row => row.customer_id === reviewCustomerId), unchecked: 0, unavailable: 0 } : null} error={reviewQueueError} query="" canRefresh={canMutateMembers} onReload={loadReviewQueue} onCustomer={id => { const customer = customers.find(row => row.id === id); if (customer) { setReviewCustomerId(null); setDetailCustomer(customer); } }} />
      </div></div>}
      {(status === "historical" || status === "all_with_history") && <HistoricalCustomersTable rows={filteredHistoricalRows} canViewFinancials={canViewFinancials} sort={historicalSort} onSort={setHistoricalSort} heading="Historisch" />}
      {addOpen && (
        <AddCustomerModal
          plans={plans}
          launches={launches}
          existingCustomers={customers}
          existingViewers={viewers}
          historicalParticipants={historicalParticipants}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); loadCustomers(); }}
          onOpenExisting={(customer) => { setAddOpen(false); setDetailCustomer(customer); }}
          onReactivateExisting={(customer) => { setAddOpen(false); setEditingCustomer({ customer, reactivatePreset: true }); }}
        />
      )}
      {editingCustomer && <EditCustomerModal customer={editingCustomer.customer} reactivatePreset={editingCustomer.reactivatePreset} existingCustomers={customers} existingViewers={viewers} plans={plans} launches={launches} canViewFinancials={canMutateMembers || canViewFinancials} onClose={() => setEditingCustomer(null)} onSaved={() => { setEditingCustomer(null); loadCustomers(); }} />}
      {detailCustomer && <CustomerDetailModal customer={detailCustomer} customers={customers} existingViewers={viewers} plans={plans} launches={launches} canViewFinancials={canViewFinancials} canMutateMembers={canMutateMembers} onClose={() => setDetailCustomer(null)} onChanged={loadCustomers} />}
      {cancellationCustomer && <CancelCustomerModal customer={cancellationCustomer} onClose={() => setCancellationCustomer(null)} onSaved={async () => { setCancellationCustomer(null); await loadCustomers(); }} />}
      {reviewingSignup && <LaunchBillingReviewModal signup={reviewingSignup} onClose={() => setReviewingSignup(null)} onSaved={async () => { setReviewingSignup(null); setSignupMessage("Het concept is gecontroleerd; versturen en periodieke facturering zijn gestart."); await loadCustomers(); }} />}
      {linkingSignup && <LaunchExistingBillingModal signup={linkingSignup} onClose={() => setLinkingSignup(null)} onSaved={async () => { setLinkingSignup(null); setSignupMessage("De bestaande factuur en periodieke factuur zijn gecontroleerd en gekoppeld."); await loadCustomers(); }} />}
      {cancellingSignup && <LaunchSignupCancellationModal signup={cancellingSignup} onClose={() => setCancellingSignup(null)} onSaved={async () => { setCancellingSignup(null); setSignupMessage("De aanmelding, factuur en periodieke facturering zijn geannuleerd. De onbetaald-lijst is bijgewerkt of wordt automatisch opnieuw geprobeerd."); await loadCustomers(); }} />}
    </section>
  );
}

function LaunchSignupTable({
  signups,
  customers,
  canViewFinancials,
  canMutateMembers,
  busyId,
  onResolve,
  onCancel,
  onReview,
  onLinkExisting,
  onOpenCustomer,
}: {
  signups: LaunchSignup[];
  customers: Customer[];
  canViewFinancials: boolean;
  canMutateMembers: boolean;
  busyId: string | null;
  onResolve: (signup: LaunchSignup, action: "retry") => void;
  onCancel: (signup: LaunchSignup) => void;
  onReview: (signup: LaunchSignup) => void;
  onLinkExisting: (signup: LaunchSignup) => void;
  onOpenCustomer: (customer: Customer) => void;
}) {
  return (
    <div className="table-shell launch-signup-table-shell">
      <table className="data-table launch-signup-table">
        <thead><tr>
          <th>Klant</th><th>Factuur e-mail</th><th>Bedrijf</th><th>Abonnement</th>
          <th>Facturering</th><th>Account</th><th>Status aanmelding</th><th>Factuur</th>
          {canViewFinancials && <th>Bedrag</th>}<th>Aangemeld</th><th>Betaald</th><th>Controle</th>
          <th className="sticky-actions">Acties</th>
        </tr></thead>
        <tbody>
          {signups.map((signup) => {
            const linkedCustomer = customers.find((customer) => customer.id === signup.customer_id)
              || customers.find((customer) => customer.billing_email.toLowerCase() === signup.email.toLowerCase());
            return (
            <tr key={signup.id}>
              <td><strong>{[signup.first_name, signup.last_name].filter(Boolean).join(" ") || signup.customer_name || "-"}</strong>{signup.is_test && <span><Badge value="test" /></span>}</td>
              <td>{signup.email}</td>
              <td>{signup.company_name || (signup.is_business_customer ? "Zakelijk" : "Privé")}</td>
              <td>{signup.plan_label}<span>{billingPeriodLabel(signup.billing_period)}</span></td>
              <td>{billingProviderLabel(signup.billing_provider)}</td>
              <td>{billingAccountLabel(signup.provider_account)}</td>
              <td><Badge value={launchSignupStatusLabel(signup.status)} tone={launchSignupStatusTone(signup.status)} /></td>
              <td>
                {signup.external_invoice_number || "-"}
                <span>{signup.invoice_status ? `Status: ${billingInvoiceStatusLabel(signup.invoice_status)}` : "Concept wordt aangemaakt"}</span>
              </td>
              {canViewFinancials && <td>{formatCurrency(signup.amount_due)}</td>}
              <td>{formatDateTime(signup.submitted_at)}</td>
              <td>{formatDateTime(signup.paid_at || signup.invoice_paid_at)}</td>
              <td>{signup.manual_review_reason || signup.last_error || "-"}</td>
              <td className="sticky-actions"><div className="row-actions compact-action-icons">
                {linkedCustomer && <button className="icon-button" onClick={() => onOpenCustomer(linkedCustomer)} title="Profiel bekijken" aria-label={`Profiel bekijken van ${launchSignupCustomerLabel(signup)}`}><Eye size={16} /></button>}
                {signup.invoice_status === "draft" && launchInvoiceAdminUrl(signup) && <a className="icon-button" href={launchInvoiceAdminUrl(signup)!} target="_blank" rel="noreferrer" title="Conceptfactuur openen" aria-label="Conceptfactuur openen"><FileText size={16} /></a>}
                {canMutateMembers && signup.status === "manual_review" && signup.billing_subscription_id && signup.external_invoice_id && <button className="icon-button" disabled={busyId === signup.id} onClick={() => onReview(signup)} title="Conceptfactuur versturen" aria-label="Conceptfactuur versturen"><Send size={16} /></button>}
                {canMutateMembers && ["manual_review", "billing_failed", "received"].includes(signup.status) && signup.billing_subscription_id && <button className="icon-button" disabled={busyId === signup.id} onClick={() => onLinkExisting(signup)} title="Bestaande factuur koppelen" aria-label="Bestaande factuur koppelen"><Link2 size={16} /></button>}
                {canMutateMembers && ["billing_failed", "received"].includes(signup.status) && <button className="icon-button" disabled={busyId === signup.id} onClick={() => onResolve(signup, "retry")} title="Opnieuw proberen" aria-label="Opnieuw proberen"><RefreshCw size={16} /></button>}
                {canMutateMembers && launchSignupCanBeCancelled(signup.status) && <button className="icon-button danger" disabled={busyId === signup.id} onClick={() => onCancel(signup)} title={signup.status === "cancellation_pending" ? "Annulering opnieuw uitvoeren" : "Aanmelding annuleren"} aria-label={signup.status === "cancellation_pending" ? "Annulering opnieuw uitvoeren" : "Aanmelding annuleren"}><X size={16} /></button>}
              </div></td>
            </tr>
            );
          })}
          {signups.length === 0 && <tr><td colSpan={canViewFinancials ? 13 : 12} className="empty">Geen aanmeldingen in deze selectie.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function launchSignupCanBeCancelled(status: string) {
  return !["payment_received", "activation_pending", "active", "cancelled"].includes(status);
}

function LaunchSignupCancellationModal({
  signup,
  nested = false,
  onClose,
  onSaved,
}: {
  signup: LaunchSignup;
  nested?: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmationMatches = confirmationEmail.trim().toLowerCase() === signup.email.trim().toLowerCase();
  const invoiceAction = signup.invoice_status === "draft"
    ? "De conceptfactuur wordt bij de factuurprovider verwijderd."
    : signup.invoice_status === "sent"
      ? signup.billing_provider === "wefact"
        ? "De verstuurde WeFact-factuur wordt volledig gecrediteerd."
        : "De verstuurde Invoice Ninja-factuur wordt geannuleerd."
      : ["cancelled", "reversed"].includes(String(signup.invoice_status))
        ? "De factuur is al geannuleerd of teruggedraaid; dit wordt gecontroleerd."
        : signup.external_invoice_id
          ? "De actuele providerstatus wordt gecontroleerd en de onbetaalde factuur wordt veilig afgehandeld."
          : "Er is nog geen factuur om te verwijderen of te crediteren.";

  async function cancelSignup(event: React.FormEvent) {
    event.preventDefault();
    if (!confirmationMatches) {
      setError("Vul exact het factuur-e-mailadres van deze aanmelding in.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data, error: requestError } = await supabase!.rpc("request_launch_signup_cancellation", {
        p_signup_id: signup.id,
        p_confirmation_email: confirmationEmail.trim(),
      });
      if (requestError) throw requestError;
      const result = data as { job_id?: string | null; already_completed?: boolean } | null;
      if (result?.job_id && !result.already_completed) {
        await processBillingNow({ jobId: result.job_id });
      }
      await onSaved();
    } catch (cancelError) {
      setError(readableErrorMessage(cancelError, "De aanmelding kon niet worden geannuleerd."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={nested ? "nested-modal-backdrop" : "modal-backdrop"}>
      <form className="modal launch-cancellation-modal" onSubmit={cancelSignup}>
        <div className="modal-header">
          <div><h2>Aanmelding annuleren</h2><p className="muted">{launchSignupCustomerLabel(signup)} · {signup.email}</p></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="banner warning">Deze actie is alleen bedoeld voor een onbetaalde aanmelding. Een (deels) betaalde factuur blokkeert de annulering automatisch.</div>
        <ul className="cancellation-check-list">
          <li>{invoiceAction}</li>
          <li>De periodieke facturering wordt direct stopgezet, ook als de factuur eerder handmatig is verwijderd.</li>
          <li>De contactpersoon wordt uit de ActiveCampaign-lijst “aangemeld, niet betaald” geschreven.</li>
          <li>Thinkific en betaalde ledenlijsten worden niet aangepast, omdat voor een onbetaalde aanmelding nog geen toegang hoort te bestaan.</li>
        </ul>
        <label className="confirmation-field">
          Bevestig door het e-mailadres exact in te voeren
          <input type="email" autoComplete="off" value={confirmationEmail} onChange={(event) => setConfirmationEmail(event.target.value)} placeholder={signup.email} />
        </label>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={busy}>Terug</button>
          <button type="submit" className="primary-button danger-button" disabled={busy || !confirmationMatches}>{busy ? "Annulering verwerken…" : "Definitief annuleren"}</button>
        </div>
      </form>
    </div>
  );
}

function LaunchBillingReviewModal({ signup, onClose, onSaved }: { signup: LaunchSignup; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [vatRegime, setVatRegime] = useState(signup.vat_regime === "reverse_charge" ? "reverse_charge" : "standard_21");
  const [amountDue, setAmountDue] = useState(Number(signup.amount_due || 0).toFixed(2));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const grossPlanPrice = Number(signup.price_incl_vat_applied || signup.amount_due || 0);

  function changeVatRegime(value: string) {
    setVatRegime(value);
    const amount = value === "standard_21" ? grossPlanPrice : grossPlanPrice / 1.21;
    setAmountDue(amount.toFixed(2));
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { error: rpcError } = await supabase!.rpc("finalize_launch_signup_draft", {
        p_signup_id: signup.id,
        p_vat_regime: vatRegime,
        p_amount_due: Number(amountDue),
        p_note: note.trim() || null,
      });
      if (rpcError) throw rpcError;
      await processBillingNow({ signupId: signup.id });
      await onSaved();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal launch-billing-review-modal" onSubmit={save}>
        <div className="modal-header"><div><h2>Concept controleren en versturen</h2><p className="muted">{launchSignupCustomerLabel(signup)} · {signup.email}</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button></div>
        <div className="launch-review-summary">
          <span><strong>Land</strong>{signup.country_code}</span>
          <span><strong>Btw-nummer</strong>{signup.tax_number || "Niet ingevuld"}</span>
          <span><strong>VIES</strong>{vatValidationLabel(signup.vat_validation_status)}</span>
          <span><strong>Facturering</strong>{billingProviderLabel(signup.billing_provider)} · {billingAccountLabel(signup.provider_account)}</span>
        </div>
        {signup.manual_review_reason && <div className="banner warning">{signup.manual_review_reason}</div>}
        {launchInvoiceAdminUrl(signup) && <p><a className="secondary-button inline-link" href={launchInvoiceAdminUrl(signup)!} target="_blank" rel="noreferrer"><FileText size={15} /> Conceptfactuur in {billingProviderLabel(signup.billing_provider)} openen</a></p>}
        <div className="form-grid">
          <label>Btw-behandeling<select value={vatRegime} onChange={(event) => changeVatRegime(event.target.value)}><option value="standard_21">21% btw (bedrag inclusief)</option><option value="reverse_charge">0% / btw verlegd</option></select></label>
          <label>Definitief factuurbedrag<input type="number" min="0.01" step="0.01" value={amountDue} onChange={(event) => setAmountDue(event.target.value)} required /></label>
          <label className="span-2">Interne notitie<textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Waarom is deze btw-behandeling goedgekeurd?" /></label>
        </div>
        <p className="muted">Na bevestiging werkt de beheertool het concept bij, verstuurt de eerste factuur en start de periodieke facturering vanaf de volgende periode. De aanmelding verschuift pas na een geslaagde providerreactie.</p>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annuleren</button><button className="primary-button" disabled={busy}>{busy ? "Concept verwerken…" : "Concept goedkeuren en versturen"}</button></div>
      </form>
    </div>
  );
}

function LaunchExistingBillingModal({ signup, onClose, onSaved }: { signup: LaunchSignup; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [vatRegime, setVatRegime] = useState(signup.vat_regime === "reverse_charge" ? "reverse_charge" : "standard_21");
  const [amountDue, setAmountDue] = useState(Number(signup.amount_due || 0).toFixed(2));
  const [note, setNote] = useState("");
  const [candidates, setCandidates] = useState<ExistingBillingCandidate[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [selectedRecurringId, setSelectedRecurringId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const grossPlanPrice = Number(signup.price_incl_vat_applied || signup.amount_due || 0);
  const selectedCandidate = candidates.find((candidate) => candidate.invoiceId === selectedInvoiceId) || null;

  useEffect(() => {
    let cancelled = false;
    loadExistingBillingCandidates(signup.id)
      .then((items) => {
        if (cancelled) return;
        setCandidates(items);
        if (items.length === 1) {
          setSelectedInvoiceId(items[0].invoiceId);
          setSelectedRecurringId(items[0].recommendedRecurringId || "");
        }
      })
      .catch((loadError) => { if (!cancelled) setError(loadError instanceof Error ? loadError.message : String(loadError)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [signup.id]);

  function changeVatRegime(value: string) {
    setVatRegime(value);
    setAmountDue((value === "standard_21" ? grossPlanPrice : grossPlanPrice / 1.21).toFixed(2));
  }

  function chooseCandidate(candidate: ExistingBillingCandidate) {
    setSelectedInvoiceId(candidate.invoiceId);
    setSelectedRecurringId(candidate.recommendedRecurringId || "");
    setAmountDue(Number(candidate.amountDue).toFixed(2));
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedCandidate || !selectedRecurringId) {
      setError("Kies een factuur en de bijbehorende periodieke factuur.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { error: rpcError } = await supabase!.rpc("link_existing_launch_billing", {
        p_signup_id: signup.id,
        p_vat_regime: vatRegime,
        p_amount_due: Number(amountDue),
        p_invoice_id: selectedCandidate.invoiceId,
        p_recurring_id: selectedRecurringId,
        p_note: note.trim() || null,
      });
      if (rpcError) throw rpcError;
      await processBillingNow({ signupId: signup.id });
      await onSaved();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal launch-existing-billing-modal" onSubmit={save}>
        <div className="modal-header"><div><h2>Verstuurde factuur ophalen</h2><p className="muted">Automatisch zoeken op {signup.email} in {billingAccountLabel(signup.provider_account)}</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button></div>
        <div className="form-grid compact-review-fields">
          <label>Btw-behandeling<select value={vatRegime} onChange={(event) => changeVatRegime(event.target.value)}><option value="standard_21">21% btw</option><option value="reverse_charge">0% / btw verlegd</option></select></label>
          <label>Verwacht bedrag<input type="number" min="0.01" step="0.01" value={amountDue} onChange={(event) => setAmountDue(event.target.value)} required /></label>
        </div>
        {loading && <div className="loading-state"><RefreshCw className="spin" size={18} /> Facturen ophalen…</div>}
        {!loading && candidates.length === 0 && !error && <div className="banner warning">Geen recente facturen gevonden voor dit e-mailadres in het gekozen factuuraccount. Controleer eerst of de factuur bij de juiste klant en administratie staat.</div>}
        {!loading && candidates.length > 0 && <div className="existing-billing-candidates">
          {candidates.map((candidate) => (
            <label key={candidate.invoiceId} className={selectedInvoiceId === candidate.invoiceId ? "selected" : ""}>
              <input type="radio" name="existing-invoice" checked={selectedInvoiceId === candidate.invoiceId} onChange={() => chooseCandidate(candidate)} />
              <span><strong>{candidate.invoiceNumber}</strong><small>{formatDate(candidate.invoiceDate)} · {formatCurrency(candidate.amountDue)} · {billingInvoiceStatusLabel(candidate.status)}</small></span>
              {Math.abs(Number(candidate.amountDue) - Number(amountDue || 0)) < 0.005 ? <Badge value="bedrag klopt" tone="success" /> : <Badge value="bedrag controleren" tone="warning" />}
            </label>
          ))}
        </div>}
        {selectedCandidate && <div className="form-grid compact-review-fields">
          <label className="span-2">Periodieke factuur<select value={selectedRecurringId} onChange={(event) => setSelectedRecurringId(event.target.value)} required><option value="">Kies de actieve periodieke factuur</option>{selectedCandidate.recurringCandidates.filter((recurring) => recurring.status === "active").map((recurring) => <option key={recurring.id} value={recurring.id}>{recurring.label} · {formatCurrency(recurring.amountDue)} · volgende {formatDate(recurring.nextInvoiceDate)}</option>)}</select></label>
          <label className="span-2">Interne notitie<textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} /></label>
        </div>}
        <p className="muted">De koppeling wordt pas opgeslagen nadat het factuursysteem e-mail, klant, bedrag, factuurstatus en periodieke factuur opnieuw heeft gecontroleerd.</p>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annuleren</button><button className="primary-button" disabled={busy || loading || !selectedCandidate || !selectedRecurringId}>{busy ? "Controleren en koppelen…" : "Geselecteerde factuur koppelen"}</button></div>
      </form>
    </div>
  );
}

async function loadExistingBillingCandidates(signupId: string) {
  const session = await supabase!.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Geen actieve sessie gevonden.");
  const response = await fetch(`${publicSupabaseUrl}/functions/v1/billing-existing-candidates?signup_id=${encodeURIComponent(signupId)}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Facturen ophalen mislukt.");
  return (payload.candidates || []) as ExistingBillingCandidate[];
}

function launchSignupCustomerLabel(signup: LaunchSignup) {
  return [signup.first_name, signup.last_name].filter(Boolean).join(" ") || signup.company_name || signup.customer_name || signup.email;
}

function launchInvoiceAdminUrl(signup: LaunchSignup) {
  const metadataUrl = signup.invoice_metadata?.providerAdminUrl;
  if (typeof metadataUrl === "string" && /^https:\/\//i.test(metadataUrl)) return metadataUrl;
  if (signup.billing_provider === "invoice_ninja" && signup.external_invoice_id) {
    return `https://invoicing.co/#/invoices/${encodeURIComponent(signup.external_invoice_id)}/edit`;
  }
  if (signup.billing_provider === "wefact" && signup.external_invoice_id) return "https://www.mijnwefact.nl/";
  return null;
}

function vatValidationLabel(status: string) {
  const labels: Record<string, string> = { valid: "Geldig", invalid: "Niet bevestigd", unavailable: "Niet beschikbaar", manual: "Handmatig", not_required: "Niet nodig" };
  return labels[status] || status || "Onbekend";
}

type LaunchAssignmentRow = {
  id: string;
  launch_id: string;
  customer_id: string;
  subscription_id: string | null;
  assignment_method: string;
  confidence: string;
  needs_review: boolean;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  billing_email: string;
  customer_status: string;
  subscription_status: string | null;
  start_date: string | null;
  plan_label: string | null;
  price_incl_vat_applied: number | null;
};

type LaunchUnassignedRow = {
  customer_id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  billing_email: string;
  customer_status: string;
  subscription_status: string;
  subscription_id: string;
  start_date: string | null;
  plan_label: string | null;
  price_incl_vat_applied: number | null;
};

type LaunchParticipantIdentityRow = {
  id: string;
  launch_id: string;
  email: string | null;
  email_normalized: string | null;
  matched_customer_id: string | null;
};

type LaunchAssignmentIdentityRow = {
  launch_id: string;
  customer_id: string;
  billing_email: string | null;
  subscription_id: string | null;
  created_at: string | null;
  subscription_status: string | null;
  start_date: string | null;
  cancellation_date: string | null;
  access_end_date: string | null;
};

type LaunchSortKey = "name" | "launch_month" | "status" | "participants" | "active" | "active_percent" | "retention_months" | "cohort_age_months" | "first_year_revenue" | "current_annual_revenue";
type LaunchSort = { key: LaunchSortKey; direction: "asc" | "desc" };

function LaunchesPage({ canViewFinancials, canMutateMembers }: { canViewFinancials: boolean; canMutateMembers: boolean }) {
  const detailRef = useRef<HTMLElement>(null);
  const detailRequest = useRef(0);
  const [launches, setLaunches] = useState<LaunchOverview[]>([]);
  const [selectedSignupMetrics, setSelectedSignupMetrics] = useState<LaunchSignupMetrics | null>(null);
  const [selectedSignups, setSelectedSignups] = useState<LaunchSignup[]>([]);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailRevision, setDetailRevision] = useState(0);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("reporting");
  const [selected, setSelected] = useState<LaunchOverview | null>(null);
  const [assignments, setAssignments] = useState<LaunchAssignmentRow[]>([]);
  const [participants, setParticipants] = useState<LaunchParticipant[]>([]);
  const [unassigned, setUnassigned] = useState<LaunchUnassignedRow[]>([]);
  const [knownUniqueParticipantCount, setKnownUniqueParticipantCount] = useState<number | null>(null);
  const [dashboardTotals, setDashboardTotals] = useState<DashboardTotals | null>(null);
  const [identityAssignments, setIdentityAssignments] = useState<LaunchAssignmentIdentityRow[]>([]);
  const [sort, setSort] = useState<LaunchSort>({ key: "launch_month", direction: "desc" });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingLaunch, setEditingLaunch] = useState<LaunchOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadLaunches();
  }, [canViewFinancials]);

  useEffect(() => {
    const requestedId = new URLSearchParams(window.location.hash.split("?")[1] || "").get("launch");
    const requested = launches.find(launch => launch.id === requestedId);
    if (requested) void openDetail(requested);
  }, [launches]);

  useEffect(() => () => { detailRequest.current += 1; }, []);

  async function loadLaunches() {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const [operationalResult, financialResult, unassignedResult, dashboardTotalsResult] = await Promise.all([
      supabase.from("operational_launch_overview").select("*").order("launch_month", { ascending: false }),
      canViewFinancials ? supabase.from("financial_launch_overview").select("*") : Promise.resolve({ data: [], error: null }),
      supabase.from("launch_unassigned_customers").select("*").order("start_date", { ascending: true }),
      supabase.from(canViewFinancials ? "financial_dashboard_totals" : "operational_dashboard_totals").select("*").maybeSingle(),
    ]);
    if (operationalResult.error) setError(operationalResult.error.message);
    if (unassignedResult.error) setError(unassignedResult.error.message);
    if (dashboardTotalsResult.error) setError(dashboardTotalsResult.error.message);
    setDashboardTotals((dashboardTotalsResult.data || null) as DashboardTotals | null);
    const financialById = new Map((financialResult.data || []).map((row) => [row.id, row]));
    const merged = ((operationalResult.data || []) as LaunchOverview[]).map((row) => ({ ...row, ...(financialById.get(row.id) || {}) }));
    setLaunches(merged);
    setUnassigned((unassignedResult.data || []) as LaunchUnassignedRow[]);

    const allLaunchIds = merged.map((launch) => launch.id);
    const reportingLaunchIds = merged.filter((launch) => launch.is_reporting_cohort).map((launch) => launch.id);
    if (reportingLaunchIds.length > 0) {
      const [participantIdentityResult, assignmentIdentityResult] = await Promise.all([
        supabase.from("launch_participants").select("id,launch_id,email,email_normalized,matched_customer_id").in("launch_id", reportingLaunchIds),
        supabase.from("launch_customer_assignments").select("launch_id,customer_id,billing_email,subscription_id,created_at,subscription_status,start_date,cancellation_date,access_end_date").in("launch_id", allLaunchIds),
      ]);
      if (participantIdentityResult.error) setError(participantIdentityResult.error.message);
      if (assignmentIdentityResult.error) setError(assignmentIdentityResult.error.message);
      setIdentityAssignments((assignmentIdentityResult.data || []) as LaunchAssignmentIdentityRow[]);
      setKnownUniqueParticipantCount(uniqueLaunchParticipantCount(
        (participantIdentityResult.data || []) as LaunchParticipantIdentityRow[],
        (assignmentIdentityResult.data || [])
          .filter((row) => reportingLaunchIds.includes(row.launch_id)) as LaunchAssignmentIdentityRow[],
      ));
    } else {
      setKnownUniqueParticipantCount(0);
      setIdentityAssignments([]);
    }
    setLoading(false);
  }

  async function openDetail(launch: LaunchOverview, scroll = true) {
    const request = ++detailRequest.current;
    window.history.replaceState({}, "", `#/lanceringen?launch=${encodeURIComponent(launch.id)}`);
    setSelected(launch);
    setDetailLoading(true);
    setDetailError(null);
    setAssignments([]); setParticipants([]); setSelectedSignups([]); setSelectedSignupMetrics(null);
    setDetailRevision(value => value + 1);
    if (scroll) requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    try {
      const [assignmentResult, participantResult, metricsResult, signupsResult] = await withTimeout(Promise.all([
        supabase!.from("launch_customer_assignments").select("*").eq("launch_id", launch.id).order("start_date", { ascending: true }),
        supabase!.from("launch_participant_details").select("*").eq("launch_id", launch.id).order("signup_date", { ascending: true }),
        supabase!.from("launch_signup_metrics").select("*").eq("launch_id", launch.id).maybeSingle(),
        supabase!.from("launch_signup_details")
          .select("id,launch_id,email,first_name,last_name,company_name,customer_name,plan_label,status,is_test,submitted_at,paid_at,invoice_paid_at,external_invoice_number,invoice_status")
          .eq("launch_id", launch.id).eq("is_test", false).not("status", "in", "(duplicate,cancelled)")
          .order("submitted_at", { ascending: false }).limit(MEMBER_LIST_LIMIT),
      ]), DASHBOARD_DETAILS_TIMEOUT_MS, "Lanceringsdetails laden");
      if (request !== detailRequest.current) return;
      const errors = [assignmentResult, participantResult, metricsResult, signupsResult].flatMap(result => result.error ? [result.error.message] : []);
      if (errors.length) setDetailError(errors.join(" · "));
      setAssignments((assignmentResult.data || []) as LaunchAssignmentRow[]);
      setParticipants((participantResult.data || []) as LaunchParticipant[]);
      setSelectedSignupMetrics((metricsResult.data || null) as LaunchSignupMetrics | null);
      setSelectedSignups((signupsResult.data || []) as LaunchSignup[]);
    } catch (error) {
      if (request === detailRequest.current) setDetailError(readableErrorMessage(error, "Lanceringsdetails konden niet worden geladen"));
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  }

  const statusCounts = useMemo(() => launches.reduce<Record<string, number>>((counts, launch) => {
    counts[launch.status] = (counts[launch.status] || 0) + 1;
    return counts;
  }, {}), [launches]);
  const filtered = useMemo(() => launches.filter((launch) => {
    const haystack = `${launch.name} ${launch.launch_month} ${launch.status}`.toLowerCase();
    const statusMatches = status === "reporting" ? launch.is_reporting_cohort : status === "all" || launch.status === status;
    return statusMatches && haystack.includes(query.toLowerCase());
  }), [launches, query, status]);
  const sortedFiltered = useMemo(() => [...filtered].sort((a, b) => compareLaunches(a, b, sort, identityAssignments)), [filtered, identityAssignments, sort]);
  const reportingLaunches = useMemo(() => launches.filter((launch) => launch.is_reporting_cohort), [launches]);
  const totalReportedParticipants = useMemo(() => reportingLaunches.reduce((total, launch) => total + totalLaunchParticipantCount(launch), 0), [reportingLaunches]);
  const firstYearLaunchRevenue = useMemo(() => reportingLaunches.reduce((total, launch) => total + historicalFirstYearRevenueExclVat(launch), 0), [reportingLaunches]);
  const currentLaunchRevenue = useMemo(() => canViewFinancials && dashboardTotals?.arr_excl_vat != null
    ? Number(dashboardTotals.arr_excl_vat)
    : reportingLaunches.reduce((total, launch) => total + currentAnnualRevenueExclVat(launch), 0), [canViewFinancials, dashboardTotals, reportingLaunches]);
  const launchRevenueRows = useMemo(() => [...reportingLaunches]
    // “Buiten lancering – heractivatie” is an open catch-all, not a
    // dated launch. The chart therefore shows only the official launches,
    // from the earliest month through the latest one.
    .filter((launch) => Boolean(launch.launch_month))
    .sort((a, b) => (a.launch_month || "").localeCompare(b.launch_month || ""))
    .map((launch) => ({
      label: formatLaunchMonth(launch.launch_month),
      firstYearRevenue: historicalFirstYearRevenueExclVat(launch),
      currentAnnualRevenue: currentAnnualRevenueExclVat(launch),
    })), [reportingLaunches]);
  const filteredLaunchTotals = useMemo(() => ({
    participants: filtered.reduce((total, launch) => total + totalLaunchParticipantCount(launch), 0),
    active: filtered.reduce((total, launch) => total + launch.active_paid_member_count, 0),
    firstYearRevenue: filtered.reduce((total, launch) => total + historicalFirstYearRevenueExclVat(launch), 0),
    currentAnnualRevenue: filtered.reduce((total, launch) => total + currentAnnualRevenueExclVat(launch), 0),
  }), [filtered]);
  const showDashboardTotals = status === "reporting" && query.trim() === "";
  // De totaalrij moet optellen uit precies de rijen die in de tabel zichtbaar
  // zijn. Het Dashboard-totaal omvat ook gearchiveerde en niet-gekoppelde
  // abonnementsruns en hoort daarom niet in deze kolomtotaalrij.
  const displayedActiveTotal = filteredLaunchTotals.active;
  const displayedCurrentAnnualRevenue = showDashboardTotals && canViewFinancials && dashboardTotals
    ? dashboardTotals.arr_excl_vat
    : filteredLaunchTotals.currentAnnualRevenue;
  const displayedActivePercent = filteredLaunchTotals.participants > 0
    ? (displayedActiveTotal / filteredLaunchTotals.participants) * 100
    : null;

  return (
    <section className="page full-width-page">
      <PageHeader
        title="Lanceringen"
        action={canMutateMembers ? <button className="primary-button" onClick={() => setCreateOpen(true)}><Rocket size={16} /> Nieuwe lancering</button> : undefined}
      />
      <MadeiraOverview mode="launches" canViewFinancials={canViewFinancials} canMutateMembers={canMutateMembers} />
      <div className="stats-grid priority launch-report-stats">
        <Stat label="Totaal bekende deelnemers" value={formatNumber(totalReportedParticipants)} tone="brown" />
        <Stat label="Unieke deelnemers (bekende records)" value={knownUniqueParticipantCount === null ? "-" : formatNumber(knownUniqueParticipantCount)} tone="green" />
        {canViewFinancials && <Stat label="Eerstejaarsomzet lanceringen excl. btw" value={formatCurrency(firstYearLaunchRevenue)} tone="orange" />}
        {canViewFinancials && <Stat label="Actuele jaaromzet volgens Dashboard excl. btw" value={formatCurrency(currentLaunchRevenue)} tone="green" />}
      </div>
      <p className="page-description">Open lanceringen staan ook op het dashboard. Na afsluiten blijven de cijfers en aanmeldingen hier beschikbaar via Details; latere betalingen worden bijgewerkt.</p>
      {canViewFinancials && <section className="panel launch-revenue-panel">
        <div className="panel-title-row"><div className="panel-title">Omzet per lancering excl. btw</div></div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={launchRevenueRows} margin={{ top: 20, right: 12, left: 0, bottom: 48 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={68} />
            <YAxis tickFormatter={(value) => formatCurrency(Number(value))} width={88} />
            <Tooltip content={<LaunchRevenueTooltip />} />
            <Bar dataKey="firstYearRevenue" name="Eerste jaar" fill="#8B6F5E" radius={[4, 4, 0, 0]} />
            <Bar dataKey="currentAnnualRevenue" name="Actueel jaar" fill="#2D6A4F" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </section>}
      <Toolbar query={query} onQuery={setQuery} status={status} onStatus={setStatus} statuses={["reporting", "all", "open", "closed", "draft", "archived"]} counts={{ reporting: launches.filter((launch) => launch.is_reporting_cohort).length, all: launches.length, ...statusCounts }} />
      {error && <div className="banner error">{error}</div>}
      <div className="table-shell launch-table-shell">
        <table className="data-table launch-table">
          <thead><tr>
            <LaunchSortableHeader label="Lancering" sortKey="name" sort={sort} onSort={setSort} className="launch-name-col" />
            <LaunchSortableHeader label="Maand" sortKey="launch_month" sort={sort} onSort={setSort} className="launch-month-col" />
            <LaunchSortableHeader label="Status" sortKey="status" sort={sort} onSort={setSort} className="launch-status-col" />
            <LaunchSortableHeader label={'Totaal\ndeelnemers'} sortKey="participants" sort={sort} onSort={setSort} className="launch-count-col" />
            <LaunchSortableHeader label={'Nu\nactief'} sortKey="active" sort={sort} onSort={setSort} className="launch-count-col" />
            <LaunchSortableHeader label={'%\nactief'} sortKey="active_percent" sort={sort} onSort={setSort} className="launch-count-col" />
            <LaunchSortableHeader label={'Gem. retentie\n(mnd)'} sortKey="retention_months" sort={sort} onSort={setSort} className="launch-retention-col" />
            <LaunchSortableHeader label={'Leeftijd cohort\n(mnd)'} sortKey="cohort_age_months" sort={sort} onSort={setSort} className="launch-retention-col" />
            {canViewFinancials && <><LaunchSortableHeader label={'1e jaar omzet\nexcl. btw'} sortKey="first_year_revenue" sort={sort} onSort={setSort} className="launch-revenue-col" /><LaunchSortableHeader label={'Actuele jaaromzet\nexcl. btw'} sortKey="current_annual_revenue" sort={sort} onSort={setSort} className="launch-revenue-col" /></>}
            <th className="launch-actions-col">Acties</th>
          </tr></thead>
          <tbody>
            {loading && <TableLoading colSpan={canViewFinancials ? 11 : 9} />}
            {!loading && sortedFiltered.map((launch) => {
              const metrics = launchRowMetrics(launch, identityAssignments);
              return (
              <tr key={launch.id} className={selected?.id === launch.id ? "selected-row" : ""}>
                <td className="launch-name-col"><strong>{launch.name}</strong></td>
                <td className="launch-month-col">{formatLaunchMonth(launch.launch_month)}</td>
                <td className="launch-status-col"><Badge value={launch.status} /></td>
                <td className="launch-count-col">{formatNumber(totalLaunchParticipantCount(launch))}</td>
                <td className="launch-count-col">{formatNumber(launch.active_paid_member_count)}</td>
                <td className="launch-count-col">{formatPercent(metrics.activePercent ?? undefined)}</td>
                <td className="launch-retention-col">{formatDecimal(metrics.retentionMonths ?? undefined, 1)}</td>
                <td className="launch-retention-col">{formatDecimal(metrics.cohortAgeMonths ?? undefined, 1)}</td>
                {canViewFinancials && <><td className="launch-revenue-col">{formatCurrency(historicalFirstYearRevenueExclVat(launch))}</td><td className="launch-revenue-col">{formatCurrency(currentAnnualRevenueExclVat(launch))}</td></>}
                <td className="launch-actions-col"><div className="row-actions">{canMutateMembers && <button className="icon-button" onClick={() => setEditingLaunch(launch)} title="Lancering wijzigen" aria-label={`Lancering ${launch.name} wijzigen`}><Edit3 size={14} /></button>}<button className="secondary-button compact" onClick={() => openDetail(launch)}>Details</button></div></td>
              </tr>
              );
            })}
            {!loading && sortedFiltered.length === 0 && <tr><td colSpan={canViewFinancials ? 11 : 9} className="empty">Geen lanceringen gevonden.</td></tr>}
          </tbody>
          {!loading && sortedFiltered.length > 0 && <tfoot><tr className="total-row">
            <td className="total-label" colSpan={3}><strong>Totaal</strong></td>
            <td className="launch-count-col">{formatNumber(filteredLaunchTotals.participants)}</td>
            <td className="launch-count-col" title={showDashboardTotals && dashboardTotals ? `Som van de zichtbare lanceringen. Dashboardtotaal: ${formatNumber(canViewFinancials ? dashboardTotals.active_paid_members : dashboardTotals.active_members)}; dat omvat ook gearchiveerde en niet-gekoppelde runs.` : "Som van de zichtbare lanceringen; een heractivatie kan in meerdere lanceringen voorkomen."}>{formatNumber(displayedActiveTotal)}</td>
            <td className="launch-count-col">{formatPercent(displayedActivePercent ?? undefined)}</td>
            <td className="launch-retention-col">-</td>
            <td className="launch-retention-col">-</td>
            {canViewFinancials && <><td className="launch-revenue-col">{formatCurrency(filteredLaunchTotals.firstYearRevenue)}</td><td className="launch-revenue-col" title={showDashboardTotals && dashboardTotals ? "Dashboardtotaal; omvat ook gearchiveerde en niet-gekoppelde runs." : "Som van de zichtbare lanceringen."}>{formatCurrency(displayedCurrentAnnualRevenue)}</td></>}
            <td className="launch-actions-col" />
          </tr></tfoot>}
        </table>
      </div>
      <section className="panel launch-unassigned-panel">
        <div className="panel-title-row"><div><div className="panel-title">Nog niet ingedeelde huidige abonnementsruns ({unassigned.length})</div><div className="muted">Dit zijn startmaanden waarvoor nog geen bekende lancering bestaat. Ze blijven gewoon onderdeel van Klanten.</div></div></div>
        <div className="table-panel"><table className="data-table compact-dashboard-table"><thead><tr><th>Klant</th><th>E-mail</th><th>Start</th><th>Abonnement</th><th>Status</th>{canViewFinancials && <th>Prijs incl.</th>}</tr></thead><tbody>
          {unassigned.map((row) => <tr key={row.subscription_id}><td>{row.display_name || displayName(row.first_name, row.last_name, row.company_name, null)}</td><td>{row.billing_email}</td><td>{formatDate(row.start_date)}</td><td>{row.plan_label || "-"}</td><td><Badge value={row.customer_status} /></td>{canViewFinancials && <td>{formatCurrency(row.price_incl_vat_applied)}</td>}</tr>)}
          {unassigned.length === 0 && <tr><td colSpan={canViewFinancials ? 6 : 5} className="empty">Alle bekende abonnementsruns zijn aan een lancering gekoppeld.</td></tr>}
        </tbody></table></div>
      </section>
      {selected && (
        <section ref={detailRef} className="panel launch-detail-panel" aria-label={`Details ${selected.name}`}>
          <div className="panel-title-row">
            <div><div className="panel-title">{selected.name}</div><div className="muted">{formatLaunchMonth(selected.launch_month)} · {selected.notes || "Geen notitie"}</div></div>
            <div className="row-actions">
              <button className="secondary-button compact" disabled={detailLoading} onClick={() => void openDetail(selected, false)}><RefreshCw size={14} /> Gegevens vernieuwen</button>
              <button className="icon-button" onClick={() => { detailRequest.current += 1; setSelected(null); navigateToPage("launches"); }} aria-label="Details sluiten"><X size={16} /></button>
            </div>
          </div>
          {detailError && <div className="banner error" role="alert">{detailError}</div>}
          {(detailLoading || (selectedSignupMetrics?.total_count || 0) > 0 || (assignments.length === 0 && participants.length === 0)) &&
            <LaunchSignupDashboard launch={selected} metrics={selectedSignupMetrics} canViewFinancials={canViewFinancials} />}
          {!detailLoading && selectedSignups.length > 0 && <>
            <h3 className="launch-records-heading">Aanmeldingen en betaalstatus ({selectedSignups.length})</h3>
            <div className="table-panel"><table className="data-table compact-dashboard-table">
              <thead><tr><th>Naam</th><th>E-mail</th><th>Abonnement</th><th>Status</th><th>Factuur</th><th>Aangemeld</th><th>Betaald op</th></tr></thead>
              <tbody>{selectedSignups.map(signup => <tr key={signup.id}>
                <td>{launchSignupCustomerLabel(signup)}</td><td>{signup.email}</td><td>{signup.plan_label}</td>
                <td><Badge value={launchSignupStatusLabel(signup.status)} tone={launchSignupStatusTone(signup.status)} /></td>
                <td>{signup.external_invoice_number || "-"}<span>{signup.invoice_status ? billingInvoiceStatusLabel(signup.invoice_status) : "Nog geen factuur"}</span></td>
                <td>{formatDateTime(signup.submitted_at)}</td><td>{formatDateTime(signup.invoice_paid_at || signup.paid_at)}</td>
              </tr>)}</tbody>
            </table></div>
          </>}
          <LaunchInsights key={`${selected.id}:${detailRevision}`} launchId={selected.id} canViewFinancials={canViewFinancials} onRefresh={() => void openDetail(selected, false)} />
          <h3 className="launch-records-heading">Onderliggende deelnemers</h3>
          {detailLoading ? <div className="empty">Details laden...</div> : (
            <div className="launch-detail-grid">
              <div>
                <h3>Huidige klantrecords ({assignments.length})</h3>
                <div className="table-panel"><table className="data-table compact-dashboard-table"><thead><tr><th>Klant</th><th>E-mail</th><th>Start</th><th>Status</th><th>Koppeling</th></tr></thead><tbody>
                  {assignments.map((assignment) => <tr key={assignment.id}><td>{assignment.display_name || displayName(assignment.first_name, assignment.last_name, assignment.company_name, null)}</td><td>{assignment.billing_email}</td><td>{formatDate(assignment.start_date)}</td><td><Badge value={assignment.customer_status} /></td><td>{assignment.assignment_method} / {assignment.confidence}</td></tr>)}
                  {assignments.length === 0 && <tr><td colSpan={5} className="empty">Geen klantrecord gekoppeld.</td></tr>}
                </tbody></table></div>
              </div>
              <div>
                <h3>Historische deelnemers ({participants.length})</h3>
                <div className="table-panel"><table className="data-table compact-dashboard-table"><thead><tr><th>Naam</th><th>E-mail</th><th>Datum</th><th>Abonnementstype</th>{canViewFinancials && <th>1e jaar model</th>}<th>Zekerheid</th><th>Match</th></tr></thead><tbody>
                  {participants.map((participant) => <tr key={participant.id}><td>{displayName(participant.first_name, participant.last_name, participant.company_name, null)}</td><td>{participant.email || "-"}</td><td>{formatDate(participant.signup_date)}</td><td>{participant.inferred_plan_label || "BH - EURO - MAAND"}{participant.inferred_plan_needs_review && <span className="cell-subtext">Controle nodig</span>}</td>{canViewFinancials && <td>{formatCurrency(participant.inferred_first_year_revenue_incl_vat)}</td>}<td><Badge value={planConfidenceLabel(participant.inferred_plan_confidence)} /></td><td>{participant.matched_customer_id ? <Badge value="gekoppeld" /> : <Badge value="historisch" />}</td></tr>)}
                  {participants.length === 0 && <tr><td colSpan={canViewFinancials ? 7 : 6} className="empty">Geen historische deelnemers geïmporteerd.</td></tr>}
                </tbody></table></div>
              </div>
            </div>
          )}
        </section>
      )}
      {createOpen && <CreateLaunchModal onClose={() => setCreateOpen(false)} onSaved={async () => { setCreateOpen(false); await loadLaunches(); }} />}
      {editingLaunch && <CreateLaunchModal launch={editingLaunch} onClose={() => setEditingLaunch(null)} onSaved={async () => { setEditingLaunch(null); await loadLaunches(); }} />}
    </section>
  );
}

function CreateLaunchModal({ launch, onClose, onSaved }: { launch?: LaunchOverview | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const editing = Boolean(launch);
  const [name, setName] = useState(launch?.name || "");
  const [month, setMonth] = useState(launch?.launch_month?.slice(0, 7) || (editing ? "" : new Date().toISOString().slice(0, 7)));
  const [status, setStatus] = useState(launch?.status || "open");
  const [notes, setNotes] = useState(launch?.notes || "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!month && (!editing || Boolean(launch?.launch_month))) { setError("Kies een maand."); return; }
    setBusy(true); setError(null);
    const launchMonth = month ? `${month}-01` : null;
    const label = name.trim() || (launch ? launch.name : `Lancering ${formatLaunchMonth(launchMonth)}`);
    const result = editing
      ? await supabase!.from("launches").update({ name: label, launch_month: launchMonth, status, notes: notes.trim() || null }).eq("id", launch!.id).select("id,name").single()
      : await supabase!.from("launches").insert({ name: label, launch_month: launchMonth, status, notes: notes.trim() || null }).select("id,name").single();
    if (result.error || !result.data) { setError(result.error?.message || "Lancering kon niet worden opgeslagen."); setBusy(false); return; }
    await supabase!.from("audit_events").insert({
      action: editing ? "launch_updated" : "launch_created",
      entity_type: "launch",
      entity_id: result.data.id,
      success: true,
      summary: `${editing ? "Lancering gewijzigd" : "Lancering aangemaakt"}: ${result.data.name}`,
      after_data: { launch_month: launchMonth, status, notes: notes.trim() || null },
    });
    await onSaved();
  }

  return <div className="modal-backdrop"><form className="modal" onSubmit={save}><div className="modal-header"><h2>{editing ? "Lancering wijzigen" : "Nieuwe lancering"}</h2><button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button></div><div className="form-grid"><label className="span-2">Naam<input value={name} onChange={(event) => setName(event.target.value)} placeholder="Bijv. Lancering september 2026" /></label><label>Maand<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} required={!editing || Boolean(launch?.launch_month)} /></label><label>Status<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="draft">Concept</option><option value="open">Open</option><option value="closed">Gesloten</option><option value="archived">Gearchiveerd</option></select></label><p className="form-help span-2">Een open lancering met een maand verschijnt op het dashboard. Na sluiten blijven alle gegevens bij Lanceringen beschikbaar en worden latere betalingen nog verwerkt. Vastgelegde eventmomentopnamen veranderen niet.</p><label className="span-2">Notities<textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} /></label></div>{error && <div className="banner error">{error}</div>}<div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annuleren</button><button className="primary-button" disabled={busy}>{busy ? "Opslaan..." : editing ? "Wijziging opslaan" : "Opslaan"}</button></div></form></div>;
}

function formatLaunchMonth(value: string | null | undefined) {
  if (!value) return "-";
  const parsed = new Date(`${value.slice(0, 7)}-01T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
}

function totalLaunchParticipantCount(launch: LaunchOverview) {
  if (launch.known_participant_count !== null && launch.known_participant_count !== undefined) return launch.known_participant_count;
  if (launch.reporting_participant_count !== null && launch.reporting_participant_count !== undefined) return launch.reporting_participant_count;
  // Imported launch workbooks are the authoritative cohort size when present.
  // Automatically created current-run launches use the customer assignment count.
  return launch.historical_participant_count > 0 ? launch.historical_participant_count : launch.assigned_member_count;
}

function historicalFirstYearRevenueExclVat(launch: LaunchOverview) {
  return Number(launch.historical_first_year_revenue_incl_vat || 0) / 1.21;
}

function currentAnnualRevenueExclVat(launch: LaunchOverview) {
  return Number(launch.active_mrr_excl_vat || 0) * 12;
}

function launchDateAtNoon(value: string | null | undefined) {
  if (!value) return null;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function monthsBetweenDates(start: Date | null, end: Date) {
  if (!start) return null;
  return Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.4375));
}

function launchRowMetrics(launch: LaunchOverview, assignments: LaunchAssignmentIdentityRow[], now = new Date()) {
  const cohortSize = totalLaunchParticipantCount(launch);
  const activePercent = cohortSize > 0 ? (launch.active_paid_member_count / cohortSize) * 100 : null;
  const cohortAgeMonths = monthsBetweenDates(launchDateAtNoon(launch.launch_month), now);
  const latestByCustomer = new Map<string, LaunchAssignmentIdentityRow>();
  for (const assignment of assignments.filter((row) => row.launch_id === launch.id)) {
    const previous = latestByCustomer.get(assignment.customer_id);
    if (!previous || isPreferredLaunchAssignment(assignment, previous)) latestByCustomer.set(assignment.customer_id, assignment);
  }
  const durations = Array.from(latestByCustomer.values())
    .map((assignment) => {
      const start = launchDateAtNoon(assignment.start_date);
      const ended = ["ended", "cancelled", "stopped"].includes(assignment.subscription_status || "");
      // An ended run without a known access end cannot contribute a reliable
      // retention duration. Active runs are right-censored at today.
      const end = ended ? launchDateAtNoon(assignment.access_end_date) : now;
      if (!end) return null;
      return monthsBetweenDates(start, end);
    })
    .filter((value): value is number => value !== null);
  const retentionMonths = durations.length > 0 ? durations.reduce((total, value) => total + value, 0) / durations.length : null;
  return { activePercent, retentionMonths, cohortAgeMonths };
}

function launchAssignmentTimestamp(value: string | null | undefined) {
  if (!value) return Number.NEGATIVE_INFINITY;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}

function isPreferredLaunchAssignment(candidate: LaunchAssignmentIdentityRow, previous: LaunchAssignmentIdentityRow) {
  const activeStatuses = new Set(["active", "free", "barter"]);
  const candidatePriority = activeStatuses.has(candidate.subscription_status || "") ? 0 : 1;
  const previousPriority = activeStatuses.has(previous.subscription_status || "") ? 0 : 1;
  if (candidatePriority !== previousPriority) return candidatePriority < previousPriority;
  const candidateStart = launchAssignmentTimestamp(candidate.start_date);
  const previousStart = launchAssignmentTimestamp(previous.start_date);
  if (candidateStart !== previousStart) return candidateStart > previousStart;
  const candidateCreated = launchAssignmentTimestamp(candidate.created_at);
  const previousCreated = launchAssignmentTimestamp(previous.created_at);
  if (candidateCreated !== previousCreated) return candidateCreated > previousCreated;
  return (candidate.subscription_id || "") > (previous.subscription_id || "");
}

function launchSortValue(launch: LaunchOverview, sortKey: LaunchSortKey, assignments: LaunchAssignmentIdentityRow[]) {
  const metrics = launchRowMetrics(launch, assignments);
  if (sortKey === "name") return launch.name;
  if (sortKey === "launch_month") return launch.launch_month;
  if (sortKey === "status") return launch.status;
  if (sortKey === "participants") return totalLaunchParticipantCount(launch);
  if (sortKey === "active") return launch.active_paid_member_count;
  if (sortKey === "active_percent") return metrics.activePercent;
  if (sortKey === "retention_months") return metrics.retentionMonths;
  if (sortKey === "cohort_age_months") return metrics.cohortAgeMonths;
  if (sortKey === "first_year_revenue") return historicalFirstYearRevenueExclVat(launch);
  return currentAnnualRevenueExclVat(launch);
}

function compareLaunches(a: LaunchOverview, b: LaunchOverview, sort: LaunchSort, assignments: LaunchAssignmentIdentityRow[]) {
  const aValue = launchSortValue(a, sort.key, assignments);
  const bValue = launchSortValue(b, sort.key, assignments);
  const aEmpty = aValue === null || aValue === undefined || aValue === "";
  const bEmpty = bValue === null || bValue === undefined || bValue === "";
  if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
  if (aEmpty && bEmpty) return a.name.localeCompare(b.name, "nl", { sensitivity: "base" });
  const comparison = typeof aValue === "number" && typeof bValue === "number"
    ? aValue - bValue
    : String(aValue).localeCompare(String(bValue), "nl", { sensitivity: "base", numeric: true });
  if (comparison !== 0) return sort.direction === "asc" ? comparison : -comparison;
  return a.name.localeCompare(b.name, "nl", { sensitivity: "base" });
}

function uniqueLaunchParticipantCount(participants: LaunchParticipantIdentityRow[], assignments: LaunchAssignmentIdentityRow[]) {
  const emailToCustomer = new Map<string, string>();
  const identityKeys = new Set<string>();
  for (const assignment of assignments) {
    identityKeys.add(`customer:${assignment.customer_id}`);
    const email = (assignment.billing_email || "").trim().toLowerCase();
    if (email) emailToCustomer.set(email, assignment.customer_id);
  }
  for (const participant of participants) {
    const email = (participant.email_normalized || participant.email || "").trim().toLowerCase();
    if (participant.matched_customer_id) {
      identityKeys.add(`customer:${participant.matched_customer_id}`);
      if (email) emailToCustomer.set(email, participant.matched_customer_id);
    }
  }
  for (const participant of participants) {
    if (participant.matched_customer_id) continue;
    const email = (participant.email_normalized || participant.email || "").trim().toLowerCase();
    const knownCustomerId = email ? emailToCustomer.get(email) : null;
    if (knownCustomerId) {
      identityKeys.add(`customer:${knownCustomerId}`);
    } else if (email) {
      identityKeys.add(`email:${email}`);
    } else {
      identityKeys.add(`historical:${participant.id}`);
    }
  }
  return identityKeys.size;
}

function LaunchRevenueTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: { label: string; firstYearRevenue: number; currentAnnualRevenue: number } }> }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{row.label}</strong>
      <span>Eerste jaar: {formatCurrency(row.firstYearRevenue)}</span>
      <span>Actueel jaar: {formatCurrency(row.currentAnnualRevenue)}</span>
    </div>
  );
}

function planConfidenceLabel(value: string | null | undefined) {
  if (value === "high") return "Zeker";
  if (value === "medium") return "Waarschijnlijk";
  return "Aangenomen";
}

function AddCustomerModal({
  plans,
  launches,
  existingCustomers,
  existingViewers,
  historicalParticipants,
  onClose,
  onSaved,
  onOpenExisting,
  onReactivateExisting,
}: {
  plans: Plan[];
  launches: Launch[];
  existingCustomers: Customer[];
  existingViewers: Viewer[];
  historicalParticipants: LaunchParticipant[];
  onClose: () => void;
  onSaved: () => void;
  onOpenExisting: (customer: Customer) => void;
  onReactivateExisting: (customer: Customer) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [mailingEmail, setMailingEmail] = useState("");
  const [thinkificEmail, setThinkificEmail] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [taxType, setTaxType] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [isBusinessCustomer, setIsBusinessCustomer] = useState(false);
  const [invoiceCustomerId, setInvoiceCustomerId] = useState("");
  const [ninjaCustomerId, setNinjaCustomerId] = useState("");
  const [genderMarker, setGenderMarker] = useState("");
  const [excelAmount, setExcelAmount] = useState("");
  const [excelBillingPeriod, setExcelBillingPeriod] = useState("");
  const [planId, setPlanId] = useState(plans[0]?.id || "");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceDate, setInvoiceDate] = useState("");
  const [launchId, setLaunchId] = useState("");
  const [notes, setNotes] = useState("");
  const [stoppedDueToUnpaidInvoice, setStoppedDueToUnpaidInvoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowEmailOverlap, setAllowEmailOverlap] = useState(false);
  const [historicalMatchApplied, setHistoricalMatchApplied] = useState(false);
  const [busy, setBusy] = useState(false);
  const emailConflicts = useMemo(
    () => findEmailConflicts({ billingEmail: email, mailingEmail, thinkificEmail }, existingCustomers, existingViewers),
    [email, mailingEmail, thinkificEmail, existingCustomers, existingViewers],
  );
  const outsideLaunch = launches.find((launch) => launch.name === OUTSIDE_LAUNCH_NAME);
  const launchChoices = useMemo(() => {
    const open = launches.filter((launch) => launch.status === "open");
    return [...open].sort((a, b) => Number(b.name === OUTSIDE_LAUNCH_NAME) - Number(a.name === OUTSIDE_LAUNCH_NAME) || String(b.launch_month || "").localeCompare(String(a.launch_month || "")));
  }, [launches]);
  const historicalMatches = useMemo(() => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return [];
    return historicalParticipants.filter((participant) => normalizeEmail(participant.email) === normalizedEmail);
  }, [email, historicalParticipants]);
  const historicalRepresentative = historicalMatches[0] || null;
  const isHistoricalReactivation = historicalMatchApplied && historicalMatches.length > 0;

  useEffect(() => {
    if (!planId && plans[0]?.id) setPlanId(plans[0].id);
  }, [planId, plans]);

  useEffect(() => {
    if (launchId || !outsideLaunch?.id) return;
    setLaunchId(outsideLaunch.id);
  }, [launchId, outsideLaunch?.id]);

  useEffect(() => {
    setAllowEmailOverlap(false);
    setHistoricalMatchApplied(false);
  }, [email, mailingEmail, thinkificEmail]);

  function applyHistoricalMatch() {
    const representative = historicalRepresentative;
    if (!representative) return;
    setFirstName((current) => current || representative.first_name || "");
    setLastName((current) => current || representative.last_name || "");
    setCompanyName((current) => current || representative.company_name || "");
    const inferredPlan = plans.find((plan) => plan.code === representative.inferred_plan_code)
      || plans.find((plan) => normalizeEmail(plan.display_label) === normalizeEmail(representative.inferred_plan_label));
    if (inferredPlan) setPlanId(inferredPlan.id);
    setHistoricalMatchApplied(true);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const plan = plans.find((item) => item.id === planId);
    if (!plan) {
      setError("Kies een abonnement.");
      return;
    }
    setBusy(true);
    setError(null);
    const invalidEmail = invalidEmailField([
      { label: "Factuur e-mail", value: email, required: true },
      { label: "Mailing e-mail", value: mailingEmail },
      { label: "Thinkific e-mail", value: thinkificEmail },
    ]);
    if (invalidEmail) {
      setError(`${invalidEmail} is geen geldig e-mailadres. Gebruik één adres per veld.`);
      setBusy(false);
      return;
    }
    if (emailConflicts.billingConflict) {
      await supabase!.from("audit_events").insert({
        action: "customer_create_blocked_duplicate_email",
        entity_type: "customer",
        entity_id: emailConflicts.billingConflict.customer.id,
        customer_id: emailConflicts.billingConflict.customer.id,
        success: false,
        summary: `Klant toevoegen geblokkeerd: ${email.trim()} bestaat al bij ${customerLabel(emailConflicts.billingConflict.customer)}`,
        after_data: { attempted_email: email.trim(), existing_customer_id: emailConflicts.billingConflict.customer.id },
      });
      setError("Er bestaat al een klant met dit factuur-e-mailadres. Open of heractiveer de bestaande klant.");
      setBusy(false);
      return;
    }
    if (emailConflicts.activeViewerConflict) {
      await supabase!.from("audit_events").insert({
        action: "customer_create_blocked_viewer_email",
        entity_type: "viewer",
        entity_id: emailConflicts.activeViewerConflict.viewer.id,
        customer_id: emailConflicts.activeViewerConflict.viewer.customer_id,
        success: false,
        summary: `Klant toevoegen geblokkeerd: ${email.trim()} is actieve meekijker bij ${emailConflicts.activeViewerConflict.viewer.customers?.display_name || emailConflicts.activeViewerConflict.viewer.customers?.billing_email || "onbekende hoofdklant"}`,
        after_data: { attempted_email: email.trim(), viewer_id: emailConflicts.activeViewerConflict.viewer.id },
      });
      setError("Dit factuur-e-mailadres bestaat al als actieve meekijker. Verwijder of beoordeel eerst die meekijker voordat je hiervan een hoofdklant maakt.");
      setBusy(false);
      return;
    }
    if ((emailConflicts.warnings.length > 0 || emailConflicts.viewerWarnings.length > 0) && !allowEmailOverlap) {
      setError("Er is e-mailoverlap met een bestaande klant. Controleer de melding hieronder en bevestig bewust doorgaan als dit klopt.");
      setBusy(false);
      return;
    }
    if (historicalMatches.length > 0 && !historicalMatchApplied) {
      setError("Er is een historische aanmelding met dit e-mailadres gevonden. Kies eerst 'Historisch profiel gebruiken' zodat er geen los nieuw profiel ontstaat.");
      setBusy(false);
      return;
    }
    const statusValue = plan.payment_variant === "free" ? "free" : plan.payment_variant === "barter" ? "barter" : "active";
    const defaultVatRate = Number(plan.vat_rate_default ?? 0.21);
    const defaultVatRegime = defaultVatRate === 0 ? "exempt" : defaultVatRate === 0.21 ? "standard_21" : "custom";
    const vat = calculateVat(Number(plan.price_incl_vat || 0), defaultVatRegime, defaultVatRate);
    const { data: customer, error: customerError } = await supabase!.from("customers").insert({
      first_name: firstName || null,
      last_name: lastName || null,
      company_name: companyName || null,
      display_name: companyName || [firstName, lastName].filter(Boolean).join(" ") || email.trim(),
      billing_email: email.trim(),
      mailing_email_override: mailingEmail.trim() || null,
      thinkific_email_override: thinkificEmail.trim() || null,
      address: address || null,
      postcode: postcode || null,
      city: city || null,
      country: country || null,
      tax_type: taxType || null,
      tax_number: taxNumber || null,
      is_business_customer: Boolean(isBusinessCustomer || companyName || taxNumber),
      status: statusValue,
      stopped_due_to_unpaid_invoice: stoppedDueToUnpaidInvoice,
      notes: notes || null,
      metadata: cleanMetadata({
        invoice_customer_id: invoiceCustomerId || null,
        ninja_customer_id: ninjaCustomerId || null,
        gender_marker: genderMarker || null,
      }),
    }).select().single();
    if (customerError || !customer) {
      setError(customerError?.message || "Klant kon niet worden opgeslagen.");
      setBusy(false);
      return;
    }
    const { data: subscription, error: subscriptionError } = await supabase!.from("customer_subscriptions").insert({
      customer_id: customer.id,
      plan_id: plan.id,
      status: statusValue,
      start_date: startDate,
      invoice_date: invoiceDate || null,
      counts_toward_revenue: plan.counts_toward_revenue,
      vat_regime: defaultVatRegime,
      vat_rate_applied: vat.rate,
      price_incl_vat_applied: vat.priceIncl,
      price_excl_vat_applied: vat.priceExcl,
      vat_amount_applied: vat.vatAmount,
      notes: notes || null,
      metadata: cleanMetadata({
        gender_marker: genderMarker || null,
        invoice_customer_id: invoiceCustomerId || null,
        ninja_customer_id: ninjaCustomerId || null,
        excel_amount: excelAmount || null,
        excel_billing_period: excelBillingPeriod || null,
      }),
    }).select("id").single();
    if (subscriptionError || !subscription) {
      setError(subscriptionError.message);
      setBusy(false);
      return;
    }
    if (isHistoricalReactivation) {
      const historicalParticipantIds = historicalMatches.map((participant) => participant.id);
      const { error: historicalMatchError } = await supabase!.from("launch_participants").update({
        matched_customer_id: customer.id,
        match_method: "email",
        match_confidence: "high",
      }).in("id", historicalParticipantIds);
      if (historicalMatchError) {
        setError(`Klant is opgeslagen, maar de historische aanmelding kon niet aan dit profiel worden gekoppeld: ${historicalMatchError.message}`);
        setBusy(false);
        return;
      }
    }
    const selectedLaunchId = launchId || outsideLaunch?.id || null;
    if (selectedLaunchId) {
      const { error: launchError } = await supabase!.from("customer_launch_assignments").insert({
        launch_id: selectedLaunchId,
        customer_id: customer.id,
        subscription_id: subscription.id,
        assignment_method: isHistoricalReactivation ? "historical_match" : "manual",
        confidence: "high",
        needs_review: false,
        notes: isHistoricalReactivation ? "Gekoppeld bij heractivatie vanuit historische aanmelding." : "Gekoppeld bij het toevoegen van de klant.",
      });
      if (launchError) {
        setError(`Klant is opgeslagen, maar de lancering kon niet worden gekoppeld: ${launchError.message}`);
        setBusy(false);
        return;
      }
    }
    try {
      const scheduledFor = integrationScheduleForDate(startDate);
      const syncJobs = await scheduleAccessSyncJobs(customer.id, subscription.id, scheduledFor);
      if (shouldProcessIntegrationDateNow(startDate)) {
        await processIntegrationJobsNow(customer.id, syncJobs.ids);
      }
    } catch (syncError) {
      setError(`Klant is opgeslagen, maar toegang kon niet direct worden gesynchroniseerd: ${syncError instanceof Error ? syncError.message : String(syncError)}`);
      setBusy(false);
      return;
    }
    await supabase!.from("audit_events").insert({
      action: isHistoricalReactivation ? "customer_reactivated" : "customer_created",
      entity_type: "customer",
      entity_id: customer.id,
      customer_id: customer.id,
      success: true,
      summary: isHistoricalReactivation ? `Historische klant geheractiveerd: ${email}` : `Klant toegevoegd: ${email}`,
      after_data: {
        historical_reactivation: isHistoricalReactivation,
        historical_participant_ids: isHistoricalReactivation ? historicalMatches.map((participant) => participant.id) : [],
        subscription_id: subscription.id,
        plan_id: plan.id,
        status: statusValue,
        start_date: startDate,
      },
    });
    onSaved();
  }

  return (
    <div className="modal-backdrop">
      <form className="modal wide-modal" onSubmit={save}>
        <div className="modal-header">
          <h2>Klant toevoegen</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="form-grid">
          <section className="form-section span-2">
            <div className="form-section-divider"><span>Persoonsgegevens</span></div>
            <div className="form-section-fields">
              <label>Voornaam<input value={firstName} onChange={(event) => setFirstName(event.target.value)} /></label>
              <label>Achternaam<input value={lastName} onChange={(event) => setLastName(event.target.value)} /></label>
              <label className="span-2">Bedrijfsnaam<input value={companyName} onChange={(event) => setCompanyName(event.target.value)} /></label>
              <label>Geslacht<select value={genderMarker} onChange={(event) => setGenderMarker(event.target.value)}><option value="">Onbekend</option><option value="M">Man</option><option value="V">Vrouw</option></select></label>
              <label className="checkbox-row"><input checked={isBusinessCustomer} onChange={(event) => setIsBusinessCustomer(event.target.checked)} type="checkbox" /> Zakelijke klant</label>
            </div>
          </section>

          <section className="form-section span-2">
            <div className="form-section-divider"><span>Contactgegevens</span></div>
            <div className="form-section-fields">
              <label className="span-2">Factuur e-mail<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>
              <div className="span-2">
                <EmailConflictPanel
                  conflicts={emailConflicts}
                  allowEmailOverlap={allowEmailOverlap}
                  onAllowEmailOverlap={setAllowEmailOverlap}
                  onOpenExisting={onOpenExisting}
                  onReactivateExisting={onReactivateExisting}
                />
              </div>
              {historicalMatches.length > 0 && !emailConflicts.billingConflict && (
                <div className="email-conflict-panel historical-match-panel span-2">
                  <div className="conflict-title">Historische klant gevonden</div>
                  <p className="conflict-note">Dit e-mailadres staat in {historicalMatches.length === 1 ? "een historische aanmelding" : `${historicalMatches.length} historische aanmeldingen`} zonder huidig klantprofiel. Gebruik de historische gegevens om deze persoon als heractivatie toe te voegen. De nieuwe abonnementsrun wordt niet als nieuw lid geteld.</p>
                  <div className="conflict-grid">
                    <span>Naam</span><strong>{displayName(historicalRepresentative?.first_name, historicalRepresentative?.last_name, historicalRepresentative?.company_name, null) || "-"}</strong>
                    <span>Historisch gestart</span><strong>{formatDate(historicalMatches.map((participant) => participant.signup_date).filter(Boolean).sort()[0] || null)}</strong>
                    <span>Lancering</span><strong>{[...new Set(historicalMatches.map((participant) => participant.launch_name).filter(Boolean))].join(", ") || "-"}</strong>
                    <span>Historisch abonnement</span><strong>{[...new Set(historicalMatches.map((participant) => participant.inferred_plan_label || participant.original_plan_label).filter(Boolean))].join(", ") || "-"}</strong>
                  </div>
                  <div className="conflict-actions">
                    {historicalMatchApplied
                      ? <Badge value="historische gegevens overgenomen" tone="success" />
                      : <button type="button" className="primary-button compact" onClick={applyHistoricalMatch}>Historisch profiel gebruiken</button>}
                  </div>
                </div>
              )}
              <label>Mailing e-mail afwijking<input value={mailingEmail} onChange={(event) => setMailingEmail(event.target.value)} type="email" placeholder="Leeg = factuur e-mail" /></label>
              <label>Thinkific e-mail afwijking<input value={thinkificEmail} onChange={(event) => setThinkificEmail(event.target.value)} type="email" placeholder="Leeg = factuur e-mail" /></label>
            </div>
          </section>

          <section className="form-section span-2">
            <div className="form-section-divider"><span>Adresgegevens</span></div>
            <div className="form-section-fields">
              <label className="span-2">Adres<input value={address} onChange={(event) => setAddress(event.target.value)} /></label>
              <label>Postcode<input value={postcode} onChange={(event) => setPostcode(event.target.value)} /></label>
              <label>Stad<input value={city} onChange={(event) => setCity(event.target.value)} /></label>
              <label>Land<input value={country} onChange={(event) => setCountry(event.target.value)} /></label>
            </div>
          </section>

          <section className="form-section span-2">
            <div className="form-section-divider"><span>Administratie &amp; belasting</span></div>
            <div className="form-section-fields">
              <label>Factuur klantnummer<input value={invoiceCustomerId} onChange={(event) => setInvoiceCustomerId(event.target.value)} /></label>
              <label>Ninja klantnummer<input value={ninjaCustomerId} onChange={(event) => setNinjaCustomerId(event.target.value)} /></label>
              <label>Tax type<input value={taxType} onChange={(event) => setTaxType(event.target.value)} /></label>
              <label>Btw-/taxnummer<input value={taxNumber} onChange={(event) => setTaxNumber(event.target.value)} /></label>
              <label>Excel bedrag<input value={excelAmount} onChange={(event) => setExcelAmount(event.target.value)} /></label>
              <label>Excel betaaltermijn<input value={excelBillingPeriod} onChange={(event) => setExcelBillingPeriod(event.target.value)} /></label>
            </div>
          </section>

          <section className="form-section span-2">
            <div className="form-section-divider"><span>Abonnement</span></div>
            <div className="form-section-fields">
              <label className="span-2">Abonnement<select value={planId} onChange={(event) => setPlanId(event.target.value)} required>{plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.display_label}</option>)}</select></label>
              <label className="span-2">Startdatum<input value={startDate} onChange={(event) => setStartDate(event.target.value)} type="date" required /></label>
              <label className="span-2">Lancering<select value={launchId} onChange={(event) => setLaunchId(event.target.value)}><option value={outsideLaunch?.id || ""}>{OUTSIDE_LAUNCH_NAME}</option>{launchChoices.filter((launch) => launch.id !== outsideLaunch?.id).map((launch) => <option key={launch.id} value={launch.id}>{launch.name} ({formatLaunchMonth(launch.launch_month)})</option>)}</select></label>
              <label className="span-2">Factuurdatum<input value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} type="date" /></label>
            </div>
          </section>

          <section className="form-section span-2">
            <div className="form-section-divider"><span>Betaalstatus &amp; toegang</span></div>
            <div className="form-section-fields">
              <label className="checkbox-row span-2"><input checked={stoppedDueToUnpaidInvoice} onChange={(event) => setStoppedDueToUnpaidInvoice(event.target.checked)} type="checkbox" /> {STOPPED_DUE_TO_UNPAID_INVOICE_LABEL}</label>
            </div>
          </section>

          <section className="form-section span-2">
            <div className="form-section-divider"><span>Notities</span></div>
            <div className="form-section-fields">
              <label className="span-2">Notities<textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} /></label>
            </div>
          </section>
        </div>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuleren</button>
          <button className="primary-button" disabled={busy}>{busy ? "Opslaan..." : "Opslaan"}</button>
        </div>
      </form>
    </div>
  );
}

function CopyableDetail({ value, label, onCopied, showValue = true }: { value: string | null | undefined; label: string; onCopied?: (message: string) => void; showValue?: boolean }) {
  const normalizedValue = String(value || "").trim();

  async function copyValue() {
    if (!normalizedValue) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalizedValue);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = normalizedValue;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }
      onCopied?.(`${label} gekopieerd.`);
    } catch {
      onCopied?.(`${label} kon niet worden gekopieerd.`);
    }
  }

  return (
    <span className="copyable-detail">
      {showValue && <span>{normalizedValue || "-"}</span>}
      {normalizedValue && (
        <button
          type="button"
          className="copy-icon-button"
          onClick={copyValue}
          title={`${label} kopiëren`}
          aria-label={`${label} kopiëren`}
        >
          <Copy size={13} />
        </button>
      )}
    </span>
  );
}

function DetailSubgroupTitle({
  children,
  canEdit,
  onEdit,
}: {
  children: React.ReactNode;
  canEdit?: boolean;
  onEdit?: () => void;
}) {
  return (
    <div className="detail-subgroup-title-row">
      <div className="detail-subgroup-title">{children}</div>
      {canEdit && onEdit && (
        <button type="button" className="detail-edit-button" onClick={onEdit} title={`${String(children)} wijzigen`} aria-label={`${String(children)} wijzigen`}>
          <Edit3 size={13} />
        </button>
      )}
    </div>
  );
}

function CustomerDetailModal({
  customer,
  customers,
  existingViewers,
  plans,
  launches,
  canViewFinancials,
  canMutateMembers,
  onClose,
  onChanged,
}: {
  customer: Customer;
  customers: Customer[];
  existingViewers: Viewer[];
  plans: Plan[];
  launches: Launch[];
  canViewFinancials: boolean;
  canMutateMembers: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [profileCustomer, setProfileCustomer] = useState(customer);
  const subscription = currentSubscription(profileCustomer);
  const originalStartDate = originalSubscriptionStartDate(profileCustomer);
  const hasEarlierStartDate = Boolean(originalStartDate && subscription?.start_date && originalStartDate !== subscription.start_date);
  const fullName = [profileCustomer.first_name, profileCustomer.last_name].filter(Boolean).join(" ") || displayName(profileCustomer.first_name, profileCustomer.last_name, profileCustomer.company_name, profileCustomer.display_name);
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [adminSubscriptionJobs, setAdminSubscriptionJobs] = useState<any[]>([]);
  const [billingRows, setBillingRows] = useState<BillingSubscription[]>([]);
  const [billingSwitchRows, setBillingSwitchRows] = useState<BillingSwitch[]>([]);
  const [invoiceRows, setInvoiceRows] = useState<BillingInvoice[]>([]);
  const [customerLaunchSignups, setCustomerLaunchSignups] = useState<LaunchSignup[]>([]);
  const [addViewerOpen, setAddViewerOpen] = useState(false);
  const [billingSwitchOpen, setBillingSwitchOpen] = useState(false);
  const [subscriptionImportOpen, setSubscriptionImportOpen] = useState(false);
  const [invoiceImportOpen, setInvoiceImportOpen] = useState(false);
  const [subscriptionActionsOpen, setSubscriptionActionsOpen] = useState(false);
  const [managedEntry, setManagedEntry] = useState<SubscriptionEntry | null>(null);
  const [planningKind, setPlanningKind] = useState<"end" | "switch" | null>(null);
  const [managedInvoice, setManagedInvoice] = useState<{entry: SubscriptionEntry; correction: boolean} | null>(null);
  const [linkSignup, setLinkSignup] = useState<LaunchSignup | null>(null);
  const [reviewSignup, setReviewSignup] = useState<LaunchSignup | null>(null);
  const [cancellationOpen, setCancellationOpen] = useState(false);
  const [cancellingLaunchSignup, setCancellingLaunchSignup] = useState<LaunchSignup | null>(null);
  const [editingSection, setEditingSection] = useState<CustomerProfileSection | null>(null);
  const [reactivating, setReactivating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const currentBilling = currentBillingForCustomer(billingRows, profileCustomer.id, subscription?.id);

  useEffect(() => {
    setProfileCustomer(customer);
    loadDetail();
  }, [customer.id]);

  async function loadDetail() {
    const [viewerResult, jobResult, logResult, eventResult, billingResult, billingSwitchResult, invoiceResult, launchSignupResult, adminJobsResult] = await Promise.all([
      supabase!.from("viewers").select("*").eq("customer_id", customer.id).order("added_at", { ascending: false }),
      supabase!.from("integration_jobs").select("*").eq("customer_id", customer.id).order("scheduled_for", { ascending: false }).limit(25),
      supabase!.from("integration_logs").select("*").eq("customer_id", customer.id).order("created_at", { ascending: false }).limit(25),
      supabase!.from("audit_events").select("*").eq("customer_id", customer.id).order("created_at", { ascending: false }).limit(25),
      supabase!.from("billing_subscriptions").select("*, plans(*)").eq("customer_id", customer.id).order("created_at", { ascending: false }),
      supabase!.from("billing_switches").select("*, to_plan:plans!billing_switches_to_plan_id_fkey(*)").eq("customer_id", customer.id).order("created_at", { ascending: false }),
      supabase!.from("billing_invoices").select("*").eq("customer_id", customer.id).order("invoice_date", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false }),
      supabase!.from("admin_subscription_signups").select("*").eq("customer_id", customer.id).order("submitted_at", { ascending: false }),
      supabase!.from("billing_jobs").select("id,billing_subscription_id,status,last_error,payload").eq("customer_id",customer.id).eq("job_type","admin_subscription_action").in("status",["planned","running","retry_needed","failed"]),
    ]);
    if (adminJobsResult.data) setAdminSubscriptionJobs(adminJobsResult.data);
    const firstError=[viewerResult,jobResult,logResult,eventResult,billingResult,billingSwitchResult,invoiceResult,launchSignupResult,adminJobsResult].find(r=>r.error)?.error;
    if(firstError)setError(firstError.message);
    if (viewerResult.data) setViewers(viewerResult.data as Viewer[]);
    if (jobResult.data) setJobs(jobResult.data as IntegrationJob[]);
    if (logResult.data) setLogs(logResult.data as IntegrationLog[]);
    if (eventResult.data) setEvents(eventResult.data as AuditEvent[]);
    if (billingResult.data) setBillingRows(billingResult.data as BillingSubscription[]);
    if (billingSwitchResult.data) setBillingSwitchRows(billingSwitchResult.data as unknown as BillingSwitch[]);
    if (invoiceResult.data) setInvoiceRows(invoiceResult.data as BillingInvoice[]);
    if (launchSignupResult.data) setCustomerLaunchSignups(launchSignupResult.data as LaunchSignup[]);
  }

  async function refreshProfile() {
    const { data, error: profileError } = await supabase!
      .from("customers")
      .select("*, customer_subscriptions(*, plans(*))")
      .eq("id", customer.id)
      .single();
    if (profileError) throw profileError;
    setProfileCustomer(data as Customer);
    await loadDetail();
    onChanged();
  }

  async function removeViewer(viewer: Viewer) {
    setMessage(null);
    setError(null);
    const { error: viewerError } = await supabase!.from("viewers").update({ status: "removed", removed_at: new Date().toISOString() }).eq("id", viewer.id);
    if (viewerError) {
      setError(viewerError.message);
      return;
    }
    await cancelPendingViewerSyncJobs(viewer.id);
    const { data: job, error: jobError } = await supabase!.from("integration_jobs").insert({
      customer_id: customer.id,
      viewer_id: viewer.id,
      subscription_id: subscription?.id || null,
      provider: "activecampaign",
      job_type: "remove_viewer_access",
      scheduled_for: new Date().toISOString(),
      status: "planned",
    }).select("id").single();
    if (jobError || !job) {
      setError(jobError?.message || "Uitschrijftaak kon niet worden aangemaakt.");
      return;
    }
    await supabase!.from("audit_events").insert({
      action: "viewer_removed",
      entity_type: "viewer",
      entity_id: viewer.id,
      customer_id: customer.id,
      success: true,
      summary: `Meekijker verwijderd: ${viewer.email}`,
      after_data: {
        viewer_email: viewer.email,
        customer_name: customer.display_name,
        customer_email: customer.billing_email,
        job_id: job.id,
      },
    });
    try {
      await processIntegrationJobNow(customer.id, job.id, { accessEndOnly: true });
      setMessage("Meekijker verwijderd en direct uitgeschreven.");
    } catch (processError) {
      setError(`Meekijker is verwijderd, maar direct uitschrijven mislukte: ${processError instanceof Error ? processError.message : String(processError)}`);
    }
    await loadDetail();
    onChanged();
  }

  async function retryJob(job: IntegrationJob) {
    setMessage(null);
    setError(null);
    const { error } = await supabase!.from("integration_jobs").update({
      status: "retry_needed",
      scheduled_for: new Date().toISOString(),
      last_error: null,
    }).eq("id", job.id);
    if (error) {
      setError(error.message);
      return;
    }
    setMessage("Taak opnieuw ingepland.");
    await loadDetail();
  }

  async function syncCustomerNow() {
    setSyncBusy(true);
    setMessage(null);
    setError(null);
    try {
      const session = await supabase!.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Geen actieve sessie gevonden.");
      const response = await fetch(`${publicSupabaseUrl}/functions/v1/customer-sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ customer_id: customer.id }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Sync mislukt.");
      setMessage("Klant opnieuw gesynchroniseerd met ActiveCampaign en Thinkific.");
      await loadDetail();
    } catch (syncError) {
      setError(syncError instanceof Error ? syncError.message : String(syncError));
    } finally {
      setSyncBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal detail-modal">
        <div className="modal-header">
          <div className="detail-modal-heading">
            <h2>{displayName(profileCustomer.first_name, profileCustomer.last_name, profileCustomer.company_name, profileCustomer.display_name)}</h2>
            <CopyableDetail value={fullName} label="Volledige naam" onCopied={setMessage} showValue={false} />
          </div>
          <div className="row-actions">
            {canMutateMembers && (
              <button type="button" className="secondary-button compact" onClick={syncCustomerNow} disabled={syncBusy}>
                <RefreshCw className={syncBusy ? "spin" : ""} size={14} />
                Sync opnieuw
              </button>
            )}
            <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
          </div>
        </div>
        {message && <div className="banner success">{message}</div>}
        {error && <div className="banner error">{error}</div>}

        <div className="detail-grid profile-summary-grid">
          <section className="panel">
            <div className="panel-title">Klant</div>
            <div className="detail-subgroup first">
              <DetailSubgroupTitle canEdit={canMutateMembers} onEdit={() => setEditingSection("contact")}>Klantgegevens</DetailSubgroupTitle>
              <dl className="detail-list">
                <dt>Naam</dt><dd><CopyableDetail value={fullName} label="Volledige naam" onCopied={setMessage}/></dd>
                <dt>E-mailadres</dt><dd><CopyableDetail value={profileCustomer.billing_email} label="E-mailadres" onCopied={setMessage}/></dd>
                <dt>Mailing e-mail</dt><dd><CopyableDetail value={effectiveMailingEmail(profileCustomer)} label="Mailing e-mail" onCopied={setMessage}/><div className="cell-subtext">{effectiveMailingEmail(profileCustomer).trim().toLowerCase() !== profileCustomer.billing_email.trim().toLowerCase() ? "Afwijkend e-mailadres" : "Gelijk aan e-mailadres"}</div></dd>
                <dt>Thinkific e-mail</dt><dd><CopyableDetail value={effectiveThinkificEmail(profileCustomer)} label="Thinkific e-mail" onCopied={setMessage}/><div className="cell-subtext">{effectiveThinkificEmail(profileCustomer).trim().toLowerCase() !== profileCustomer.billing_email.trim().toLowerCase() ? "Afwijkend e-mailadres" : "Gelijk aan e-mailadres"}</div></dd>
                <dt>Bedrijf</dt><dd>{profileCustomer.company_name || "Privé"}</dd>
              </dl>
            </div>
            <div className="detail-subgroup">
              <DetailSubgroupTitle canEdit={canMutateMembers} onEdit={() => setEditingSection("address")}>Adresgegevens</DetailSubgroupTitle>
              <p>{formatAddress(profileCustomer) || "Geen adres ingevuld"}</p>
            </div>
          </section>
          <section className="panel">
            <div className="panel-title">Betaling en toegang</div>
            <dl className="detail-list">
              <dt>Nieuwe aanmelding</dt><dd>{customerLaunchSignups.find(s => !["active","cancelled","duplicate"].includes(s.status)) ? launchSignupStatusLabel(customerLaunchSignups.find(s => !["active","cancelled","duplicate"].includes(s.status))!.status) : "—"}</dd>
              <dt>Lidmaatschap</dt><dd>{isAccessStatusActive(profileCustomer.status) ? "Actief" : "Geen actief lidmaatschap"}</dd>
              <dt>Betaalachterstand</dt><dd>{profileCustomer.payment_arrears ? "Ja" : "Nee"}</dd>
              <dt>Toegang tot</dt><dd>{isAccessStatusActive(profileCustomer.status) ? (subscription?.access_end_date ? formatDate(subscription.access_end_date) : "Geen einddatum gepland") : "Zie de aanmelding of historie hieronder"}</dd>
            </dl>
            {canMutateMembers && <div className="row-actions">{canMutateMembers&&<button className="secondary-button compact" onClick={()=>document.getElementById('profile-arrears')?.scrollIntoView({block:'start'})}>Betaalachterstand bekijken</button>}<button className="secondary-button compact" onClick={() => setEditingSection("payment")}>Handmatige betaalstatus</button></div>}
            <div className="detail-subgroup"><DetailSubgroupTitle canEdit={canMutateMembers} onEdit={() => setEditingSection("notes")}>Notities</DetailSubgroupTitle><div className="detail-notes profile-compact-notes">{profileCustomer.notes || "Geen notities"}</div></div>
          </section>
        </div>
        <SubscriptionCenter adminJobs={adminSubscriptionJobs} customer={profileCustomer} plans={plans} billings={billingRows} switches={billingSwitchRows} signups={customerLaunchSignups} invoices={invoiceRows} canManage={canMutateMembers} canFinance={canMutateMembers || canViewFinancials} onChanged={refreshProfile} onLink={setLinkSignup}
          onAction={(entry, action) => {
            setManagedEntry(entry);
            if (action === "end") setPlanningKind("end");
            else if (action === "planning") setPlanningKind("switch");
            else if (action === "cancel_signup" && entry.signup) setCancellingLaunchSignup(entry.signup);
            else if (action === "review" && entry.signup) setReviewSignup(entry.signup);
            else if (action === "import") setSubscriptionImportOpen(true);
            else if (action === "membership") setEditingSection("membership");
            else if (action === "invoice_edit" || action === "invoice_correct") setManagedInvoice({entry, correction: action === "invoice_correct"});
          }}/>
        <div id="profile-arrears"><ArrearsOverview customerId={profileCustomer.id} canManage={canMutateMembers} canFinance={canMutateMembers || canViewFinancials}/></div>
        {linkSignup && <LaunchExistingBillingModal signup={linkSignup} onClose={() => setLinkSignup(null)} onSaved={async () => {setLinkSignup(null);await refreshProfile();}}/>}
        {reviewSignup && <LaunchBillingReviewModal signup={reviewSignup} onClose={() => setReviewSignup(null)} onSaved={async () => {setReviewSignup(null);await refreshProfile();}}/>}
        {managedEntry && planningKind && <SubscriptionPlanningModal customer={profileCustomer} entry={managedEntry} plans={plans} kind={planningKind} onClose={() => setPlanningKind(null)} onSaved={async () => {setPlanningKind(null);await refreshProfile();}}/>}
        {managedInvoice && <SubscriptionInvoiceModal customer={profileCustomer} entry={managedInvoice.entry} correction={managedInvoice.correction} onClose={() => setManagedInvoice(null)} onSaved={async () => {setManagedInvoice(null);await refreshProfile();}}/>}

        <section className="panel table-panel customer-detail-viewers-panel">
          <div className="panel-title-row">
            <div className="panel-title">Meekijkers</div>
            {canMutateMembers && <button className="secondary-button compact" onClick={() => setAddViewerOpen(true)}><Eye size={14} /> Toevoegen</button>}
          </div>
          <table className="data-table">
            <thead><tr><th>E-mail</th><th>Status</th><th>Toegevoegd</th><th>Verwijderd</th><th>Acties</th></tr></thead>
            <tbody>
              {viewers.map((viewer) => (
                <tr key={viewer.id}>
                  <td>{viewer.email}</td>
                  <td><Badge value={viewer.status} /></td>
                  <td>{formatDateTimeSeconds(viewer.added_at)}</td>
                  <td>{formatDateTimeSeconds(viewer.removed_at)}</td>
                  <td>{canMutateMembers && viewer.status === "active" ? <button className="secondary-button compact" onClick={() => removeViewer(viewer)}>Verwijder</button> : "-"}</td>
                </tr>
              ))}
              {viewers.length === 0 && <tr><td colSpan={5} className="empty">Geen meekijkers gekoppeld.</td></tr>}
            </tbody>
          </table>
        </section>

        <DetailJobsPanel jobs={jobs} canRetry={canMutateMembers} onRetry={retryJob} />
        <DetailLogsPanel logs={logs} events={events} />

        {addViewerOpen && (
          <AddViewerModal
            customers={[profileCustomer]}
            onClose={() => setAddViewerOpen(false)}
            onSaved={async () => {
              setAddViewerOpen(false);
              setMessage("Meekijker toegevoegd en direct gesynchroniseerd.");
              await loadDetail();
              onChanged();
            }}
          />
        )}
        {subscriptionImportOpen && subscription && (
          <CustomerSubscriptionImportModal
            customer={profileCustomer}
            subscription={subscription}
            onClose={() => setSubscriptionImportOpen(false)}
            onSaved={async () => {
              setSubscriptionImportOpen(false);
              setMessage("Abonnement opgehaald. De bestaande opzegging en einddatum zijn niet gewijzigd.");
              await refreshProfile();
            }}
          />
        )}
        {invoiceImportOpen && subscription && (
          <CustomerInvoiceImportModal
            customer={profileCustomer}
            onClose={() => setInvoiceImportOpen(false)}
            onSaved={async (count) => {
              setInvoiceImportOpen(false);
              setMessage(`${count} factu${count === 1 ? "ur" : "ren"} opgehaald. Het abonnement en de opzegging zijn niet gewijzigd.`);
              await refreshProfile();
            }}
          />
        )}
        {billingSwitchOpen && currentBilling && subscription && (
          <BillingSwitchModal
            customer={profileCustomer}
            currentBilling={currentBilling}
            currentSubscription={subscription}
            plans={plans}
            onClose={() => setBillingSwitchOpen(false)}
            onSaved={async () => {
              setBillingSwitchOpen(false);
              setMessage("Factureringswissel gepland voor de volgende factuurperiode; er wordt niet geprorateerd.");
              await refreshProfile();
            }}
          />
        )}
        {subscriptionActionsOpen && subscription && (
          <SubscriptionActionsModal
            hasOpenSwitch={Boolean(billingSwitchRows.find((item) => ["scheduled", "processing", "failed"].includes(item.status)))}
            hasScheduledCancellation={isScheduledCancellation(subscription)}
            onClose={() => setSubscriptionActionsOpen(false)}
            onSwitch={() => {
              setSubscriptionActionsOpen(false);
              setBillingSwitchOpen(true);
            }}
            onCancel={() => {
              setSubscriptionActionsOpen(false);
              setCancellationOpen(true);
            }}
          />
        )}
        {cancellationOpen && subscription && (
          <CancelCustomerModal
            customer={profileCustomer}
            nested
            onClose={() => setCancellationOpen(false)}
            onSaved={async () => {
              setCancellationOpen(false);
              setMessage("Opzegging en factuurstop zijn ingepland voor de gekozen einddatum.");
              await refreshProfile();
            }}
          />
        )}
        {cancellingLaunchSignup && (
          <LaunchSignupCancellationModal
            signup={cancellingLaunchSignup}
            nested
            onClose={() => setCancellingLaunchSignup(null)}
            onSaved={async () => {
              setCancellingLaunchSignup(null);
              setMessage("De onbetaalde aanmelding, factuur en periodieke facturering zijn volledig geannuleerd.");
              await refreshProfile();
            }}
          />
        )}
        {editingSection && subscription && (
          <EditCustomerModal
            customer={profileCustomer}
            existingCustomers={customers}
            existingViewers={existingViewers}
            plans={plans}
            launches={launches}
            canViewFinancials={canMutateMembers || canViewFinancials}
            section={editingSection}
            nested
            onClose={() => setEditingSection(null)}
            onSaved={async (result) => {
              const label = customerProfileSectionLabel(editingSection);
              setEditingSection(null);
              if (result.kind === "payment_arrears_resolved") {
                setMessage(`Betaalachterstand opgelost. Klant en toegang zijn hersteld; oorspronkelijke startdatum ${formatDate(result.startDate)} is behouden.`);
                setError(result.syncError || null);
              } else {
                setMessage(`${label} opgeslagen.`);
              }
              await refreshProfile();
            }}
          />
        )}
        {reactivating && subscription && (
          <EditCustomerModal
            customer={profileCustomer}
            reactivatePreset
            existingCustomers={customers}
            existingViewers={existingViewers}
            plans={plans}
            launches={launches}
            canViewFinancials={canMutateMembers || canViewFinancials}
            nested
            onClose={() => setReactivating(false)}
            onSaved={async () => {
              setReactivating(false);
              setMessage("Klant geheractiveerd en nieuwe abonnementsrun gestart.");
              await refreshProfile();
            }}
          />
        )}
      </div>
    </div>
  );
}

function customerProfileSectionLabel(section: CustomerProfileSection) {
  const labels: Record<CustomerProfileSection, string> = {
    contact: "Contactgegevens",
    address: "Adresgegevens",
    administration: "Administratie",
    membership: "Lidmaatschap",
    payment: "Betaalstatus en toegang",
    notes: "Notities",
  };
  return labels[section];
}

function BillingSubscriptionsPanel({
  customerId,
  currentSubscriptionId,
  billings,
  switches,
  launchSignups,
  canViewFinancials,
  canMutateMembers,
  onEdit,
  onCancel,
  onImport,
}: {
  customerId: string;
  currentSubscriptionId: string | null;
  billings: BillingSubscription[];
  switches: BillingSwitch[];
  launchSignups: LaunchSignup[];
  canViewFinancials: boolean;
  canMutateMembers: boolean;
  onEdit: () => void;
  onCancel: (signup: LaunchSignup) => void;
  onImport: () => void;
}) {
  const currentBilling = currentBillingForCustomer(billings, customerId, currentSubscriptionId);

  return (
    <section className="panel table-panel detail-data-panel billing-subscriptions-panel">
      <div className="panel-title-row">
        <div>
          <div className="panel-title">Abonnementen</div>
          <div className="panel-subtitle">Periodieke facturering, geplande wijzigingen en historie</div>
        </div>
        {canMutateMembers && currentSubscriptionId && !currentBilling && (
          <button type="button" className="secondary-button compact" onClick={onImport}>
            <Download size={14} /> Abonnement ophalen
          </button>
        )}
      </div>
      <div className="billing-subscriptions-table-shell">
        <table className="data-table detail-data-table billing-subscriptions-table">
          <thead><tr>
            <th>Abonnement</th><th>Status</th><th>Periode</th>{canViewFinancials && <th>Bedrag</th>}
            <th>Factuursysteem</th><th>Periodiek ID</th><th>Start</th><th>Volgende factuur</th>
            <th>Stop gepland</th><th>Stopgezet op</th><th>Geplande wijziging</th><th className="sticky-actions">Acties</th>
          </tr></thead>
          <tbody>
            {billings.map((billing) => {
              const plannedSwitch = switches.find((item) => item.from_billing_subscription_id === billing.id && ["scheduled", "processing", "failed"].includes(item.status));
              const isCurrent = currentBilling?.id === billing.id;
              const launchSignup = billing.signup_id ? launchSignups.find((signup) => signup.id === billing.signup_id) || null : null;
              const canCancelPendingSignup = Boolean(
                launchSignup
                && ["pending", "failed"].includes(billing.status)
                && launchSignupCanBeCancelled(launchSignup.status),
              );
              return (
                <tr key={billing.id}>
                  <td><strong>{billing.plans?.display_label || "-"}</strong><span className="cell-subtext">{metadataText(billing.metadata, "billing_switch_id") ? "Vervangend abonnement" : isCurrent ? "Huidig abonnement" : "Historisch abonnement"}</span></td>
                  <td><Badge value={billing.status} />{billing.last_error && <span className="cell-subtext error-text">{billing.last_error}</span>}</td>
                  <td>{billingPeriodLabel(billing.billing_period)}</td>
                  {canViewFinancials && <td>{formatCurrency(billing.amount_due)}</td>}
                  <td>{billingProviderLabel(billing.provider)}<span className="cell-subtext">{billingAccountLabel(billing.provider_account)}</span></td>
                  <td>{billing.external_recurring_id || "-"}</td>
                  <td>{formatDateTime(billing.started_at)}</td>
                  <td>{formatDate(billing.next_invoice_date)}</td>
                  <td>{formatDate(billing.stop_scheduled_for)}</td>
                  <td>{formatDateTime(billing.stopped_at)}</td>
                  <td>{plannedSwitch ? <><strong>{plannedSwitch.to_plan?.display_label || "Nieuw abonnement"}</strong><span className="cell-subtext">Vanaf {formatDate(plannedSwitch.effective_on)} · <Badge value={plannedSwitch.status} /></span>{plannedSwitch.last_error && <span className="cell-subtext error-text">{plannedSwitch.last_error}</span>}</> : "-"}</td>
                  <td className="sticky-actions">
                    <div className="row-actions compact-action-icons">
                      {canMutateMembers && isCurrent && ["active", "switch_scheduled"].includes(billing.status) && (
                        <button type="button" className="icon-button" onClick={onEdit} title="Abonnement wijzigen of opzegging plannen" aria-label="Abonnement wijzigen of opzegging plannen">
                          <Edit3 size={15} />
                        </button>
                      )}
                      {canMutateMembers && canCancelPendingSignup && launchSignup && (
                        <button type="button" className="icon-button danger" onClick={() => onCancel(launchSignup)} title="Onbetaalde aanmelding en abonnement annuleren" aria-label="Onbetaalde aanmelding en abonnement annuleren">
                          <X size={15} />
                        </button>
                      )}
                      {!(canMutateMembers && ((isCurrent && ["active", "switch_scheduled"].includes(billing.status)) || canCancelPendingSignup)) && "-"}
                    </div>
                  </td>
                </tr>
              );
            })}
            {billings.length === 0 && <tr><td colSpan={canViewFinancials ? 12 : 11} className="empty">Geen periodiek factureringsabonnement gekoppeld.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CustomerSubscriptionImportModal({
  customer,
  subscription,
  onClose,
  onSaved,
}: {
  customer: Customer;
  subscription: CustomerSubscription;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [candidates, setCandidates] = useState<CustomerSubscriptionImportCandidate[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [searchErrors, setSearchErrors] = useState<Array<{ providerAccount: string; error: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const selectedCandidate = candidates.find((candidate) => customerSubscriptionCandidateKey(candidate) === selectedKey) || null;

  useEffect(() => {
    let cancelled = false;
    loadCustomerImportCandidates<CustomerSubscriptionImportCandidate>(customer.id, "subscription")
      .then((payload) => {
        if (cancelled) return;
        setCandidates(payload.candidates);
        setSearchErrors(payload.searchErrors);
        if (payload.candidates.length === 1) setSelectedKey(customerSubscriptionCandidateKey(payload.candidates[0]));
      })
      .catch((loadError) => { if (!cancelled) setError(loadError instanceof Error ? loadError.message : String(loadError)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customer.id]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedCandidate) {
      setError("Kies het abonnement dat je wilt ophalen.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await importCustomerSubscription({
        customerId: customer.id,
        provider: selectedCandidate.provider,
        providerAccount: selectedCandidate.providerAccount,
        clientId: selectedCandidate.clientId,
        recurringId: selectedCandidate.id,
        note,
      });
      await onSaved();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nested-modal-backdrop">
      <form className="modal launch-existing-billing-modal" onSubmit={save}>
        <div className="modal-header">
          <div><h2>Abonnement ophalen</h2><p className="muted">Zoeken op {customer.billing_email}</p></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="banner info">Je haalt alleen het bestaande abonnement op. WeFact of Invoice Ninja wordt niet gewijzigd en een bestaande opzegging wordt niet ingetrokken.</div>
        <div className="launch-review-summary">
          <span><strong>Abonnement</strong>{subscription.plans?.display_label || "-"}</span>
          <span><strong>Verwacht bedrag</strong>{formatCurrency(subscription.price_incl_vat_applied)}</span>
          <span><strong>Status</strong>{subscription.status}</span>
          <span><strong>Opzegging</strong>{subscription.access_end_date ? `Factuurstop op ${formatDate(subscription.access_end_date)}` : "Niet ingepland"}</span>
        </div>
        {loading && <div className="loading-state"><RefreshCw className="spin" size={18} /> Bestaande abonnementen zoeken…</div>}
        {!loading && candidates.length === 0 && !error && <div className="banner warning">Geen bestaand abonnement gevonden voor dit e-mailadres. Controleer in welk factuuraccount de klant staat.</div>}
        {searchErrors.map((item) => <div key={item.providerAccount} className="banner warning compact-banner">{billingAccountLabel(item.providerAccount)} kon niet worden doorzocht: {item.error}</div>)}
        {!loading && candidates.length > 0 && <div className="existing-billing-candidates">
          {candidates.map((candidate) => {
            const key = customerSubscriptionCandidateKey(candidate);
            return (
              <label key={key} className={selectedKey === key ? "selected" : ""}>
                <input type="radio" name="customer-existing-subscription" checked={selectedKey === key} onChange={() => setSelectedKey(key)} />
                <span><strong>{candidate.label}</strong><small>{formatCurrency(candidate.amountDue)} · {candidate.billingPeriod || "periode onbekend"} · volgende {formatDate(candidate.nextInvoiceDate)} · {billingAccountLabel(candidate.providerAccount)}</small><small>{candidate.clientLabel} · periodiek ID {candidate.id}</small></span>
                <Badge value={candidate.status} tone={["active", "stopping"].includes(candidate.status) ? "success" : "muted"} />
              </label>
            );
          })}
        </div>}
        {selectedCandidate && <div className="form-grid compact-review-fields">
          <label className="span-2">Interne notitie<textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Eventuele toelichting bij deze handmatige koppeling" /></label>
        </div>}
        <p className="muted">Na bevestiging verschijnt dit abonnement in het overzicht. Facturen haal je los op in de rubriek Facturen.</p>
        {subscription.access_end_date && <div className="banner warning">De opzegging blijft staan. De periodieke facturering wordt automatisch ingepland om op {formatDate(subscription.access_end_date)} te stoppen.</div>}
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annuleren</button><button className="primary-button" disabled={busy || loading || !selectedCandidate}>{busy ? "Abonnement ophalen…" : "Abonnement ophalen"}</button></div>
      </form>
    </div>
  );
}

function customerSubscriptionCandidateKey(candidate: CustomerSubscriptionImportCandidate) {
  return `${candidate.provider}:${candidate.providerAccount}:${candidate.clientId}:${candidate.id}`;
}

async function loadCustomerImportCandidates<T>(customerId: string, kind: "subscription" | "invoices") {
  const session = await supabase!.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Geen actieve sessie gevonden.");
  const response = await fetch(`${publicSupabaseUrl}/functions/v1/billing-customer-import?customer_id=${encodeURIComponent(customerId)}&kind=${kind}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Gegevens ophalen mislukt.");
  return {
    candidates: (payload.candidates || []) as T[],
    searchErrors: (payload.searchErrors || []) as Array<{ providerAccount: string; error: string }>,
  };
}

async function importCustomerSubscription(input: {
  customerId: string;
  provider: CustomerSubscriptionImportCandidate["provider"];
  providerAccount: CustomerSubscriptionImportCandidate["providerAccount"];
  clientId: string;
  recurringId: string;
  note: string;
}) {
  const session = await supabase!.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Geen actieve sessie gevonden.");
  const response = await fetch(`${publicSupabaseUrl}/functions/v1/billing-customer-import`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      kind: "subscription",
      customer_id: input.customerId,
      provider: input.provider,
      provider_account: input.providerAccount,
      client_id: input.clientId,
      recurring_id: input.recurringId,
      note: input.note.trim() || null,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Abonnement ophalen mislukt.");
  return payload as { billingId: string };
}

type CustomerInvoiceImportGroup = {
  key: string;
  provider: CustomerBillingImportCandidate["provider"];
  providerAccount: CustomerBillingImportCandidate["providerAccount"];
  clientId: string;
  clientLabel: string;
  invoices: CustomerBillingImportCandidate[];
};

function CustomerInvoiceImportModal({
  customer,
  onClose,
  onSaved,
}: {
  customer: Customer;
  onClose: () => void;
  onSaved: (count: number) => void | Promise<void>;
}) {
  const [candidates, setCandidates] = useState<CustomerBillingImportCandidate[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [searchErrors, setSearchErrors] = useState<Array<{ providerAccount: string; error: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const groups = useMemo(() => customerInvoiceImportGroups(candidates), [candidates]);
  const selectedGroup = groups.find((group) => group.key === selectedKey) || null;

  useEffect(() => {
    let cancelled = false;
    loadCustomerImportCandidates<CustomerBillingImportCandidate>(customer.id, "invoices")
      .then((payload) => {
        if (cancelled) return;
        setCandidates(payload.candidates);
        setSearchErrors(payload.searchErrors);
        const loadedGroups = customerInvoiceImportGroups(payload.candidates);
        if (loadedGroups.length === 1) setSelectedKey(loadedGroups[0].key);
      })
      .catch((loadError) => { if (!cancelled) setError(loadError instanceof Error ? loadError.message : String(loadError)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customer.id]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedGroup) {
      setError("Kies de factuurklant waarvan je de facturen wilt ophalen.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await importCustomerInvoices({
        customerId: customer.id,
        provider: selectedGroup.provider,
        providerAccount: selectedGroup.providerAccount,
        clientId: selectedGroup.clientId,
      });
      await onSaved(result.importedInvoiceCount);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nested-modal-backdrop">
      <form className="modal launch-existing-billing-modal" onSubmit={save}>
        <div className="modal-header">
          <div><h2>Facturen ophalen</h2><p className="muted">Zoeken op {customer.billing_email}</p></div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="banner info">Je leest alleen bestaande verstuurde, deels betaalde en betaalde facturen in. Er wordt niets verstuurd en het abonnement wordt niet gewijzigd.</div>
        {loading && <div className="loading-state"><RefreshCw className="spin" size={18} /> Factuurhistorie zoeken in alle factuuraccounts…</div>}
        {!loading && groups.length === 0 && !error && <div className="banner warning">Geen verstuurde of betaalde facturen gevonden voor dit e-mailadres.</div>}
        {searchErrors.map((item) => <div key={item.providerAccount} className="banner warning compact-banner">{billingAccountLabel(item.providerAccount)} kon niet worden doorzocht: {item.error}</div>)}
        {!loading && groups.length > 0 && <div className="existing-billing-candidates">
          {groups.map((group) => {
            const dates = group.invoices.map((invoice) => invoice.invoiceDate).filter(Boolean).sort();
            const paid = group.invoices.filter((invoice) => invoice.status === "paid").length;
            const open = group.invoices.length - paid;
            return (
              <label key={group.key} className={selectedKey === group.key ? "selected" : ""}>
                <input type="radio" name="customer-invoice-client" checked={selectedKey === group.key} onChange={() => setSelectedKey(group.key)} />
                <span>
                  <strong>{group.clientLabel}</strong>
                  <small>{billingAccountLabel(group.providerAccount)} · klant-ID {group.clientId}</small>
                  <small>{dates.length ? `${formatDate(dates[0])} t/m ${formatDate(dates[dates.length - 1])}` : "Factuurdatums onbekend"} · {paid} betaald · {open} open/deels betaald</small>
                </span>
                <Badge value={`${group.invoices.length} factu${group.invoices.length === 1 ? "ur" : "ren"}`} tone="muted" />
              </label>
            );
          })}
        </div>}
        <p className="muted">Je kunt deze actie later opnieuw uitvoeren; bestaande facturen worden dan bijgewerkt en nieuwe facturen worden toegevoegd.</p>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annuleren</button><button className="primary-button" disabled={busy || loading || !selectedGroup}>{busy ? "Facturen inlezen…" : selectedGroup ? `${selectedGroup.invoices.length} facturen inlezen` : "Facturen inlezen"}</button></div>
      </form>
    </div>
  );
}

function customerInvoiceImportGroups(candidates: CustomerBillingImportCandidate[]): CustomerInvoiceImportGroup[] {
  const groups = new Map<string, CustomerInvoiceImportGroup>();
  for (const candidate of candidates) {
    const key = `${candidate.provider}:${candidate.providerAccount}:${candidate.clientId}`;
    const group = groups.get(key) || {
      key,
      provider: candidate.provider,
      providerAccount: candidate.providerAccount,
      clientId: candidate.clientId,
      clientLabel: candidate.clientLabel,
      invoices: [],
    };
    group.invoices.push(candidate);
    groups.set(key, group);
  }
  return [...groups.values()].sort((left, right) => right.invoices.length - left.invoices.length || left.clientLabel.localeCompare(right.clientLabel));
}

async function importCustomerInvoices(input: {
  customerId: string;
  provider: CustomerBillingImportCandidate["provider"];
  providerAccount: CustomerBillingImportCandidate["providerAccount"];
  clientId: string;
}) {
  const session = await supabase!.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Geen actieve sessie gevonden.");
  const response = await fetch(`${publicSupabaseUrl}/functions/v1/billing-customer-import`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      kind: "invoices",
      customer_id: input.customerId,
      provider: input.provider,
      provider_account: input.providerAccount,
      client_id: input.clientId,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || "Facturen ophalen mislukt.");
  return payload as { importedInvoiceCount: number };
}

function SubscriptionActionsModal({
  hasOpenSwitch,
  hasScheduledCancellation,
  onClose,
  onSwitch,
  onCancel,
}: {
  hasOpenSwitch: boolean;
  hasScheduledCancellation: boolean;
  onClose: () => void;
  onSwitch: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="nested-modal-backdrop">
      <div className="modal subscription-actions-modal">
        <div className="modal-header">
          <h2>Abonnement wijzigen</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <p className="muted">Kies wat je met dit lopende abonnement wilt doen.</p>
        <div className="subscription-action-options">
          <button type="button" className="secondary-button subscription-action-option" onClick={onSwitch} disabled={hasOpenSwitch || hasScheduledCancellation}>
            <Edit3 size={16} />
            <span><strong>Abonnement of facturering wijzigen</strong><small>{hasOpenSwitch ? "Er staat al een wijziging open." : hasScheduledCancellation ? "Trek eerst de geplande opzegging in." : "Plan, betaalvariant of volgende factuurperiode aanpassen."}</small></span>
          </button>
          <button type="button" className="secondary-button subscription-action-option" onClick={onCancel}>
            <CalendarClock size={16} />
            <span><strong>{hasScheduledCancellation ? "Opzegging wijzigen" : "Opzegging inplannen"}</strong><small>Toegang en periodieke facturering op een gekozen datum beëindigen.</small></span>
          </button>
        </div>
        <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annuleren</button></div>
      </div>
    </div>
  );
}

function BillingSwitchModal({
  customer,
  currentBilling,
  currentSubscription,
  plans,
  onClose,
  onSaved,
}: {
  customer: Customer;
  currentBilling: BillingSubscription;
  currentSubscription: CustomerSubscription;
  plans: Plan[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const billingPlans = plans.filter((plan) => ["eur", "btc"].includes(plan.payment_variant));
  const [planId, setPlanId] = useState(currentSubscription.plans?.id || billingPlans[0]?.id || "");
  const [effectiveOn, setEffectiveOn] = useState(currentBilling.next_invoice_date || new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { error: switchError } = await supabase!.rpc("schedule_billing_switch", {
        p_customer_id: customer.id,
        p_new_plan_id: planId,
        p_effective_on: effectiveOn,
      });
      if (switchError) throw switchError;
      await processBillingNow();
      onSaved();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : String(saveError));
    } finally {
      setBusy(false);
    }
  }

  const selectedPlan = billingPlans.find((plan) => plan.id === planId);
  const nextProvider = selectedPlan?.payment_variant === "btc" ? "Invoice Ninja" : "WeFact";
  const nextAccount = selectedPlan?.payment_variant === "btc"
    ? (customer.is_business_customer ? "Mark Daamen LLC" : "Mark Daamen")
    : "WeFact";

  return (
    <div className="nested-modal-backdrop">
      <form className="modal billing-switch-modal" onSubmit={save}>
        <div className="modal-header"><h2>Facturering wijzigen</h2><button type="button" className="icon-button" onClick={onClose}><X size={18} /></button></div>
        <div className="banner warning">De wissel gaat in op de gekozen volgende factuurdatum. Er is geen proratering en er vindt geen terugbetaling plaats.</div>
        <div className="form-grid">
          <label className="span-2">Nieuw abonnement<select value={planId} onChange={(event) => setPlanId(event.target.value)} required>{billingPlans.map((plan) => <option key={plan.id} value={plan.id}>{plan.display_label}</option>)}</select></label>
          <label className="span-2">Ingangsdatum<input type="date" min={new Date().toISOString().slice(0, 10)} value={effectiveOn} onChange={(event) => setEffectiveOn(event.target.value)} required /></label>
        </div>
        <dl className="detail-list switch-preview">
          <dt>Huidig</dt><dd>{billingProviderLabel(currentBilling.provider)} · {billingAccountLabel(currentBilling.provider_account)}</dd>
          <dt>Nieuw</dt><dd>{nextProvider} · {nextAccount}</dd>
          <dt>Termijn</dt><dd>{billingPeriodLabel(selectedPlan?.billing_period || "-")}</dd>
        </dl>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Annuleren</button><button className="primary-button" disabled={busy || !planId || !effectiveOn}>{busy ? "Plannen..." : "Wissel plannen"}</button></div>
      </form>
    </div>
  );
}

function BillingInvoicesPanel({
  invoices,
  canViewFinancials,
  canImport,
  onImport,
}: {
  invoices: BillingInvoice[];
  canViewFinancials: boolean;
  canImport: boolean;
  onImport: () => void;
}) {
  return (
    <section className="panel table-panel detail-data-panel invoice-panel">
      <div className="panel-title-row">
        <div>
          <div className="panel-title">Facturen</div>
        </div>
        {canImport && <button type="button" className="secondary-button compact" onClick={onImport}><Download size={14} /> Facturen ophalen</button>}
      </div>
      <table className="data-table detail-data-table invoice-table">
        <thead><tr><th>Factuur</th><th>Status</th><th>Factuurdatum</th><th>Vervaldatum</th>{canViewFinancials && <th>Bedrag</th>}{canViewFinancials && <th>Betaald</th>}<th>Betaald op</th><th>Acties</th></tr></thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.id}>
              <td><strong>{invoice.external_invoice_number || invoice.external_invoice_id || "-"}</strong><span className="cell-subtext">{billingProviderLabel(invoice.provider)} · {billingAccountLabel(invoice.provider_account)}</span></td>
              <td><Badge value={billingInvoiceStatusLabel(invoice.status)} tone={billingInvoiceStatusTone(invoice.status)} /></td>
              <td>{formatDate(invoice.invoice_date)}</td>
              <td>{formatDate(invoice.due_date)}</td>
              {canViewFinancials && <td>{formatCurrency(invoice.amount_due)}</td>}
              {canViewFinancials && <td>{formatCurrency(invoice.amount_paid)}</td>}
              <td>{formatDateTime(invoice.paid_at)}</td>
              <td>{invoice.payment_url ? <a className="secondary-button compact inline-link" href={invoice.payment_url} target="_blank" rel="noreferrer"><FileText size={14} /> Factuur openen</a> : "-"}</td>
            </tr>
          ))}
          {invoices.length === 0 && <tr><td colSpan={canViewFinancials ? 8 : 6} className="empty">Geen facturen gevonden.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

function billingInvoiceStatusLabel(status: string) {
  const labels: Record<string, string> = {
    draft: "concept",
    sent: "verstuurd",
    partial: "deels betaald",
    paid: "betaald",
    cancelled: "geannuleerd",
    reversed: "teruggedraaid",
    unknown: "onbekend",
  };
  return labels[status] || status || "onbekend";
}

function billingInvoiceStatusTone(status: string): BadgeTone {
  if (status === "paid") return "success";
  if (["cancelled", "reversed"].includes(status)) return "danger";
  if (["sent", "partial"].includes(status)) return "warning";
  return "muted";
}

function DetailJobsPanel({ jobs, canRetry, onRetry }: { jobs: IntegrationJob[]; canRetry: boolean; onRetry: (job: IntegrationJob) => void }) {
  return (
    <details className="panel detail-data-panel collapsible-detail-panel">
      <summary className="collapsible-panel-summary"><span className="panel-title">Koppelingstaken</span><ChevronDown size={17} /></summary>
      <div className="collapsible-panel-content">
      <table className="data-table detail-data-table detail-jobs-table">
        <thead><tr><th>Aangemaakt</th><th>Provider en actie</th><th>Status</th><th>Gepland voor</th><th>Afgerond</th><th>Pogingen</th><th>Detail</th></tr></thead>
        <tbody>
          {jobs.map((job) => (
            <tr key={job.id}>
              <td>{formatDateTimeSeconds(job.created_at)}</td>
              <td>
                <strong>{integrationJobLabel(job.job_type)}</strong>
                <span className="cell-subtext">{providerLabel(job.provider)} / {job.job_type}</span>
              </td>
              <td><Badge value={job.status} /></td>
              <td>{formatDateTimeSeconds(job.scheduled_for)}</td>
              <td>{formatDateTimeSeconds(job.completed_at)}</td>
              <td>{job.attempts}</td>
              <td>
                {job.last_error
                  ? <span className="error-text">{job.last_error}</span>
                  : <span>{jobDetail(job)}</span>}
                {canRetry && ["failed", "retry_needed"].includes(job.status) && (
                  <button className="secondary-button compact inline-action" onClick={() => onRetry(job)}>Opnieuw proberen</button>
                )}
              </td>
            </tr>
          ))}
          {jobs.length === 0 && <tr><td colSpan={7} className="empty">Geen koppelingstaken.</td></tr>}
        </tbody>
      </table>
      </div>
    </details>
  );
}

function DetailLogsPanel({ logs, events }: { logs: IntegrationLog[]; events: AuditEvent[] }) {
  const rows = [
    ...logs.map(formatActivityIntegrationLog),
    ...events.map(formatActivityEvent),
  ].sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());

  return (
    <details className="panel detail-data-panel collapsible-detail-panel">
      <summary className="collapsible-panel-summary"><span className="panel-title">Laatste logs</span><ChevronDown size={17} /></summary>
      <div className="collapsible-panel-content">
      <table className="data-table detail-data-table detail-logs-table">
        <thead><tr><th>Tijd</th><th>Platform / actie</th><th>Status</th><th>Wat is er gebeurd?</th><th>Context en resultaat</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{formatDateTimeSeconds(row.when)}</td>
              <td><strong>{activityActionLabel(row.title)}</strong></td>
              <td><Badge value={row.status} /></td>
              <td>{row.detail}</td>
              <td>
                {(row.details || []).map((detail) => <div key={detail}>{detail}</div>)}
                {row.meta && <div className="cell-subtext">{row.meta}</div>}
                {!row.details?.length && !row.meta && "-"}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="empty">Nog geen logs voor deze klant.</td></tr>}
        </tbody>
      </table>
      </div>
    </details>
  );
}

function EditCustomerModal({
  customer,
  reactivatePreset = false,
  section,
  nested = false,
  existingCustomers,
  existingViewers,
  plans,
  launches,
  canViewFinancials,
  onClose,
  onSaved,
}: {
  customer: Customer;
  reactivatePreset?: boolean;
  section?: CustomerProfileSection;
  nested?: boolean;
  existingCustomers: Customer[];
  existingViewers: Viewer[];
  plans: Plan[];
  launches: Launch[];
  canViewFinancials: boolean;
  onClose: () => void;
  onSaved: (result: EditCustomerSaveResult) => void | Promise<void>;
}) {
  const subscription = currentSubscription(customer);
  const shouldPrepareReactivation = reactivatePreset && Boolean(subscription) && !isAccessStatusActive(subscription?.status || customer.status);
  const today = new Date().toISOString().slice(0, 10);
  const [firstName, setFirstName] = useState(customer.first_name || "");
  const [lastName, setLastName] = useState(customer.last_name || "");
  const [companyName, setCompanyName] = useState(customer.company_name || "");
  const [billingEmail, setBillingEmail] = useState(customer.billing_email);
  const [mailingEmail, setMailingEmail] = useState(customer.mailing_email_override || "");
  const [thinkificEmail, setThinkificEmail] = useState(customer.thinkific_email_override || "");
  const [address, setAddress] = useState(customer.address || "");
  const [postcode, setPostcode] = useState(customer.postcode || "");
  const [city, setCity] = useState(customer.city || "");
  const [country, setCountry] = useState(customer.country || "");
  const [taxType, setTaxType] = useState(customer.tax_type || "");
  const [taxNumber, setTaxNumber] = useState(customer.tax_number || "");
  const [isBusinessCustomer, setIsBusinessCustomer] = useState(customer.is_business_customer || false);
  const [invoiceCustomerId, setInvoiceCustomerId] = useState(metadataText(customer.metadata, "invoice_customer_id") || metadataText(subscription?.metadata, "invoice_customer_id"));
  const [ninjaCustomerId, setNinjaCustomerId] = useState(metadataText(customer.metadata, "ninja_customer_id") || metadataText(subscription?.metadata, "ninja_customer_id"));
  const [genderMarker, setGenderMarker] = useState(genderCode(customer, subscription));
  const [excelAmount, setExcelAmount] = useState(metadataText(subscription?.metadata, "excel_amount"));
  const [excelBillingPeriod, setExcelBillingPeriod] = useState(metadataText(subscription?.metadata, "excel_billing_period") || metadataText(subscription?.metadata, "excel_term"));
  const [planId, setPlanId] = useState(subscription?.plans?.id || plans[0]?.id || "");
  const [status, setStatus] = useState(shouldPrepareReactivation ? "active" : editableCustomerStatus(subscription?.status || customer.status));
  const [paymentArrears, setPaymentArrears] = useState(shouldPrepareReactivation ? false : customer.payment_arrears || false);
  const [startDate, setStartDate] = useState(shouldPrepareReactivation ? today : subscription?.start_date || "");
  const [invoiceDate, setInvoiceDate] = useState(shouldPrepareReactivation ? today : subscription?.invoice_date || "");
  const [cancellationDate, setCancellationDate] = useState(shouldPrepareReactivation ? "" : subscription?.cancellation_date || "");
  const [launchId, setLaunchId] = useState("");
  const [vatRegime, setVatRegime] = useState(subscription?.vat_regime || "standard_21");
  const [vatRate, setVatRate] = useState(String(Number(subscription?.vat_rate_applied ?? 0.21)));
  const [priceIncl, setPriceIncl] = useState(String(Number(subscription?.price_incl_vat_applied ?? subscription?.plans?.price_incl_vat ?? 0)));
  const [notes, setNotes] = useState(customer.notes || "");
  const [stoppedDueToUnpaidInvoice, setStoppedDueToUnpaidInvoice] = useState(shouldPrepareReactivation ? false : customer.stopped_due_to_unpaid_invoice || false);
  const [error, setError] = useState<string | null>(null);
  const [allowEmailOverlap, setAllowEmailOverlap] = useState(false);
  const [busy, setBusy] = useState(false);
  const statusChangedToEnded = status === "ended" && subscription.status !== "ended";
  const resolvesPaymentArrears = section === "payment"
    && customer.payment_arrears
    && !paymentArrears
    && !customer.stopped_due_to_unpaid_invoice
    && !stoppedDueToUnpaidInvoice
    && customer.status === "ended"
    && subscription.status === "ended";
  const emailConflicts = useMemo(
    () => findEmailConflicts({ billingEmail, mailingEmail, thinkificEmail }, existingCustomers, existingViewers, customer.id),
    [billingEmail, mailingEmail, thinkificEmail, existingCustomers, existingViewers, customer.id],
  );
  const outsideLaunch = launches.find((launch) => launch.name === OUTSIDE_LAUNCH_NAME);
  const launchChoices = useMemo(() => {
    const open = launches.filter((launch) => launch.status === "open");
    return [...open].sort((a, b) => Number(b.name === OUTSIDE_LAUNCH_NAME) - Number(a.name === OUTSIDE_LAUNCH_NAME) || String(b.launch_month || "").localeCompare(String(a.launch_month || "")));
  }, [launches]);

  useEffect(() => {
    setAllowEmailOverlap(false);
  }, [billingEmail, mailingEmail, thinkificEmail]);

  useEffect(() => {
    if (!shouldPrepareReactivation || launchId || !outsideLaunch?.id) return;
    setLaunchId(outsideLaunch.id);
  }, [launchId, outsideLaunch?.id, shouldPrepareReactivation]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const plan = plans.find((item) => item.id === planId);
    if (!plan || !subscription) {
      setError("Klant heeft nog geen bewerkbaar abonnement.");
      return;
    }
    setBusy(true);
    setError(null);
    if (section === "payment") {
      if (resolvesPaymentArrears) {
        const { data, error: resumeError } = await supabase!.rpc("resume_customer_after_payment_arrears", {
          p_customer_id: customer.id,
          p_subscription_id: subscription.id,
        });
        if (resumeError) {
          setError(resumeError.message);
          setBusy(false);
          return;
        }
        const result = data && typeof data === "object" ? data as Record<string, unknown> : {};
        const jobIds = Array.isArray(result.job_ids) ? result.job_ids.filter((id): id is string => typeof id === "string") : [];
        let syncError: string | null = null;
        try {
          await processIntegrationJobsNow(customer.id, jobIds);
        } catch (syncFailure) {
          syncError = `De klant is wel geactiveerd, maar niet alle externe toegang kon direct worden hersteld: ${syncFailure instanceof Error ? syncFailure.message : String(syncFailure)}. De mislukte taak kan opnieuw worden geprobeerd.`;
        }
        await onSaved({
          kind: "payment_arrears_resolved",
          startDate: typeof result.start_date === "string" ? result.start_date : subscription.start_date,
          syncError,
        });
        return;
      }

      const { error: paymentError } = await supabase!.from("customers").update({
        payment_arrears: paymentArrears,
        stopped_due_to_unpaid_invoice: stoppedDueToUnpaidInvoice,
      }).eq("id", customer.id);
      if (paymentError) {
        setError(paymentError.message);
        setBusy(false);
        return;
      }
      await supabase!.from("audit_events").insert({
        action: "customer_payment_status_updated",
        entity_type: "customer",
        entity_id: customer.id,
        customer_id: customer.id,
        success: true,
        summary: `Betaalstatus bijgewerkt voor ${customer.billing_email}`,
        before_data: {
          payment_arrears: customer.payment_arrears,
          stopped_due_to_unpaid_invoice: customer.stopped_due_to_unpaid_invoice,
        },
        after_data: {
          payment_arrears: paymentArrears,
          stopped_due_to_unpaid_invoice: stoppedDueToUnpaidInvoice,
        },
      });
      await onSaved({ kind: "saved" });
      return;
    }
    if (!section || section === "contact") {
      const invalidEmail = invalidEmailField([
        { label: "Factuur e-mail", value: billingEmail, required: true },
        { label: "Mailing e-mail", value: mailingEmail },
        { label: "Thinkific e-mail", value: thinkificEmail },
      ]);
      if (invalidEmail) {
        setError(`${invalidEmail} is geen geldig e-mailadres. Gebruik één adres per veld.`);
        setBusy(false);
        return;
      }
      if (emailConflicts.billingConflict) {
        setError(`Factuur e-mail bestaat al bij ${customerLabel(emailConflicts.billingConflict.customer)}. Open die bestaande klant in plaats van deze klant te wijzigen.`);
        setBusy(false);
        return;
      }
      if (emailConflicts.activeViewerConflict) {
        setError(`Factuur e-mail bestaat al als actieve meekijker bij ${emailConflicts.activeViewerConflict.viewer.customers?.display_name || emailConflicts.activeViewerConflict.viewer.customers?.billing_email || "onbekende hoofdklant"}. Verwijder of beoordeel eerst die meekijker.`);
        setBusy(false);
        return;
      }
      if ((emailConflicts.warnings.length > 0 || emailConflicts.viewerWarnings.length > 0) && !allowEmailOverlap) {
        setError("Er is e-mailoverlap met een bestaande klant. Controleer de melding hieronder en bevestig bewust doorgaan als dit klopt.");
        setBusy(false);
        return;
      }
    }
    const vat = calculateVat(Number(priceIncl), vatRegime, Number(vatRate));
    const isReactivation = isAccessStatusActive(status) && !isAccessStatusActive(subscription.status);
    // Alleen een echte overgang naar ended is een beëindigingsactie. Een
    // bestaand ended-profiel mag administratieve velden wijzigen zonder
    // opnieuw externe koppelingstaken te starten.
    const endedFromEdit = statusChangedToEnded;
    const previousEmailTargets = integrationEmailTargets({
      billingEmail: customer.billing_email,
      mailingEmail: customer.mailing_email_override,
      thinkificEmail: customer.thinkific_email_override,
    });
    const currentEmailTargets = integrationEmailTargets({
      billingEmail,
      mailingEmail,
      thinkificEmail,
    });
    const emailTargetsChanged = JSON.stringify(previousEmailTargets) !== JSON.stringify(currentEmailTargets);
    const emailChange = emailChangeTargets(previousEmailTargets, currentEmailTargets);
    const { error: customerError } = await supabase!.from("customers").update({
      first_name: firstName || null,
      last_name: lastName || null,
      company_name: companyName || null,
      display_name: companyName || [firstName, lastName].filter(Boolean).join(" ") || billingEmail,
      billing_email: billingEmail.trim(),
      mailing_email_override: mailingEmail.trim() || null,
      thinkific_email_override: thinkificEmail.trim() || null,
      address: address || null,
      postcode: postcode || null,
      city: city || null,
      country: country || null,
      tax_type: taxType || null,
      tax_number: taxNumber || null,
      is_business_customer: Boolean(isBusinessCustomer || companyName || taxNumber),
      status,
      payment_arrears: paymentArrears,
      stopped_due_to_unpaid_invoice: stoppedDueToUnpaidInvoice,
      notes: notes || null,
      metadata: cleanMetadata({
        ...(customer.metadata || {}),
        invoice_customer_id: invoiceCustomerId || null,
        ninja_customer_id: ninjaCustomerId || null,
        gender_marker: genderMarker || null,
      }),
    }).eq("id", customer.id);
    if (customerError) {
      setError(customerError.message);
      setBusy(false);
      return;
    }
    const subscriptionUpdates: Record<string, unknown> = {
      plan_id: plan.id,
      status,
      start_date: startDate || null,
      invoice_date: invoiceDate || null,
      cancellation_date: cancellationDate || null,
      counts_toward_revenue: plan.counts_toward_revenue,
      notes: notes || null,
      metadata: cleanMetadata({
        ...(subscription.metadata || {}),
        invoice_customer_id: invoiceCustomerId || null,
        ninja_customer_id: ninjaCustomerId || null,
        gender_marker: genderMarker || null,
        excel_amount: excelAmount || null,
        excel_billing_period: excelBillingPeriod || null,
      }),
    };
    if (canViewFinancials) {
      subscriptionUpdates.vat_regime = vatRegime;
      subscriptionUpdates.vat_rate_applied = vat.rate;
      subscriptionUpdates.price_incl_vat_applied = vat.priceIncl;
      subscriptionUpdates.price_excl_vat_applied = vat.priceExcl;
      subscriptionUpdates.vat_amount_applied = vat.vatAmount;
    }
    if (endedFromEdit) {
      // De externe toegang wordt bij een nieuwe overgang naar ended direct
      // verwerkt. De einddatum toegang is daarom vandaag; de opzegdatum blijft
      // altijd de handmatig ingevoerde administratieve datum.
      subscriptionUpdates.access_end_date = today;
      subscriptionUpdates.cancellation_reason = subscription.cancellation_reason
        || (stoppedDueToUnpaidInvoice ? "Handmatig beëindigd wegens onbetaalde factuur." : "Handmatig beëindigd via Klant wijzigen.");
      // ended_at wordt pas gezet wanneer alle externe toegangsacties geslaagd zijn.
      subscriptionUpdates.ended_at = null;
    }
    let activeSubscriptionId = subscription.id;
    if (isReactivation) {
      const { data: newSubscription, error: subscriptionError } = await supabase!.from("customer_subscriptions").insert({
        ...subscriptionUpdates,
        customer_id: customer.id,
        cancellation_date: null,
        access_end_date: null,
        ended_at: null,
        cancellation_reason: null,
      }).select("id").single();
      if (subscriptionError || !newSubscription) {
        setError(subscriptionError?.message || "Nieuwe abonnementsrun kon niet worden opgeslagen.");
        setBusy(false);
        return;
      }
      activeSubscriptionId = newSubscription.id;
      const selectedLaunchId = launchId || outsideLaunch?.id || null;
      if (selectedLaunchId) {
        const { error: launchError } = await supabase!.from("customer_launch_assignments").insert({
          launch_id: selectedLaunchId,
          customer_id: customer.id,
          subscription_id: activeSubscriptionId,
          assignment_method: "manual",
          confidence: "high",
          needs_review: false,
          notes: "Gekoppeld bij heractivatie.",
        });
        if (launchError) {
          setError(`Heractivatie opgeslagen, maar de lancering kon niet worden gekoppeld: ${launchError.message}`);
          setBusy(false);
          return;
        }
      }
      await supabase!.from("audit_events").insert({
        action: "subscription_run_created",
        entity_type: "customer_subscription",
        entity_id: activeSubscriptionId,
        customer_id: customer.id,
        success: true,
        summary: `Nieuwe abonnementsrun aangemaakt voor ${billingEmail}`,
        after_data: { previous_subscription_id: subscription.id, plan_id: plan.id, status, start_date: startDate || null },
      });
    } else {
      const { error: subscriptionError } = await supabase!.from("customer_subscriptions").update(subscriptionUpdates).eq("id", subscription.id);
      if (subscriptionError) {
        setError(subscriptionError.message);
        setBusy(false);
        return;
      }
    }
    if (endedFromEdit) {
      try {
        const scheduledFor = new Date().toISOString();
        const removal = await scheduleAccessRemovalJobs(
          customer.id,
          activeSubscriptionId,
          scheduledFor,
          "status_changed_to_ended",
          mergeIntegrationEmailTargets(previousEmailTargets, currentEmailTargets),
        );
        await supabase!.from("audit_events").insert({
          action: "cancellation_scheduled",
          entity_type: "customer_subscription",
          entity_id: activeSubscriptionId,
          customer_id: customer.id,
          success: true,
          summary: `Klant handmatig op ended gezet; externe toegang direct beëindigen voor ${billingEmail}`,
          after_data: {
            triggered_by: "status_changed_to_ended",
            cancellation_date: subscriptionUpdates.cancellation_date,
            access_end_date: subscriptionUpdates.access_end_date,
            scheduled_for: removal.scheduledFor,
            triggered_at: removal.triggeredAt,
            job_ids: removal.jobIds,
            already_succeeded_job_ids: removal.reusedJobIds,
            running_job_ids: removal.runningJobIds,
            email_targets: removal.emailTargets,
          },
        });
        if (removal.jobIds.length) {
          await processIntegrationJobsNow(customer.id, removal.jobIds, { accessEndOnly: true });
        }
      } catch (processError) {
        setError(`Klant is op ended gezet, maar één of meer externe beëindigingsacties konden niet direct worden uitgevoerd: ${processError instanceof Error ? processError.message : String(processError)}`);
        setBusy(false);
        return;
      }
    } else if (isAccessStatusActive(status) && (isReactivation || emailTargetsChanged)) {
      try {
        const scheduledFor = integrationScheduleForDate(startDate || new Date().toISOString().slice(0, 10));
        const syncJobs = await scheduleAccessSyncJobs(customer.id, activeSubscriptionId, scheduledFor, emailChange);
        // Bij een echte klantwijziging worden de externe records op ID
        // bijgewerkt. De oude e-mail mag dan niet als los account worden
        // uitgeschreven; dat zou de providerhistorie verbreken.
        const staleEmailJobIds = emailChange
          ? []
          : await scheduleStaleEmailCleanupJobs(customer.id, scheduledFor, previousEmailTargets, currentEmailTargets, syncJobs.byProvider);
        if (shouldProcessIntegrationDateNow(startDate || new Date().toISOString().slice(0, 10))) {
          await processIntegrationJobsNow(customer.id, [...syncJobs.ids, ...staleEmailJobIds]);
        }
      } catch (syncError) {
        setError(`Klant is opgeslagen, maar toegang kon niet direct worden gesynchroniseerd: ${syncError instanceof Error ? syncError.message : String(syncError)}`);
        setBusy(false);
        return;
      }
    }
    await supabase!.from("audit_events").insert({
      action: isReactivation ? "customer_reactivated" : "customer_updated",
      entity_type: "customer",
      entity_id: customer.id,
      customer_id: customer.id,
      success: true,
      summary: isReactivation ? `Klant heractiveerd: ${billingEmail}` : `Klant gewijzigd: ${billingEmail}`,
      after_data: {
        plan_id: plan.id,
        status,
        payment_arrears: paymentArrears,
        stopped_due_to_unpaid_invoice: stoppedDueToUnpaidInvoice,
        reactivated: isReactivation,
        ended_from_edit: endedFromEdit,
        address: address || null,
        postcode: postcode || null,
        city: city || null,
        country: country || null,
        tax_type: taxType || null,
        tax_number: taxNumber || null,
        gender_marker: genderMarker || null,
        excel_amount: excelAmount || null,
        excel_billing_period: excelBillingPeriod || null,
        invoice_date: invoiceDate || null,
        cancellation_date: cancellationDate || null,
        access_end_date: subscriptionUpdates.access_end_date ?? subscription.access_end_date ?? null,
        integration_email_targets: currentEmailTargets,
        ...(emailChange ? { email_change_mode: "same_customer_existing_account", email_change: emailChange } : {}),
        ...(endedFromEdit ? { external_access_processing: "direct" } : {}),
        ...(canViewFinancials ? { vat_regime: vatRegime, vat_rate_applied: vat.rate } : {}),
      },
    });
    await onSaved({ kind: "saved" });
  }

  return (
    <div className={nested ? "nested-modal-backdrop" : "modal-backdrop"}>
      <form className={`modal wide-modal ${section ? "profile-section-edit-modal" : ""}`} onSubmit={save}>
        <div className="modal-header">
          <h2>{shouldPrepareReactivation ? "Klant heractiveren" : section ? `${customerProfileSectionLabel(section)} wijzigen` : "Klant wijzigen"}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="form-grid">
          {(!section || section === "contact") && <section className="form-section span-2">
            <div className="form-section-divider"><span>Persoonsgegevens</span></div>
            <div className="form-section-fields">
              <label>Voornaam<input value={firstName} onChange={(event) => setFirstName(event.target.value)} /></label>
              <label>Achternaam<input value={lastName} onChange={(event) => setLastName(event.target.value)} /></label>
              <label className="span-2">Bedrijfsnaam<input value={companyName} onChange={(event) => setCompanyName(event.target.value)} /></label>
              {!section && <label>Geslacht<select value={genderMarker} onChange={(event) => setGenderMarker(event.target.value)}><option value="">Onbekend</option><option value="M">Man</option><option value="V">Vrouw</option></select></label>}
              <label className="checkbox-row"><input checked={isBusinessCustomer} onChange={(event) => setIsBusinessCustomer(event.target.checked)} type="checkbox" /> Zakelijke klant</label>
            </div>
          </section>}

          {(!section || section === "contact") && <section className="form-section span-2">
            <div className="form-section-divider"><span>Contactgegevens</span></div>
            <div className="form-section-fields">
              <label className="span-2">Factuur e-mail<input value={billingEmail} onChange={(event) => setBillingEmail(event.target.value)} type="email" required /></label>
              <div className="span-2">
                <EmailConflictPanel
                  conflicts={emailConflicts}
                  allowEmailOverlap={allowEmailOverlap}
                  onAllowEmailOverlap={setAllowEmailOverlap}
                />
              </div>
              <p className="form-section-help span-2">Dit is een wijziging van dezelfde klant. ActiveCampaign en Thinkific behouden het bestaande account en wijzigen alleen het e-mailadres.</p>
              <label>Mailing e-mail afwijking<input value={mailingEmail} onChange={(event) => setMailingEmail(event.target.value)} type="email" placeholder="Leeg = factuur e-mail" /></label>
              <label>Thinkific e-mail afwijking<input value={thinkificEmail} onChange={(event) => setThinkificEmail(event.target.value)} type="email" placeholder="Leeg = factuur e-mail" /></label>
            </div>
          </section>}

          {(!section || section === "address") && <section className="form-section span-2">
            <div className="form-section-divider"><span>Adresgegevens</span></div>
            <div className="form-section-fields">
              <label className="span-2">Adres<input value={address} onChange={(event) => setAddress(event.target.value)} /></label>
              <label>Postcode<input value={postcode} onChange={(event) => setPostcode(event.target.value)} /></label>
              <label>Stad<input value={city} onChange={(event) => setCity(event.target.value)} /></label>
              <label>Land<input value={country} onChange={(event) => setCountry(event.target.value)} /></label>
              <label>Tax type<input value={taxType} onChange={(event) => setTaxType(event.target.value)} /></label>
              <label>Btw-/taxnummer<input value={taxNumber} onChange={(event) => setTaxNumber(event.target.value)} /></label>
            </div>
          </section>}

          {(!section || section === "administration") && <section className="form-section span-2">
            <div className="form-section-divider"><span>Administratie</span></div>
            <div className="form-section-fields">
              <label>Factuur klantnummer<input value={invoiceCustomerId} onChange={(event) => setInvoiceCustomerId(event.target.value)} /></label>
              <label>Ninja klantnummer<input value={ninjaCustomerId} onChange={(event) => setNinjaCustomerId(event.target.value)} /></label>
              {section === "administration" && <label>Geslacht<select value={genderMarker} onChange={(event) => setGenderMarker(event.target.value)}><option value="">Onbekend / niet delen</option><option value="M">Man</option><option value="V">Vrouw</option></select></label>}
              <label>Excel bedrag<input value={excelAmount} onChange={(event) => setExcelAmount(event.target.value)} /></label>
              <label>Excel betaaltermijn<input value={excelBillingPeriod} onChange={(event) => setExcelBillingPeriod(event.target.value)} /></label>
            </div>
          </section>}

          {canViewFinancials && !section && (
            <section className="form-section span-2">
              <div className="form-section-divider"><span>BTW</span></div>
              <div className="form-section-fields">
                <label>BTW-regime<select value={vatRegime} onChange={(event) => setVatRegime(event.target.value)}><option value="standard_21">standard_21</option><option value="reverse_charge">reverse_charge</option><option value="exempt">exempt</option><option value="custom">custom</option></select></label>
                <label>BTW-rate<input value={vatRate} onChange={(event) => setVatRate(event.target.value)} type="number" step="0.0001" min="0" /></label>
                <label>Prijs incl. / bedrag<input value={priceIncl} onChange={(event) => setPriceIncl(event.target.value)} type="number" step="0.01" min="0" /></label>
              </div>
            </section>
          )}

          {(!section || section === "membership") && <section className="form-section span-2">
            <div className="form-section-divider"><span>Abonnement</span></div>
            <div className="form-section-fields">
              <label>Abonnement<select value={planId} onChange={(event) => setPlanId(event.target.value)} required>{plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.display_label}</option>)}</select></label>
              <label>Status<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="active">active</option><option value="ended">ended</option><option value="free">free</option><option value="barter">barter</option></select></label>
              {statusChangedToEnded && <p className="form-section-help span-2">Bij een overgang naar ended wordt de einddatum toegang op vandaag gezet en externe toegang direct verwerkt. De opzegdatum blijft handmatig.</p>}
              <label>Startdatum<input value={startDate} onChange={(event) => setStartDate(event.target.value)} type="date" /></label>
              <label>Factuurdatum<input value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} type="date" /></label>
              <label>Opzegdatum<input value={cancellationDate} onChange={(event) => setCancellationDate(event.target.value)} type="date" /></label>
              <label>Einddatum toegang<input className="read-only-field" value={subscription?.access_end_date || ""} type="date" disabled title="Wijzigen kan via Opzegging plannen." /></label>
              {shouldPrepareReactivation && <label className="span-2">Nieuwe lancering<select value={launchId} onChange={(event) => setLaunchId(event.target.value)}><option value={outsideLaunch?.id || ""}>{OUTSIDE_LAUNCH_NAME}</option>{launchChoices.filter((launch) => launch.id !== outsideLaunch?.id).map((launch) => <option key={launch.id} value={launch.id}>{launch.name} ({formatLaunchMonth(launch.launch_month)})</option>)}</select></label>}
            </div>
          </section>}

          {(!section || section === "payment") && <section className="form-section span-2">
            <div className="form-section-divider"><span>Betaalstatus &amp; toegang</span></div>
            <div className="form-section-fields">
              <label className="checkbox-row span-2"><input checked={paymentArrears} onChange={(event) => setPaymentArrears(event.target.checked)} type="checkbox" /> Handmatige betaalachterstand (oude werkwijze: ActiveCampaign en Thinkific stopgezet)</label>
              <label className="checkbox-row span-2"><input checked={stoppedDueToUnpaidInvoice} onChange={(event) => setStoppedDueToUnpaidInvoice(event.target.checked)} type="checkbox" /> {STOPPED_DUE_TO_UNPAID_INVOICE_LABEL}</label>
              {resolvesPaymentArrears && <div className="banner success span-2">Bij opslaan wordt het bestaande abonnement weer actief. De oorspronkelijke startdatum {formatDate(subscription.start_date)} en alle abonnementsgegevens blijven behouden; ActiveCampaign en Thinkific worden opnieuw gesynchroniseerd.</div>}
              {section === "payment" && customer.payment_arrears && !paymentArrears && customer.stopped_due_to_unpaid_invoice && <div className="banner warning span-2">De periodieke facturering is ook stopgezet. Gebruik na het opslaan Heractiveren om een nieuwe abonnementsrun te starten.</div>}
            </div>
          </section>}

          {(!section || section === "notes") && <section className="form-section span-2">
            <div className="form-section-divider"><span>Notities</span></div>
            <div className="form-section-fields">
              <label className="span-2">Notities<textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} /></label>
            </div>
          </section>}
        </div>
        {shouldPrepareReactivation && <div className="banner success">Heractivatie voorbereid: status active, startdatum vandaag en betaalachterstand uit. Bij opslaan wordt een nieuwe abonnementsrun aangemaakt en de toegang opnieuw gesynchroniseerd.</div>}
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuleren</button>
          <button className="primary-button" disabled={busy}>{busy ? "Opslaan..." : shouldPrepareReactivation ? "Heractiveren" : resolvesPaymentArrears ? "Opslaan en toegang herstellen" : "Opslaan"}</button>
        </div>
      </form>
    </div>
  );
}

function CancelCustomerModal({ customer, nested = false, onClose, onSaved }: { customer: Customer; nested?: boolean; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const subscription = currentSubscription(customer);
  const today = new Date().toISOString().slice(0, 10);
  const hasExistingCancellation = Boolean(subscription?.cancellation_date || subscription?.access_end_date);
  const [cancellationDate, setCancellationDate] = useState(subscription?.cancellation_date || today);
  const [accessEndDate, setAccessEndDate] = useState(subscription?.access_end_date || today);
  const [reason, setReason] = useState(subscription?.cancellation_reason || "");
  const [paymentArrears, setPaymentArrears] = useState(customer.payment_arrears || false);
  const [stoppedDueToUnpaidInvoice, setStoppedDueToUnpaidInvoice] = useState(customer.stopped_due_to_unpaid_invoice || false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!subscription) {
      setError("Geen abonnement gevonden.");
      return;
    }
    setBusy(true);
    setError(null);
    const scheduledFor = integrationScheduleForDate(accessEndDate);
    const triggeredAt = new Date().toISOString();
    const emailTargets = integrationEmailTargets({
      billingEmail: customer.billing_email,
      mailingEmail: customer.mailing_email_override,
      thinkificEmail: customer.thinkific_email_override,
    });
    const jobPayload = { triggered_by: "cancellation_modal", triggered_at: triggeredAt, reason: reason || null, email_targets: emailTargets };
    const { error: customerError } = await supabase!.from("customers").update({
      payment_arrears: paymentArrears,
      stopped_due_to_unpaid_invoice: stoppedDueToUnpaidInvoice,
    }).eq("id", customer.id);
    if (customerError) {
      setError(customerError.message);
      setBusy(false);
      return;
    }
    const { error: subError } = await supabase!.from("customer_subscriptions").update({
      cancellation_date: cancellationDate,
      access_end_date: accessEndDate,
      cancellation_reason: reason || null,
    }).eq("id", subscription.id);
    if (subError) {
      setError(subError.message);
      setBusy(false);
      return;
    }
    await supabase!.from("integration_jobs").update({
      status: "cancelled",
      completed_at: new Date().toISOString(),
      last_error: "Vervangen door een nieuw geplande opzegging.",
    })
      .eq("subscription_id", subscription.id)
      .in("job_type", ["cancel_active_member", "expire_customer_access", "remove_viewer_access"])
      .in("status", ["planned", "retry_needed", "failed"]);
    const { data: accessJobs, error: accessJobError } = await supabase!.from("integration_jobs").insert([
      { customer_id: customer.id, subscription_id: subscription.id, provider: "activecampaign", job_type: "cancel_active_member", scheduled_for: scheduledFor, status: "planned", payload: jobPayload },
      { customer_id: customer.id, subscription_id: subscription.id, provider: "thinkific", job_type: "expire_customer_access", scheduled_for: scheduledFor, status: "planned", payload: jobPayload },
    ]).select("id");
    if (accessJobError) {
      setError(accessJobError.message);
      setBusy(false);
      return;
    }
    const { data: viewers } = await supabase!.from("viewers").select("id").eq("customer_id", customer.id).eq("status", "active");
    const viewerJobs = (viewers || []).map((viewer) => ({
      customer_id: customer.id,
      viewer_id: viewer.id,
      subscription_id: subscription.id,
      provider: "activecampaign",
      job_type: "remove_viewer_access",
      scheduled_for: scheduledFor,
      status: "planned",
      payload: jobPayload,
    }));
    let viewerAccessJobs: Array<{ id: string }> = [];
    if (viewerJobs.length) {
      const { data, error } = await supabase!.from("integration_jobs").insert(viewerJobs).select("id");
      if (error) {
        setError(error.message);
        setBusy(false);
        return;
      }
      viewerAccessJobs = data || [];
    }
    await supabase!.from("audit_events").insert({
      action: "cancellation_scheduled",
      entity_type: "customer_subscription",
      entity_id: subscription.id,
      customer_id: customer.id,
      success: true,
      summary: `Opzegging ingepland voor ${customer.billing_email} per ${accessEndDate}`,
      after_data: {
        triggered_by: "cancellation_modal",
        cancellation_date: cancellationDate,
        access_end_date: accessEndDate,
        scheduled_for: scheduledFor,
        triggered_at: new Date().toISOString(),
        job_ids: [...(accessJobs || []), ...viewerAccessJobs].map((job) => job.id),
        payment_arrears: paymentArrears,
        stopped_due_to_unpaid_invoice: stoppedDueToUnpaidInvoice,
        reason,
        email_targets: emailTargets,
      },
    });
    if (shouldProcessIntegrationDateNow(accessEndDate)) {
      const immediateResults = await Promise.allSettled([
        processIntegrationJobsNow(customer.id, [...(accessJobs || []), ...viewerAccessJobs].map((job) => job.id), { accessEndOnly: true }),
        processBillingNow(),
      ]);
      const immediateErrors = immediateResults
        .filter((result): result is PromiseRejectedResult => result.status === "rejected")
        .map((result) => result.reason instanceof Error ? result.reason.message : String(result.reason));
      if (immediateErrors.length) {
        setError(`Opzegging is opgeslagen, maar direct verwerken mislukte: ${immediateErrors.join(" / ")}`);
        setBusy(false);
        return;
      }
    }
    await onSaved();
  }

  return (
    <div className={nested ? "nested-modal-backdrop" : "modal-backdrop"}>
      <form className="modal" onSubmit={save}>
        <div className="modal-header">
          <h2>{hasExistingCancellation ? "Opzegging wijzigen" : "Opzegging inplannen"}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <p className="muted">{customer.billing_email}</p>
        <div className="form-grid">
          <label>Opzegdatum<input value={cancellationDate} onChange={(event) => setCancellationDate(event.target.value)} type="date" required /></label>
          <label>Einddatum toegang<input value={accessEndDate} onChange={(event) => setAccessEndDate(event.target.value)} type="date" required /></label>
          <label className="span-2">Reden / notitie<textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} /></label>
          <label className="checkbox-row span-2"><input checked={paymentArrears} onChange={(event) => setPaymentArrears(event.target.checked)} type="checkbox" /> Handmatige betaalachterstand (oude werkwijze: ActiveCampaign en Thinkific stopgezet)</label>
          <label className="checkbox-row span-2"><input checked={stoppedDueToUnpaidInvoice} onChange={(event) => setStoppedDueToUnpaidInvoice(event.target.checked)} type="checkbox" /> {STOPPED_DUE_TO_UNPAID_INVOICE_LABEL}</label>
        </div>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuleren</button>
          <button className="primary-button" disabled={busy}>{busy ? "Opslaan..." : hasExistingCancellation ? "Wijziging opslaan" : "Inplannen"}</button>
        </div>
      </form>
    </div>
  );
}

type CancellationSortKey = "completed_at" | "customer" | "tasks" | "status" | "attempts" | "scheduled_for" | "error";
type CancellationSort = { key: CancellationSortKey; direction: SortDirection };

function CancellationsPage({ canMutateMembers }: { canMutateMembers: boolean }) {
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [tab, setTab] = useState("planned");
  const [sort, setSort] = useState<CancellationSort>({ key: "scheduled_for", direction: "asc" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [undoingCancellation, setUndoingCancellation] = useState<{
    customerId: string;
    subscriptionId: string;
    customerLabel: string;
  } | null>(null);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setSort(tab === "succeeded"
      ? { key: "completed_at", direction: "desc" }
      : { key: "scheduled_for", direction: "asc" });
  }, [tab]);

  async function load() {
    const [integrationResult, billingResult] = await Promise.all([
      supabase!
        .from("integration_jobs")
        .select("*, customers(display_name,billing_email)")
        .order("scheduled_for", { ascending: true })
        .limit(MEMBER_LIST_LIMIT),
      supabase!
        .from("billing_jobs")
        .select("*, customers(display_name,billing_email)")
        .eq("job_type", "stop_recurring_billing")
        .order("scheduled_for", { ascending: true })
        .limit(MEMBER_LIST_LIMIT),
    ]);
    if (integrationResult.error || billingResult.error) {
      setError(integrationResult.error?.message || billingResult.error?.message || "Opzeggingstaken konden niet worden geladen.");
      return;
    }
    setJobs([
      ...((integrationResult.data || []) as IntegrationJob[]).filter(isAccessEndJob),
      ...((billingResult.data || []) as IntegrationJob[]).filter(isAccessEndJob),
    ]);
  }

  async function processDueJobs() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const session = await supabase!.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Geen actieve sessie gevonden.");

      const [billingOutcome, accessOutcome] = await Promise.allSettled([
        processBillingNow(),
        (async () => {
          const response = await fetch(`${publicSupabaseUrl}/functions/v1/process-cancellations?access_end_only=1`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.error || "Verwerken van toegangstaken mislukt.");
          return result;
        })(),
      ]);

      const processed =
        (billingOutcome.status === "fulfilled" ? Number(billingOutcome.value.processed || 0) : 0) +
        (accessOutcome.status === "fulfilled" ? Number(accessOutcome.value.processed || 0) : 0);
      const failures = [billingOutcome, accessOutcome]
        .filter((outcome): outcome is PromiseRejectedResult => outcome.status === "rejected")
        .map((outcome) => (outcome.reason instanceof Error ? outcome.reason.message : String(outcome.reason)));

      if (processed > 0) setMessage(`${processed} taken verwerkt, inclusief facturering.`);
      if (failures.length > 0) setError(failures.join(" "));
      await load();
    } catch (processError) {
      setError(processError instanceof Error ? processError.message : String(processError));
    } finally {
      setBusy(false);
    }
  }

  async function undoCancellation() {
    if (!undoingCancellation) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    const { error: undoError } = await supabase!.rpc("cancel_scheduled_customer_cancellation", {
      p_customer_id: undoingCancellation.customerId,
      p_subscription_id: undoingCancellation.subscriptionId,
    });
    if (undoError) {
      setError(undoError.message);
      setBusy(false);
      return;
    }
    setMessage(`Opzegging voor ${undoingCancellation.customerLabel} is geannuleerd. De klant blijft actief.`);
    setUndoingCancellation(null);
    await load();
    setBusy(false);
  }

  const now = Date.now();
  const dueJobs = jobs.filter((job) => ["planned", "retry_needed"].includes(job.status) && new Date(job.scheduled_for).getTime() <= now);
  const plannedJobs = jobs.filter((job) => ["planned", "retry_needed"].includes(job.status) && new Date(job.scheduled_for).getTime() > now);
  const succeededJobs = jobs.filter((job) => job.status === "succeeded" && hasCancellationCustomer(job));
  const failedJobs = jobs.filter((job) => job.status === "failed");
  const retryNeededJobs = jobs.filter((job) => job.status === "retry_needed");
  const dueCount = countCustomersForJobs(dueJobs);
  const plannedCount = countCustomersForJobs(plannedJobs);
  const succeededCount = countCustomersForJobs(succeededJobs);
  const failedCount = countCustomersForJobs(failedJobs);
  const retryNeededCount = countCustomersForJobs(retryNeededJobs);
  const tabCounts = { due: dueCount, planned: plannedCount, succeeded: succeededCount, failed: failedCount, retry_needed: retryNeededCount };
  const tabs = ["due", "planned", "succeeded", "failed", "retry_needed"];
  const filtered = jobs.filter((job) => {
    const due = new Date(job.scheduled_for).getTime() <= now;
    if (tab === "planned") return ["planned", "retry_needed"].includes(job.status) && !due;
    if (tab === "due") return ["planned", "retry_needed"].includes(job.status) && due;
    if (tab === "succeeded") return job.status === "succeeded" && hasCancellationCustomer(job);
    return job.status === tab;
  });
  const grouped = sortCancellationGroups(groupJobsByCustomer(filtered), sort);
  const showCompletedAt = tab === "succeeded";
  const showScheduledFirst = !showCompletedAt;
  const cancellationColumnCount = (showCompletedAt ? 7 : 6) + (canMutateMembers ? 1 : 0);

  useEffect(() => {
    if (dueCount > 0 && tab === "planned" && plannedCount === 0) setTab("due");
  }, [dueCount, plannedCount, tab]);

  return (
    <section className="page full-width-page">
      <PageHeader
        title="Opzeggingen"
        action={canMutateMembers && tab === "due"
          ? <button className="primary-button" onClick={processDueJobs} disabled={busy || dueCount === 0}><RefreshCw className={busy ? "spin" : ""} size={16} /> Nu verwerken</button>
          : undefined}
      />
      <div className="stats-grid">
        <Stat label="Later gepland" value={formatNumber(plannedCount)} />
        <Stat label="Nu aan de beurt" value={formatNumber(dueCount)} tone={dueCount > 0 ? "orange" : "green"} />
        <Stat label="Mislukt" value={formatNumber(failedCount)} tone={failedCount > 0 ? "orange" : "green"} />
      </div>
      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}
      <SegmentedTabs values={tabs} value={tab} onChange={setTab} counts={tabCounts} />
      <div className="panel table-panel">
        <table className={`data-table cancellations-table ${showCompletedAt ? "has-completed" : "has-scheduled-first"}`}>
          <thead>
            <tr>
              {showScheduledFirst && <GenericSortableHeader label="Gepland voor" sortKey="scheduled_for" sort={sort} onSort={setSort} />}
              {showCompletedAt && <GenericSortableHeader label="Uitgevoerd op" sortKey="completed_at" sort={sort} onSort={setSort} />}
              <GenericSortableHeader label="Klant" sortKey="customer" sort={sort} onSort={setSort} />
              <GenericSortableHeader label="Taken" sortKey="tasks" sort={sort} onSort={setSort} />
              <GenericSortableHeader label="Status" sortKey="status" sort={sort} onSort={setSort} />
              <GenericSortableHeader label="Pogingen" sortKey="attempts" sort={sort} onSort={setSort} />
              {showCompletedAt && <GenericSortableHeader label="Gepland voor" sortKey="scheduled_for" sort={sort} onSort={setSort} />}
              <GenericSortableHeader label="Fout" sortKey="error" sort={sort} onSort={setSort} />
              {canMutateMembers && <th>Acties</th>}
            </tr>
          </thead>
          <tbody>
            {grouped.map((group) => (
              <tr key={group.key}>
                {showScheduledFirst && <td>{formatDate(group.scheduledFor)}</td>}
                {showCompletedAt && <td>{formatDateTimeSeconds(group.completedAt)}</td>}
                <td>{group.customerLabel}</td>
                <td className="task-summary-cell">{group.taskLabels.map((task) => <span key={task}>{task}</span>)}</td>
                <td><Badge value={group.status} /></td>
                <td>{group.attempts}</td>
                {showCompletedAt && <td>{formatDate(group.scheduledFor)}</td>}
                <td>{group.error || "-"}</td>
                {canMutateMembers && (
                  <td>
                    {group.canUndo
                      ? <button className="secondary-button compact" onClick={() => setUndoingCancellation({
                          customerId: group.customerId,
                          subscriptionId: group.subscriptionId,
                          customerLabel: group.customerLabel,
                        })}><X size={14} /> Annuleren</button>
                      : "-"}
                  </td>
                )}
              </tr>
            ))}
            {grouped.length === 0 && <tr><td colSpan={cancellationColumnCount} className="empty">Geen klanten met opzeggingstaken in deze status.</td></tr>}
          </tbody>
        </table>
      </div>
      {undoingCancellation && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h2>Opzegging annuleren</h2>
              <button type="button" className="icon-button" onClick={() => setUndoingCancellation(null)} aria-label="Sluiten"><X size={18} /></button>
            </div>
            <p>Weet je zeker dat je de geplande opzegging voor <strong>{undoingCancellation.customerLabel}</strong> wilt annuleren?</p>
            <p className="muted">De opzegdatum en einddatum verdwijnen. De klant blijft actief en de geplande ActiveCampaign-, Thinkific- en factuurtaak worden niet uitgevoerd.</p>
            <div className="modal-actions">
              <button type="button" className="secondary-button" onClick={() => setUndoingCancellation(null)}>Terug</button>
              <button type="button" className="primary-button" onClick={undoCancellation} disabled={busy}>{busy ? "Annuleren..." : "Opzegging annuleren"}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ViewersPage({ canMutateMembers }: { canMutateMembers: boolean }) {
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
    supabase!.from("customers").select("id,display_name,first_name,last_name,company_name,billing_email,status,created_at").in("status", ["active", "free", "barter"]).order("display_name").limit(MEMBER_LIST_LIMIT).then(({ data }) => setCustomers((data || []) as Customer[]));
  }, []);

  async function load() {
    const { data } = await supabase!
      .from("viewers")
      .select("*, customers(display_name,billing_email)")
      .order("added_at", { ascending: false })
      .limit(MEMBER_LIST_LIMIT);
    setViewers((data || []) as Viewer[]);
  }

  async function removeViewer(viewer: Viewer) {
    setMessage(null);
    setError(null);
    const { error: viewerError } = await supabase!.from("viewers").update({ status: "removed", removed_at: new Date().toISOString() }).eq("id", viewer.id);
    if (viewerError) {
      setError(viewerError.message);
      return;
    }
    await cancelPendingViewerSyncJobs(viewer.id);
    const { data: job, error: jobError } = await supabase!.from("integration_jobs").insert({
      customer_id: viewer.customer_id,
      viewer_id: viewer.id,
      provider: "activecampaign",
      job_type: "remove_viewer_access",
      scheduled_for: new Date().toISOString(),
      status: "planned",
    }).select("id").single();
    if (jobError || !job) {
      setError(jobError?.message || "Uitschrijftaak kon niet worden aangemaakt.");
      return;
    }
    await supabase!.from("audit_events").insert({
      action: "viewer_removed",
      entity_type: "viewer",
      entity_id: viewer.id,
      customer_id: viewer.customer_id,
      success: true,
      summary: `Meekijker verwijderd: ${viewer.email}`,
      after_data: {
        viewer_email: viewer.email,
        customer_name: viewer.customers?.display_name || null,
        customer_email: viewer.customers?.billing_email || null,
        job_id: job.id,
      },
    });
    try {
      await processIntegrationJobNow(viewer.customer_id, job.id, { accessEndOnly: true });
      setMessage("Meekijker verwijderd en direct uitgeschreven.");
    } catch (processError) {
      setError(`Meekijker is verwijderd, maar direct uitschrijven mislukte: ${processError instanceof Error ? processError.message : String(processError)}`);
    }
    await load();
  }

  const filtered = viewers.filter((viewer) => [viewer.email, viewer.customers?.display_name, viewer.customers?.billing_email].filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase()));

  return (
    <section className="page full-width-page">
      <PageHeader title="Meekijkers" action={canMutateMembers ? <button className="primary-button" onClick={() => setAddOpen(true)}><Eye size={16} /> Meekijker toevoegen</button> : undefined} />
      <div className="toolbar"><SearchBox value={query} onChange={setQuery} /></div>
      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}
      <div className="table-shell">
        <table className="data-table">
          <thead><tr><th>Meekijker e-mail</th><th>Hoofdklant</th><th>Status</th><th>Toegevoegd</th><th>Verwijderd</th><th>Acties</th></tr></thead>
          <tbody>
            {filtered.map((viewer) => (
              <tr key={viewer.id}>
                <td>{viewer.email}</td>
                <td>
                  <div>{viewer.customers?.display_name || viewer.customers?.billing_email || "-"}</div>
                  {viewer.customers?.billing_email && <div className="cell-subtext">{viewer.customers.billing_email}</div>}
                </td>
                <td><Badge value={viewer.status} /></td>
                <td>{formatDateTimeSeconds(viewer.added_at)}</td>
                <td>{formatDateTimeSeconds(viewer.removed_at)}</td>
                <td>
                  {canMutateMembers && viewer.status === "active" && (
                    <button className="icon-button" onClick={() => removeViewer(viewer)} title="Meekijker verwijderen">
                      <Trash2 size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="empty">Geen meekijkers gevonden.</td></tr>}
          </tbody>
        </table>
      </div>
      {addOpen && (
        <AddViewerModal
          customers={customers}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            setMessage("Meekijker toegevoegd en direct gesynchroniseerd.");
            load();
          }}
        />
      )}
    </section>
  );
}

function AddViewerModal({ customers, onClose, onSaved }: { customers: Customer[]; onClose: () => void; onSaved: () => void }) {
  const [customerId, setCustomerId] = useState(customers[0]?.id || "");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [allCustomers, setAllCustomers] = useState<Customer[]>(customers);
  const [allViewers, setAllViewers] = useState<Viewer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const viewerConflicts = useMemo(
    () => findViewerAddConflicts(email, allCustomers, allViewers, customerId),
    [email, allCustomers, allViewers, customerId],
  );

  useEffect(() => {
    if (!customerId && customers[0]?.id) setCustomerId(customers[0].id);
  }, [customerId, customers]);

  useEffect(() => {
    async function loadConflictData() {
      const [customerResult, viewerResult] = await Promise.all([
        supabase!.from("customers").select("*, customer_subscriptions(*, plans(*))").limit(MEMBER_LIST_LIMIT),
        supabase!.from("viewers").select("*, customers(display_name,billing_email)").limit(MEMBER_LIST_LIMIT),
      ]);
      setAllCustomers((customerResult.data || customers) as Customer[]);
      setAllViewers((viewerResult.data || []) as Viewer[]);
    }
    loadConflictData();
  }, [customers]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!customerId) {
      setError("Kies een hoofdklant.");
      return;
    }
    setBusy(true);
    setError(null);
    if (viewerConflicts.activeCustomer) {
      setError(`Dit e-mailadres is al actieve klant bij ${customerLabel(viewerConflicts.activeCustomer.customer)}. Iemand kan niet tegelijk actieve klant en meekijker zijn.`);
      setBusy(false);
      return;
    }
    if (viewerConflicts.activeViewer) {
      setError(`Dit e-mailadres is al actieve meekijker bij ${viewerConflicts.activeViewer.customers?.display_name || viewerConflicts.activeViewer.customers?.billing_email || "een andere hoofdklant"}. Een meekijker kan maar bij één hoofdklant actief zijn.`);
      setBusy(false);
      return;
    }
    const { data: viewer, error: viewerError } = await supabase!.from("viewers").insert({
      customer_id: customerId,
      email: email.trim(),
      status: "active",
      notes: notes || null,
    }).select().single();
    if (viewerError || !viewer) {
      setError(viewerError?.message || "Meekijker kon niet worden opgeslagen.");
      setBusy(false);
      return;
    }
    const { data: job, error: jobError } = await supabase!.from("integration_jobs").insert({
      customer_id: customerId,
      viewer_id: viewer.id,
      provider: "activecampaign",
      job_type: "sync_viewer_access",
      scheduled_for: new Date().toISOString(),
      status: "planned",
    }).select("id").single();
    if (jobError || !job) {
      setError(jobError?.message || "Sync-taak kon niet worden aangemaakt.");
      setBusy(false);
      return;
    }
    const selectedCustomer = customers.find((customer) => customer.id === customerId);
    await supabase!.from("audit_events").insert({
      action: "viewer_added",
      entity_type: "viewer",
      entity_id: viewer.id,
      customer_id: customerId,
      success: true,
      summary: `Meekijker toegevoegd: ${email.trim()}`,
      after_data: {
        viewer_email: email.trim(),
        customer_name: selectedCustomer?.display_name || null,
        customer_email: selectedCustomer?.billing_email || null,
        job_id: job.id,
      },
    });
    try {
      await processIntegrationJobNow(customerId, job.id);
    } catch (processError) {
      setError(`Meekijker is opgeslagen, maar direct synchroniseren mislukte: ${processError instanceof Error ? processError.message : String(processError)}`);
      setBusy(false);
      return;
    }
    onSaved();
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={save}>
        <div className="modal-header">
          <h2>Meekijker toevoegen</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="form-grid">
          <label className="span-2">Hoofdklant<select value={customerId} onChange={(event) => setCustomerId(event.target.value)} required>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.display_name || customer.billing_email}</option>)}</select></label>
          <label className="span-2">Meekijker e-mail<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>
          <div className="span-2"><ViewerAddConflictPanel conflicts={viewerConflicts} /></div>
          <label className="span-2">Notities<textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} /></label>
        </div>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuleren</button>
          <button className="primary-button" disabled={busy}>{busy ? "Opslaan..." : "Opslaan"}</button>
        </div>
      </form>
    </div>
  );
}

function PlansPage({ canViewFinancials, canManagePlans }: { canViewFinancials: boolean; canManagePlans: boolean }) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [metrics, setMetrics] = useState<ProductMetric[]>([]);
  const [sort, setSort] = useState<PlanSort>({ key: "revenue", direction: "desc" });
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, [canViewFinancials]);

  async function loadPlans() {
    const [plansResult, metricsResult] = await Promise.all([
      supabase!.from("plans").select("*").order("sort_order"),
      supabase!.from(canViewFinancials ? "financial_product_metrics" : "operational_product_metrics").select("*").order("plan_label"),
    ]);
    if (plansResult.error) setError(plansResult.error.message);
    setPlans((plansResult.data || []) as Plan[]);
    setMetrics((metricsResult.data || []) as ProductMetric[]);
  }

  async function togglePlan(plan: Plan) {
    setMessage(null);
    setError(null);
    const { error } = await supabase!.from("plans").update({ active: !plan.active }).eq("id", plan.id);
    if (error) {
      setError(error.message);
      return;
    }
    await supabase!.from("audit_events").insert({
      action: plan.active ? "plan_deactivated" : "plan_activated",
      entity_type: "plan",
      entity_id: plan.id,
      success: true,
      summary: `${plan.display_label} ${plan.active ? "gedeactiveerd" : "geactiveerd"}`,
    });
    setMessage(plan.active ? "Plan gedeactiveerd." : "Plan geactiveerd.");
    await loadPlans();
  }

  const metricsByCode = useMemo(() => new Map(metrics.map((metric) => [metric.plan_code, metric])), [metrics]);
  const sortedPlans = useMemo(() => sortPlans(plans, metricsByCode, sort), [plans, metricsByCode, sort]);

  return (
    <section className="page full-width-page">
      <PageHeader title="Abonnementen" action={canManagePlans ? <button className="primary-button" onClick={() => setAddOpen(true)}><FileSpreadsheet size={16} /> Plan toevoegen</button> : undefined} />
      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}
      <div className="panel table-panel">
        <table className="data-table plans-table">
          <thead><tr>
            <GenericSortableHeader label="Product" sortKey="product" sort={sort} onSort={setSort} />
            <GenericSortableHeader label="Code" sortKey="code" sort={sort} onSort={setSort} />
            <GenericSortableHeader label="Termijn" sortKey="term" sort={sort} onSort={setSort} />
            <GenericSortableHeader label="Betaaltype" sortKey="payment_variant" sort={sort} onSort={setSort} />
            {canViewFinancials && <GenericSortableHeader label="Prijs incl." sortKey="price_incl" sort={sort} onSort={setSort} />}
            {canViewFinancials && <GenericSortableHeader label="Btw default" sortKey="vat" sort={sort} onSort={setSort} />}
            {canViewFinancials && <GenericSortableHeader label="Prijs excl." sortKey="price_excl" sort={sort} onSort={setSort} />}
            {canViewFinancials && <GenericSortableHeader label="Omzet" sortKey="revenue" sort={sort} onSort={setSort} />}
            <GenericSortableHeader label="Actief" sortKey="active" sort={sort} onSort={setSort} />
            <GenericSortableHeader label="Actieve leden" sortKey="members" sort={sort} onSort={setSort} />
            {canViewFinancials && <GenericSortableHeader label="MRR excl." sortKey="mrr_excl" sort={sort} onSort={setSort} />}
            {canViewFinancials && <GenericSortableHeader label="MRR incl." sortKey="mrr_incl" sort={sort} onSort={setSort} />}
            {canManagePlans && <th>Acties</th>}
          </tr></thead>
          <tbody>
            {sortedPlans.map((plan) => {
              const metric = metricsByCode.get(plan.code);
              return <tr key={plan.id}>
                <td>{plan.display_label}</td>
                <td>{plan.code}</td>
                <td>{plan.billing_period}</td>
                <td>{plan.payment_variant}</td>
                {canViewFinancials && <td>{formatCurrency(plan.price_incl_vat)}</td>}
                {canViewFinancials && <td>{formatPercent(Number(plan.vat_rate_default) * 100)}</td>}
                {canViewFinancials && <td>{formatCurrency(plan.price_excl_vat_default)}</td>}
                {canViewFinancials && <td><Badge value={plan.counts_toward_revenue ? "ja" : "nee"} tone={plan.counts_toward_revenue ? "success" : "danger"} /></td>}
                <td><Badge value={plan.active ? "active" : "inactive"} /></td>
                <td>{formatNumber(metric?.active_members)}</td>
                {canViewFinancials && <td>{formatCurrency(metric?.mrr_excl_vat)}</td>}
                {canViewFinancials && <td>{formatCurrency(metric?.mrr_incl_vat)}</td>}
                {canManagePlans && <td>
                  <div className="row-actions">
                    <button className="icon-button" onClick={() => setEditingPlan(plan)} title="Plan wijzigen"><Edit3 size={16} /></button>
                    <button className="secondary-button compact" onClick={() => togglePlan(plan)}>{plan.active ? "Deactiveer" : "Activeer"}</button>
                  </div>
                </td>}
              </tr>;
            })}
            {plans.length === 0 && <tr><td colSpan={(canViewFinancials ? 12 : 6) + (canManagePlans ? 1 : 0)} className="empty">Nog geen plannen.</td></tr>}
          </tbody>
        </table>
      </div>
      {addOpen && <PlanModal onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); setMessage("Plan toegevoegd."); await loadPlans(); }} />}
      {editingPlan && <PlanModal plan={editingPlan} onClose={() => setEditingPlan(null)} onSaved={async () => { setEditingPlan(null); setMessage("Plan bijgewerkt."); await loadPlans(); }} />}
    </section>
  );
}

type PlanSortKey = "product" | "code" | "term" | "payment_variant" | "price_incl" | "vat" | "price_excl" | "revenue" | "active" | "members" | "mrr_excl" | "mrr_incl";
type PlanSort = { key: PlanSortKey; direction: SortDirection };

function planSortValue(plan: Plan, metric: ProductMetric | undefined, key: PlanSortKey): string | number | null {
  switch (key) {
    case "product": return plan.display_label;
    case "code": return plan.code;
    case "term": return plan.billing_period;
    case "payment_variant": return plan.payment_variant;
    case "price_incl": return Number(plan.price_incl_vat);
    case "vat": return Number(plan.vat_rate_default);
    case "price_excl": return Number(plan.price_excl_vat_default);
    case "revenue": return plan.counts_toward_revenue ? 1 : 0;
    case "active": return plan.active ? 1 : 0;
    case "members": return metric?.active_members ?? null;
    case "mrr_excl": return metric?.mrr_excl_vat ?? null;
    case "mrr_incl": return metric?.mrr_incl_vat ?? null;
  }
}

function sortPlans(plans: Plan[], metricsByCode: Map<string, ProductMetric>, sort: PlanSort) {
  return [...plans].sort((a, b) => {
    const aValue = planSortValue(a, metricsByCode.get(a.code), sort.key);
    const bValue = planSortValue(b, metricsByCode.get(b.code), sort.key);
    if (aValue === null && bValue !== null) return 1;
    if (aValue !== null && bValue === null) return -1;
    if (aValue === null && bValue === null) return 0;
    const comparison = typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue), "nl", { numeric: true, sensitivity: "base" });
    return sort.direction === "asc" ? comparison : -comparison;
  });
}

function PlanModal({ plan, onClose, onSaved }: { plan?: Plan; onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState(plan?.code || "");
  const [label, setLabel] = useState(plan?.display_label || "");
  const [billingPeriod, setBillingPeriod] = useState(plan?.billing_period || "month");
  const [paymentVariant, setPaymentVariant] = useState(plan?.payment_variant || "eur");
  const [priceIncl, setPriceIncl] = useState(String(Number(plan?.price_incl_vat ?? 0)));
  const [vatRate, setVatRate] = useState(String(Number(plan?.vat_rate_default ?? 0.21)));
  const [countsTowardRevenue, setCountsTowardRevenue] = useState(plan?.counts_toward_revenue ?? true);
  const [active, setActive] = useState(plan?.active ?? true);
  const [sortOrder, setSortOrder] = useState(String(Number(plan?.sort_order ?? 100)));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      code: code.trim(),
      name: label.trim(),
      display_label: label.trim(),
      billing_period: billingPeriod,
      payment_variant: paymentVariant,
      price_incl_vat: Number(priceIncl || 0),
      vat_rate_default: Number(vatRate || 0),
      counts_toward_revenue: countsTowardRevenue,
      active,
      sort_order: Number(sortOrder || 100),
    };
    const result = plan
      ? await supabase!.from("plans").update(payload).eq("id", plan.id)
      : await supabase!.from("plans").insert(payload);
    if (result.error) {
      setError(result.error.message);
      setBusy(false);
      return;
    }
    await supabase!.from("audit_events").insert({
      action: plan ? "plan_updated" : "plan_created",
      entity_type: "plan",
      entity_id: plan?.id || null,
      success: true,
      summary: `${plan ? "Plan gewijzigd" : "Plan toegevoegd"}: ${payload.display_label}`,
      after_data: payload,
    });
    onSaved();
  }

  return (
    <div className="modal-backdrop">
      <form className="modal wide-modal" onSubmit={save}>
        <div className="modal-header">
          <h2>{plan ? "Plan wijzigen" : "Plan toevoegen"}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="form-grid">
          <label>Code<input value={code} onChange={(event) => setCode(event.target.value)} required disabled={Boolean(plan)} /></label>
          <label>Label<input value={label} onChange={(event) => setLabel(event.target.value)} required /></label>
          <label>Termijn<select value={billingPeriod} onChange={(event) => setBillingPeriod(event.target.value)}><option value="month">month</option><option value="quarter">quarter</option><option value="year">year</option><option value="free">free</option><option value="barter">barter</option></select></label>
          <label>Betaaltype<select value={paymentVariant} onChange={(event) => setPaymentVariant(event.target.value)}><option value="eur">eur</option><option value="btc">btc</option><option value="free">free</option><option value="barter">barter</option></select></label>
          <label>Prijs incl. btw<input value={priceIncl} onChange={(event) => setPriceIncl(event.target.value)} type="number" step="0.01" min="0" /></label>
          <label>Btw-rate default<input value={vatRate} onChange={(event) => setVatRate(event.target.value)} type="number" step="0.0001" min="0" /></label>
          <label>Sorteervolgorde<input value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} type="number" step="1" /></label>
          <label className="checkbox-row"><input checked={countsTowardRevenue} onChange={(event) => setCountsTowardRevenue(event.target.checked)} type="checkbox" /> Telt mee voor omzet</label>
          <label className="checkbox-row"><input checked={active} onChange={(event) => setActive(event.target.checked)} type="checkbox" /> Actief</label>
        </div>
        {error && <div className="banner error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuleren</button>
          <button className="primary-button" disabled={busy}>{busy ? "Opslaan..." : "Opslaan"}</button>
        </div>
      </form>
    </div>
  );
}

function ExportsPage({ canViewFinancials, isSuperAdmin }: { canViewFinancials: boolean; isSuperAdmin: boolean }) {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (canViewFinancials) loadExports();
  }, [canViewFinancials]);

  async function loadExports(append = false) {
    setLoadingRows(true);
    try {
      const offset = append ? rows.length : 0;
      const { data, error: loadError } = await supabase!.from("exports").select("*").order("created_at", { ascending: false }).order("id", { ascending: false }).range(offset, offset + 99);
      if (loadError) throw loadError;
      setRows((previous) => append ? [...previous, ...(data || [])] : (data || []));
      setHasMore((data || []).length === 100);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setLoadingRows(false);
    }
  }

  async function downloadExport() {
    setBusy(true);
    setMessage(null);
    setError(null);
    const session = await supabase!.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) {
      setError("Geen actieve sessie gevonden.");
      setBusy(false);
      return;
    }
    const url = `${publicSupabaseUrl}/functions/v1/export-members`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) {
      setError(await response.text());
      setBusy(false);
      return;
    }
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filenameFromDisposition(response.headers.get("Content-Disposition")) || "BH_leden_export.xls";
    link.click();
    URL.revokeObjectURL(link.href);
    await loadExports();
    setMessage("Export opgeslagen. Je kunt hetzelfde bestand later opnieuw downloaden via Acties.");
    setBusy(false);
  }

  async function downloadStoredExport(row: Record<string, unknown>) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const session = await supabase!.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Geen actieve sessie gevonden.");
      const response = await fetch(`${publicSupabaseUrl}/functions/v1/export-members?id=${encodeURIComponent(String(row.id))}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(await response.text());
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filenameFromDisposition(response.headers.get("Content-Disposition")) || String(row.file_path || `brede-horizon-export-${row.id}.xls`).split("/").pop() || "brede-horizon-export.xls";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (storedExportError) {
      setError(storedExportError instanceof Error ? storedExportError.message : String(storedExportError));
    } finally {
      setBusy(false);
    }
  }

  async function sendWeeklySummary() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const session = await supabase!.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Geen actieve sessie gevonden.");
      const response = await fetch(`${publicSupabaseUrl}/functions/v1/weekly-summary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Weekmail verzenden mislukt.");
      setMessage(`Weekmail verzonden. Exportrecord: ${payload.export?.id || "aangemaakt"}.`);
      await loadExports();
    } catch (summaryError) {
      setError(summaryError instanceof Error ? summaryError.message : String(summaryError));
    } finally {
      setBusy(false);
    }
  }

  if (!canViewFinancials) return <section className="page"><PageHeader title="Klanten export" /><p className="page-description">Je hebt geen financiële rechten om klantenexports te bekijken of downloaden.</p></section>;

  const exportLabels: Record<string, string> = { members_full_financial: "Handmatig", daily_members_snapshot_financial: "Dagelijks", weekly_members_snapshot_financial: "Wekelijks", monthly_members_snapshot_financial: "Maandelijks", members_operational: "Operationeel (oud)" };
  const columnLabels: Record<string, string> = { file_path: "Bestand", export_type: "Type", record_count: "Records", status: "Status", created_at: "Aangemaakt", sent_at: "Verzonden" };
  const statusLabels: Record<string, string> = { succeeded: "Geslaagd", failed: "Mislukt", planned: "In wachtrij", running: "Bezig" };
  const columns = ["file_path", "export_type", "record_count", "status", "created_at", "sent_at"];
  return (
    <section className="page">
      <PageHeader
        title="Klanten export"
        action={(
          <div className="row-actions">
            {isSuperAdmin && <button className="secondary-button" onClick={sendWeeklySummary} disabled={busy}><MailCheck size={16} /> Weekmail sturen</button>}
            {canViewFinancials && <button className="primary-button" onClick={downloadExport} disabled={busy}><Download size={16} /> Klanten export</button>}
          </div>
        )}
      />
      <p className="page-description">De Excel bevat vier tabbladen: Actieve leden, Alle leden, Niet actieve leden en Historische aanmeldingen. Actieve leden zijn inclusief gratis toegang en ruil. Dit is één volledig financieel overzicht, inclusief abonnementsprijzen, facturering en btw. Alleen gebruikers met toegang tot klantenexports hebben toegang.</p>
      <p className="page-description">De dagelijkse klantenexport wordt om 08.00 uur Nederlandse tijd opgeslagen, zonder e-mail. Automatische exports worden 400 dagen bewaard. Iedere maandag om 08.00 uur gaat de weekmail naar de ingestelde beheerontvangers. De bestandsnaam bevat de Nederlandse datum en een volgnummer: BH_leden_export_2026-09-24_v1.xls.</p>
      <p className="page-description">Op de eerste van iedere maand om 08.00 uur Nederlandse tijd gaat een backupmail naar contact@bredehorizon.com, met de instructie het bestand in Proton Drive op te slaan. De maandmail verwijst naar deze pagina; log in om het bestand te downloaden.</p>
      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}
      <div className="panel table-panel exports-table-panel">
        <table className="data-table exports-table">
          <colgroup><col className="export-file-col" /><col className="export-type-col" /><col className="export-count-col" /><col className="export-status-col" /><col className="export-date-col" /><col className="export-date-col" /><col className="export-action-col" /></colgroup>
          <thead><tr>{columns.map((column) => <th key={column}>{columnLabels[column]}</th>)}<th>Acties</th></tr></thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={String(row.id || index)}>
                {columns.map((column) => <td key={column}>
                  {column === "file_path" ? <div title={String(row.file_path || "")}>{String(row.file_path || "-").split("/").pop()}</div>
                    : column === "export_type" ? exportLabels[String(row.export_type)] || String(row.export_type)
                    : column === "created_at" || column === "sent_at" ? <ExportTimestamp value={row[column]} />
                    : column === "status" ? <>{statusLabels[String(row.status)] || String(row.status || "-")}{row.last_error ? <details className="export-error"><summary>Foutmelding</summary>{String(row.last_error)}</details> : null}</>
                    : String(row[column] ?? "-")}
                </td>)}
                <td>
                  {row.file_path && canViewFinancials ? (
                    <button className="icon-button" title="Download opgeslagen export" onClick={() => downloadStoredExport(row)} disabled={busy}>
                      <Download size={16} />
                    </button>
                  ) : "-"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="empty" colSpan={columns.length + 1}>{loadingRows ? "Exports laden…" : "Nog geen exports."}</td></tr>}
          </tbody>
        </table>
      </div>
      {hasMore && <button className="secondary-button" onClick={() => loadExports(true)} disabled={loadingRows || busy}>{loadingRows ? "Laden…" : "Oudere exports laden"}</button>}
    </section>
  );
}

function ExportTimestamp({ value }: { value: unknown }) {
  if (!value) return <>-</>;
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return <>-</>;
  const day = new Intl.DateTimeFormat("nl-NL", { timeZone: "Europe/Amsterdam", day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
  const time = new Intl.DateTimeFormat("nl-NL", { timeZone: "Europe/Amsterdam", hour: "2-digit", minute: "2-digit" }).format(date);
  return <time dateTime={date.toISOString()} title={`${day} ${time} (Nederlandse tijd)`}>{day}<span>{time}</span></time>;
}

function BackupsPage() {
  const [rows, setRows] = useState<Backup[]>([]);
  const [busyType, setBusyType] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [watchedBackupId, setWatchedBackupId] = useState<string | null>(null);

  useEffect(() => {
    const watched = rows.find((row) => row.id === watchedBackupId);
    if (!watched || watched.status === "running") return;
    if (watched.status === "succeeded") {
      setMessage(`${backupTypeLabel(watched.backup_type)} is opgeslagen en kan worden gedownload.`);
    } else {
      setMessage(null);
      setError(watched.error || "Backup maken mislukt. Je kunt opnieuw proberen.");
    }
    setWatchedBackupId(null);
  }, [rows, watchedBackupId]);

  useEffect(() => {
    void loadBackups();
  }, []);

  useEffect(() => {
    if (!rows.some((row) => row.status === "running")) return undefined;
    const interval = window.setInterval(() => {
      void loadBackups();
    }, 2500);
    return () => window.clearInterval(interval);
  }, [rows]);

  async function loadBackups() {
    if (!supabase) return;
    const { data, error: loadError } = await supabase.from("backups").select("*").order("created_at", { ascending: false }).limit(100);
    if (loadError) { setError(loadError.message); return; }
    const serverRows = (data || []) as Backup[];
    setRows((currentRows) => {
      const pendingRows = currentRows.filter((row) => row.id.startsWith("pending-") && row.status === "running");
      const stillPending = pendingRows.filter((pending) => !serverRows.some((serverRow) => serverRow.backup_type === pending.backup_type && serverRow.created_at >= pending.created_at));
      return [...stillPending, ...serverRows];
    });
  }

  async function createBackup(type: Backup["backup_type"]) {
    if (!supabase || busyType) return;
    setBusyType(type);
    setMessage(null);
    setError(null);
    const optimisticBackup: Backup = {
      id: `pending-${type}-${Date.now()}`,
      backup_type: type,
      status: "running",
      storage_provider: "supabase_storage",
      storage_bucket: null,
      file_path: null,
      file_name: null,
      file_size_bytes: null,
      sha256: null,
      created_by: null,
      created_at: new Date().toISOString(),
      completed_at: null,
      error: null,
      metadata: { optimistic: true },
    };
    setRows((currentRows) => [optimisticBackup, ...currentRows]);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Geen actieve sessie gevonden.");
      const response = await fetch(`${publicSupabaseUrl}/functions/v1/backup-snapshot`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ type }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "Backup maken mislukt.");
      if (payload.queued) {
        setWatchedBackupId(payload.backup.id);
        setMessage(`${backupTypeLabel(type)} ${payload.existing ? "wordt al verwerkt" : "is gestart"}. De voortgang verschijnt hieronder. Je kunt deze pagina sluiten; de backup gaat door.`);
      } else {
        setMessage(`${backupTypeLabel(type)} is opgeslagen. SHA-256: ${String(payload.backup?.sha256 || "onbekend").slice(0, 16)}…`);
      }
      await loadBackups();
    } catch (backupError) {
      setError(backupError instanceof Error ? backupError.message : String(backupError));
      await loadBackups();
    } finally {
      setRows((currentRows) => currentRows.filter((row) => row.id !== optimisticBackup.id));
      setBusyType(null);
    }
  }

  async function downloadBackup(backup: Backup) {
    if (!supabase || !backup.file_path) return;
    setBusyType(`download:${backup.id}`);
    setError(null);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Geen actieve sessie gevonden.");
      const response = await fetch(`${publicSupabaseUrl}/functions/v1/backup-snapshot?id=${encodeURIComponent(backup.id)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(await response.text());
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filenameFromDisposition(response.headers.get("Content-Disposition")) || backup.file_name || "brede-horizon-backup";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : String(downloadError));
    } finally {
      setBusyType(null);
    }
  }

  const newestSuccess = rows.find((row) => row.status === "succeeded");
  function renderBackupButton(type: Backup["backup_type"], label: string, Icon: typeof Database, className: string) {
    const isBusy = busyType === type || rows.some((row) => row.backup_type === type && row.status === "running");
    const iconSize = className.includes("compact") ? 15 : 16;
    return (
      <button className={className} onClick={() => createBackup(type)} disabled={Boolean(busyType) || isBusy} aria-busy={isBusy}>
        {isBusy ? <RefreshCw className="spin" size={iconSize} /> : <Icon size={iconSize} />}
        <span>{isBusy ? "Bezig…" : label}</span>
      </button>
    );
  }
  return (
    <section className="page">
      <PageHeader
        title="Backups"
        action={(
          <div className="row-actions backup-actions">
            {renderBackupButton("database_snapshot", "Database", Database, "secondary-button")}
            {renderBackupButton("sql_snapshot", "SQL-data", FileText, "secondary-button")}
            {renderBackupButton("activecampaign_lists_snapshot", "AC-lijsten Extreem", Mail, "secondary-button")}
            {renderBackupButton("activecampaign_account_snapshot", "ActiveCampaign Stijn", Mail, "secondary-button")}
            {renderBackupButton("code_snapshot", "Codebackup", FileArchive, "secondary-button")}
            {renderBackupButton("full_snapshot", "Volledige snapshot", Archive, "primary-button")}
          </div>
        )}
      />
      <p className="page-description">Alleen zichtbaar voor superadmins. Iedere dag wordt automatisch een volledige ZIP-herstelset gemaakt met database-JSON, SQL-data, storage-inventaris en GitHub-code. Iedere maandag wordt daarnaast een read-only backup van de Brede Horizon- en meekijkerslijst in ActiveCampaign Extreem gemaakt. Op de eerste dag van iedere maand wordt het volledige ActiveCampaign-account van Stijn als JSON opgeslagen, met alle lijsten en alleen actieve contacten, en per e-mail gemeld met de herinnering om in te loggen en het bestand te downloaden. De dagelijkse en Stijn-accountbackups blijven 60 dagen staan; de Extreem-lijsten 8 weken.</p>
      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}

      <div className="backup-overview-grid">
        <section className="panel backup-intro-panel">
          <div className="panel-title">Wat wordt geback-upt?</div>
          <div className="backup-check-list">
            <div><Badge value="database" /><span>Database JSON met alle bekende tabellen, rijen en schema-/constraintinventaris.</span></div>
            <div><Badge value="sql" /><span>Een data-only SQL-bestand dat online in Supabase wordt opgebouwd; de GitHub-migraties leveren het schema.</span></div>
            <div><Badge value="storage" /><span>De volledige ZIP bevat ook de storage-inventaris van de private buckets.</span></div>
            <div><Badge value="code" /><span>De volledige ZIP bevat de echte GitHub-codebackup; hiervoor moeten de GitHub secrets zijn ingesteld.</span></div>
            <div><Badge value="activecampaign" /><span>Wekelijks worden de contacten en lijstlidmaatschappen van de twee Brede Horizon-lijsten in ActiveCampaign Extreem read-only opgeslagen. Maandelijks wordt daarnaast het volledige ActiveCampaign-account van Stijn opgeslagen met alle lijsten en alleen actieve contacten; deze accountbackup is ook handmatig te starten.</span></div>
            <div><Badge value="secrets" /><span>API-sleutels en Auth-gebruikers worden bewust niet in bestanden gezet; die moeten apart in de platformen worden beheerd.</span></div>
          </div>
        </section>
        <section className="panel backup-status-panel">
          <div className="panel-title">Opslag en herstel</div>
          <div className="backup-status-row"><Cloud size={17} /><span>Supabase Storage</span><Badge value="configured" /></div>
          <div className="backup-status-row"><HardDrive size={17} /><span>Cloudflare R2</span><Badge value="not configured" /></div>
          <p className="muted">Cloudflare Pages is hosting, geen bestandsarchief. R2 kan later als tweede opslaglaag worden toegevoegd zodra de Cloudflare-token R2-rechten heeft.</p>
          {newestSuccess && <p className="muted">Laatste geslaagde backup: {formatDateTime(newestSuccess.completed_at || newestSuccess.created_at)}. Bewaartermijn: 60 dagen voor standaardbackups, 8 weken voor ActiveCampaign-lijsten.</p>}
        </section>
      </div>

      <section className="panel backup-manual-panel">
        <div className="panel-title-row">
          <div><div className="panel-title">Handmatige backups</div><div className="muted">Een volledige snapshot bevat voortaan database JSON, SQL-data en de GitHub-codebackup in één ZIP. Losse bestanden blijven ook afzonderlijk beschikbaar.</div></div>
          <div className="row-actions">
            {renderBackupButton("sql_snapshot", "SQL-data", FileText, "secondary-button compact")}
            {renderBackupButton("activecampaign_lists_snapshot", "AC-lijsten Extreem", Mail, "secondary-button compact")}
            {renderBackupButton("activecampaign_account_snapshot", "ActiveCampaign Stijn", Mail, "secondary-button compact")}
            {renderBackupButton("storage_inventory", "Storage-inventaris", HardDrive, "secondary-button compact")}
            {renderBackupButton("code_snapshot", "Codebackup", FileArchive, "secondary-button compact")}
          </div>
        </div>
      </section>

      <div className="panel table-panel backups-table-panel">
        <table className="data-table backups-table">
          <thead><tr><th>Type</th><th>Status</th><th>Opslag</th><th>Aangemaakt</th><th>Bestand</th><th>Grootte</th><th>SHA-256</th><th>Acties</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={row.status === "running" ? "backup-row-running" : undefined}>
                <td>{backupTypeLabel(row.backup_type)}</td>
                <td><Badge value={backupStatusLabel(row.status)} />{row.status === "running" && row.metadata.resumable === true && <div className="muted" role="status">{stijnBackupProgress(row.metadata)}</div>}</td>
                <td>{row.storage_provider === "cloudflare_r2" ? "Cloudflare R2" : "Supabase Storage"}</td>
                <td>{formatDateTime(row.created_at)}</td>
                <td>{row.file_name || row.error || "-"}</td>
                <td>{row.file_size_bytes ? formatBytes(row.file_size_bytes) : "-"}</td>
                <td><code title={row.sha256 || undefined}>{row.sha256 ? `${row.sha256.slice(0, 12)}…` : "-"}</code></td>
                <td>{row.status === "succeeded" && row.file_path ? <button className="icon-button" title="Backup downloaden" onClick={() => downloadBackup(row)} disabled={Boolean(busyType)} aria-busy={busyType === `download:${row.id}`}>{busyType === `download:${row.id}` ? <RefreshCw className="spin" size={16} /> : <Download size={16} />}</button> : "-"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="empty" colSpan={8}>Nog geen backups.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function backupTypeLabel(type: Backup["backup_type"]) {
  return ({ database_snapshot: "Database JSON", sql_snapshot: "SQL-data", storage_inventory: "Storage-inventaris", code_snapshot: "Code", full_snapshot: "Volledig (ZIP)", activecampaign_lists_snapshot: "ActiveCampaign-lijsten (Extreem)", activecampaign_stijn_lists_snapshot: "ActiveCampaign-lijsten (Stijn, oud)", activecampaign_account_snapshot: "ActiveCampaign Stijn (volledig)" })[type];
}

function backupStatusLabel(status: Backup["status"]) {
  return ({ running: "Bezig", succeeded: "Gelukt", failed: "Mislukt" })[status];
}

function stijnBackupProgress(metadata: Record<string, unknown>) {
  if (metadata.retrying) return "Tijdelijk onderbroken; automatische herpoging volgt.";
  if (metadata.phase === "lists") return "Lijsten ophalen…";
  if (metadata.phase === "finalize") return "Bestand opslaan en controleren…";
  const count = Number(metadata.contact_count || 0).toLocaleString("nl-NL");
  const total = Number(metadata.total_contacts || 0);
  return total > 0 ? `${count} van circa ${total.toLocaleString("nl-NL")} contacten` : `${count} contacten opgehaald`;
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function filenameFromDisposition(disposition: string | null) {
  const match = (disposition || "").match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : "";
}

function EmailSyncPage() {
  const [logs, setLogs] = useState<EmailSyncLog[]>([]);
  useEffect(() => {
    supabase!.from("email_sync_logs").select("*").order("created_at", { ascending: false }).limit(200).then(({ data }) => setLogs((data || []) as EmailSyncLog[]));
  }, []);
  return (
    <LogTable title="E-mail sync" logs={logs.map(formatEmailSyncLog)} before={<EmailSyncOverview />} />
  );
}

function EmailSyncOverview() {
  return (
    <section className="email-sync-overview" aria-label="Actieve e-mail syncs">
      <div className="email-sync-overview-heading">
        <div>
          <h2>Actieve e-mail syncs</h2>
          <p>Deze routes spiegelen contactacties. De pijlen geven de richting aan en de tekst onderaan welke acties worden doorgezet.</p>
        </div>
        <Badge value="Actief" />
      </div>
      <div className="email-sync-route-grid">
        <EmailSyncRoute
          title="Brede Horizon-leden"
          description="Een actie in de beheertool wordt naar alle drie de ActiveCampaign-lijsten doorgezet."
          source="Beheertool"
          targets={["Extreem", "Stijn", "Mark"]}
          direction="Een richting"
        />
        <EmailSyncRoute
          title="Meekijkers"
          description="Meekijkers worden vanuit de beheertool naar de drie meekijkerlijsten gesynchroniseerd."
          source="Beheertool"
          targets={["Extreem", "Stijn", "Mark"]}
          direction="Een richting"
        />
        <EmailSyncRoute
          title="ESCAPE//925"
          description="Aanmelden en afmelden op de Stijn-lijst worden via een webhook gespiegeld naar Mark."
          source="Stijn"
          targets={["Mark"]}
          direction="Een richting"
        />
        <EmailSyncRoute
          title="Escape Crypto 10x"
          description="Aanmelden en afmelden op de Stijn-lijst voor de Crypto 10x-download worden via een webhook gespiegeld naar Mark."
          source="Stijn"
          targets={["Mark"]}
          direction="Een richting"
        />
        <EmailSyncRoute
          title="Event september 2026"
          description="Aanmelden en afmelden op de Stijn-eventlijst worden via een webhook gespiegeld naar Mark."
          source="Stijn"
          targets={["Mark"]}
          direction="Een richting"
        />
        <EmailSyncRoute
          title="Voormalige leden"
          description="Aanmelden en afmelden op de Stijn-lijst voor voormalige leden worden via een webhook gespiegeld naar Mark."
          source="Stijn"
          targets={["Mark"]}
          direction="Een richting"
        />
        <EmailSyncRoute
          title="Opgezegde leden"
          description="Wanneer de Brede Horizon-toegang definitief eindigt, zet de beheertool het contact op beide voormalige-ledenlijsten."
          source="Beheertool"
          targets={["Stijn", "Mark"]}
          direction="Een richting"
          footer="Na toegang beëindigd"
        />
        <EmailSyncRoute
          title="Launch uitsluiting"
          description="Aanmelden op de Stijn- of Mark-lijst wordt naar de andere lijst gespiegeld. Afmeldingen worden bewust niet doorgezet."
          source="Stijn"
          targets={["Mark"]}
          direction="Twee richtingen"
          bidirectional
          footer="Alleen aanmelden"
        />
      </div>
    </section>
  );
}

function EmailSyncRoute({
  title,
  description,
  source,
  targets,
  direction,
  bidirectional = false,
  footer = "Aanmelden + afmelden",
}: {
  title: string;
  description: string;
  source: string;
  targets: string[];
  direction: string;
  bidirectional?: boolean;
  footer?: string;
}) {
  return (
    <article className="email-sync-route">
      <div className="email-sync-route-title">
        <strong>{title}</strong>
        <Badge value={direction} tone="muted" />
      </div>
      <p>{description}</p>
      <div className="email-sync-flow" aria-label={bidirectional ? `${source} en ${targets.join(", ")} over en weer` : `${source} naar ${targets.join(", ")}`}>
        <span className="email-sync-node">{source}</span>
        {bidirectional ? <ArrowLeftRight size={17} aria-hidden="true" /> : <ArrowRight size={17} aria-hidden="true" />}
        <span className="email-sync-targets">
          {targets.map((target) => <span className="email-sync-node" key={target}>{target}</span>)}
        </span>
      </div>
      <small>{footer}</small>
    </article>
  );
}

function ActivityPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [integrationLogs, setIntegrationLogs] = useState<IntegrationLog[]>([]);
  useEffect(() => {
    Promise.all([
      supabase!
        .from("audit_events")
        .select("*, customers(display_name,billing_email)")
        .order("created_at", { ascending: false })
        .limit(200),
      supabase!
        .from("integration_logs")
        .select("*, customers(display_name,billing_email), viewers(email)")
        .order("created_at", { ascending: false })
        .limit(200),
    ]).then(([eventResult, logResult]) => {
      setEvents((eventResult.data || []) as AuditEvent[]);
      setIntegrationLogs((logResult.data || []) as IntegrationLog[]);
    });
  }, []);

  const auditRows = events.map(formatActivityEvent);
  const integrationRows = integrationLogs.map(formatActivityIntegrationLog);
  const rows = [...auditRows, ...integrationRows]
    .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
    .slice(0, 250);

  return <LogTable title="Activiteitenlog" logs={rows} variant="activity" />;
}

function ConnectionsPage({ canRunDiagnostics }: { canRunDiagnostics: boolean }) {
  const [diagnostics, setDiagnostics] = useState<null | {
    generated_at: string;
    summary: { total: number; ok: number; failed: number };
    checks: Array<{ name: string; ok: boolean; status?: number; detail?: string }>;
  }>(null);
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cards = [
    ["ActiveCampaign", "Drie instanties, actieve ledenlijsten, meekijkerslijsten en ESCAPE//925 sync."],
    ["Thinkific", "Hoofdaccount per klant met Brede Horizon producttoegang."],
    ["Resend", "Interne notificaties, weekmail en foutmeldingen."],
    ["Supabase jobs", "Opzeggingen, exports en webhookverwerking."],
  ];

  useEffect(() => {
    loadConnections();
  }, []);

  async function loadConnections() {
    const [jobsResult, logsResult] = await Promise.all([
      supabase!.from("integration_jobs").select("*, customers(display_name,billing_email)").order("created_at", { ascending: false }).limit(100),
      supabase!.from("integration_logs").select("*").order("created_at", { ascending: false }).limit(100),
    ]);
    if (jobsResult.data) setJobs(jobsResult.data as IntegrationJob[]);
    if (logsResult.data) setLogs(logsResult.data as IntegrationLog[]);
  }

  async function runDiagnostics() {
    setBusy(true);
    setError(null);
    try {
      const session = await supabase!.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error("Geen actieve sessie gevonden.");
      const response = await fetch(`${publicSupabaseUrl}/functions/v1/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Diagnostiek mislukt.");
      setDiagnostics(payload);
      await loadConnections();
    } catch (diagnosticsError) {
      setError(diagnosticsError instanceof Error ? diagnosticsError.message : String(diagnosticsError));
    } finally {
      setBusy(false);
    }
  }

  const filteredJobs = useMemo(() => {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) return jobs;
    return jobs.filter((job) => [
      job.provider,
      job.job_type,
      job.status,
      job.last_error,
      job.customers?.display_name,
      job.customers?.billing_email,
    ].filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery));
  }, [jobs, query]);

  const filteredLogs = useMemo(() => {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) return logs;
    return logs.filter((log) => [
      log.provider,
      log.action,
      log.success ? "succeeded" : "failed",
      log.message,
      log.error,
    ].filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery));
  }, [logs, query]);

  return (
    <section className="page full-width-page">
      <PageHeader title="Koppelingen" action={canRunDiagnostics ? <button className="secondary-button" onClick={runDiagnostics} disabled={busy}><RefreshCw className={busy ? "spin" : ""} size={16} /> Diagnostiek draaien</button> : undefined} />
      {error && <div className="banner error">{error}</div>}
      <div className="connection-grid">
        {cards.map(([title, text]) => (
          <div className="panel" key={title}>
            <div className="panel-title">{title}</div>
            <p className="muted">{text}</p>
            <Badge value={diagnostics ? (diagnostics.summary.failed === 0 ? "ok" : "check") : "status"} />
          </div>
        ))}
      </div>
      <div className="toolbar">
        <div className="muted">{formatNumber(jobs.length)} taken / {formatNumber(logs.length)} logs</div>
        <SearchBox value={query} onChange={setQuery} />
      </div>
      <div className="dashboard-grid">
        <section className="panel table-panel">
          <div className="panel-title">Recente koppelingstaken</div>
          <table className="data-table">
            <thead><tr><th>Klant</th><th>Provider</th><th>Taak</th><th>Status</th><th>Gepland</th><th>Pogingen</th><th>Fout</th></tr></thead>
            <tbody>
              {filteredJobs.map((job) => (
                <tr key={job.id}>
                  <td>{job.customers?.display_name || job.customers?.billing_email || "-"}</td>
                  <td>{job.provider}</td>
                  <td>{job.job_type}</td>
                  <td><Badge value={job.status} /></td>
                  <td>{formatDate(job.scheduled_for)}</td>
                  <td>{job.attempts}</td>
                  <td>{job.last_error || "-"}</td>
                </tr>
              ))}
              {filteredJobs.length === 0 && <tr><td colSpan={7} className="empty">Geen koppelingstaken gevonden.</td></tr>}
            </tbody>
          </table>
        </section>

        <section className="panel table-panel">
          <div className="panel-title">Recente integratielogs</div>
          <table className="data-table">
            <thead><tr><th>Tijd</th><th>Provider</th><th>Actie</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td>{formatDate(log.created_at)}</td>
                  <td>{log.provider}</td>
                  <td>{log.action}</td>
                  <td><Badge value={log.success ? "succeeded" : "failed"} /></td>
                  <td>{log.error || log.message || "-"}</td>
                </tr>
              ))}
              {filteredLogs.length === 0 && <tr><td colSpan={5} className="empty">Geen integratielogs gevonden.</td></tr>}
            </tbody>
          </table>
        </section>
      </div>
      {diagnostics && (
        <section className="panel table-panel">
          <div className="panel-title">Diagnostiek laatst gedraaid: {formatDate(diagnostics.generated_at)}</div>
          <div className="stats-grid">
            <Stat label="Checks" value={formatNumber(diagnostics.summary.total)} />
            <Stat label="OK" value={formatNumber(diagnostics.summary.ok)} tone="green" />
            <Stat label="Aandacht" value={formatNumber(diagnostics.summary.failed)} tone="orange" />
          </div>
          <table className="data-table">
            <thead><tr><th>Check</th><th>Status</th><th>HTTP</th><th>Detail</th></tr></thead>
            <tbody>
              {diagnostics.checks.map((check) => (
                <tr key={check.name}>
                  <td>{check.name}</td>
                  <td><Badge value={check.ok ? "ok" : "failed"} /></td>
                  <td>{check.status || "-"}</td>
                  <td>{check.detail || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </section>
  );
}

function UsersPage() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase!.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id || null));
    loadUsers();
  }, []);

  async function loadUsers() {
    const { data, error: loadError } = await supabase!.from("profiles").select("*").order("created_at", { ascending: false });
    if (loadError) {
      setError(loadError.message);
      return;
    }
    setUsers((data || []) as Profile[]);
  }

  async function callUserAdmin(body: Record<string, unknown>) {
    setMessage(null);
    setError(null);
    const session = await supabase!.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) throw new Error("Geen actieve sessie gevonden.");
    const response = await fetch(`${publicSupabaseUrl}/functions/v1/user-admin`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const text = await response.text();
    const payload = text ? JSON.parse(text) : {};
    if (!response.ok) throw new Error(payload.error || "Gebruikersactie mislukt.");
    return payload;
  }

  async function toggleActive(user: Profile) {
    try {
      await callUserAdmin({ action: "update_profile", user_id: user.id, is_active: !user.is_active });
      setMessage(user.is_active ? "Gebruiker gedeactiveerd." : "Gebruiker geactiveerd.");
      await loadUsers();
    } catch (toggleError) {
      setError(toggleError instanceof Error ? toggleError.message : String(toggleError));
    }
  }

  async function deleteUser(user: Profile) {
    const confirmed = window.confirm(`Weet je zeker dat je ${user.email} echt wilt verwijderen?`);
    if (!confirmed) return;
    try {
      await callUserAdmin({ action: "delete_user", user_id: user.id });
      setMessage("Gebruiker verwijderd.");
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : String(deleteError));
    }
  }

  return (
    <section className="page full-width-page">
      <PageHeader title="Gebruikers" action={<button className="primary-button" onClick={() => setInviteOpen(true)}><Shield size={16} /> Gebruiker uitnodigen</button>} />
      <p className="page-description">Beheer rollen, finance-toegang en actieve gebruikers. Alleen superadmins kunnen deze acties uitvoeren.</p>
      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}
      <div className="table-shell">
        <table className="data-table">
          <thead><tr><th>E-mail</th><th>Naam</th><th>Rol</th><th>Finance</th><th>Status</th><th>Laatst gezien</th><th>Acties</th></tr></thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.display_name || "-"}</td>
                <td><Badge value={user.role} /></td>
                <td>{user.can_view_financials ? <Badge value="toegang" /> : "-"}</td>
                <td><Badge value={user.is_active ? "active" : "inactive"} /></td>
                <td>{formatDate(user.last_seen_at)}</td>
                <td>
                  <div className="row-actions">
                    <button className="icon-button" onClick={() => setEditingUser(user)} title="Rol wijzigen"><Edit3 size={16} /></button>
                    <button className="secondary-button compact" onClick={() => toggleActive(user)}>{user.is_active ? "Deactiveer" : "Activeer"}</button>
                    <button className="icon-button danger" onClick={() => deleteUser(user)} disabled={user.id === currentUserId} title={user.id === currentUserId ? "Je kunt jezelf niet verwijderen" : "Gebruiker verwijderen"}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={7} className="empty">Nog geen gebruikers.</td></tr>}
          </tbody>
        </table>
      </div>
      {inviteOpen && (
        <InviteUserModal
          onClose={() => setInviteOpen(false)}
          onSaved={async (text) => {
            setInviteOpen(false);
            setMessage(text);
            await loadUsers();
          }}
          onError={setError}
          callUserAdmin={callUserAdmin}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={async () => {
            setEditingUser(null);
            setMessage("Gebruiker bijgewerkt.");
            await loadUsers();
          }}
          onError={setError}
          callUserAdmin={callUserAdmin}
        />
      )}
    </section>
  );
}

function InviteUserModal({ onClose, onSaved, onError, callUserAdmin }: {
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
  callUserAdmin: (body: Record<string, unknown>) => Promise<Record<string, unknown>>;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Profile["role"]>("admin");
  const [canViewFinancials, setCanViewFinancials] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = await callUserAdmin({
        action: "invite_user",
        email,
        display_name: displayName || null,
        role,
        can_view_financials: canViewFinancials,
      });
      onSaved(payload.invite_sent ? "Uitnodiging verzonden." : "Gebruiker aangemaakt. Verstuur eventueel handmatig een resetlink.");
    } catch (inviteError) {
      onError(inviteError instanceof Error ? inviteError.message : String(inviteError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={save}>
        <div className="modal-header">
          <h2>Gebruiker uitnodigen</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <div className="form-grid">
          <label className="span-2">E-mailadres<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required /></label>
          <label className="span-2">Naam<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
          <label>Rol<select value={role} onChange={(event) => setRole(event.target.value as Profile["role"])}><option value="admin">admin</option><option value="readonly">readonly</option><option value="super_admin">super_admin</option></select></label>
          <label className="checkbox-row"><input checked={canViewFinancials} onChange={(event) => setCanViewFinancials(event.target.checked)} type="checkbox" /> Finance toegang</label>
        </div>
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuleren</button>
          <button className="primary-button" disabled={busy}>{busy ? "Uitnodigen..." : "Uitnodigen"}</button>
        </div>
      </form>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, onError, callUserAdmin }: {
  user: Profile;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
  callUserAdmin: (body: Record<string, unknown>) => Promise<Record<string, unknown>>;
}) {
  const [displayName, setDisplayName] = useState(user.display_name || "");
  const [role, setRole] = useState<Profile["role"]>(user.role);
  const [canViewFinancials, setCanViewFinancials] = useState(user.can_view_financials);
  const [isActive, setIsActive] = useState(user.is_active);
  const [busy, setBusy] = useState(false);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await callUserAdmin({
        action: "update_profile",
        user_id: user.id,
        display_name: displayName || null,
        role,
        can_view_financials: canViewFinancials,
        is_active: isActive,
      });
      onSaved();
    } catch (editError) {
      onError(editError instanceof Error ? editError.message : String(editError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal" onSubmit={save}>
        <div className="modal-header">
          <h2>Gebruiker wijzigen</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>
        <p className="muted">{user.email}</p>
        <div className="form-grid">
          <label className="span-2">Naam<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
          <label>Rol<select value={role} onChange={(event) => setRole(event.target.value as Profile["role"])}><option value="super_admin">super_admin</option><option value="admin">admin</option><option value="readonly">readonly</option></select></label>
          <label className="checkbox-row"><input checked={canViewFinancials} onChange={(event) => setCanViewFinancials(event.target.checked)} type="checkbox" /> Finance toegang</label>
          <label className="checkbox-row"><input checked={isActive} onChange={(event) => setIsActive(event.target.checked)} type="checkbox" /> Actief</label>
        </div>
        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose}>Annuleren</button>
          <button className="primary-button" disabled={busy}>{busy ? "Opslaan..." : "Opslaan"}</button>
        </div>
      </form>
    </div>
  );
}

function SettingsPage() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [defaultVatRate, setDefaultVatRate] = useState("0.21");
  const [defaultVatRegime, setDefaultVatRegime] = useState("standard_21");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    const { data, error } = await supabase!.from("app_settings").select("*").order("key");
    if (error) {
      setError(error.message);
      return;
    }
    const settings = (data || []) as Record<string, unknown>[];
    setRows(settings);
    const defaultVat = settings.find((row) => row.key === "default_vat")?.value as { regime?: string; rate?: number } | undefined;
    if (defaultVat) {
      setDefaultVatRegime(defaultVat.regime || "standard_21");
      setDefaultVatRate(String(defaultVat.rate ?? 0.21));
    }
  }

  async function saveDefaultVat(event: React.FormEvent) {
    event.preventDefault();
    setMessage(null);
    setError(null);
    const value = { regime: defaultVatRegime, rate: Number(defaultVatRate || 0) };
    const { error } = await supabase!.from("app_settings").upsert({
      key: "default_vat",
      value,
      description: "Default VAT configuration for new subscriptions",
    });
    if (error) {
      setError(error.message);
      return;
    }
    await supabase!.from("audit_events").insert({
      action: "default_vat_updated",
      entity_type: "app_settings",
      success: true,
      summary: `Standaard btw aangepast naar ${value.regime} / ${value.rate}`,
      after_data: value,
    });
    setMessage("Standaard btw opgeslagen.");
    await loadSettings();
  }

  return (
    <section className="page full-width-page">
      <PageHeader title="Instellingen" />
      <p className="page-description">Btw-standaard, weekmail, integratietestbeleid en algemene applicatie-instellingen.</p>
      {message && <div className="banner success">{message}</div>}
      {error && <div className="banner error">{error}</div>}

      <form className="panel settings-panel" onSubmit={saveDefaultVat}>
        <div className="panel-title">Standaard btw voor nieuwe abonnementen</div>
        <div className="form-grid">
          <label>Btw-regime<select value={defaultVatRegime} onChange={(event) => setDefaultVatRegime(event.target.value)}><option value="standard_21">standard_21</option><option value="reverse_charge">reverse_charge</option><option value="exempt">exempt</option><option value="custom">custom</option></select></label>
          <label>Btw-rate<input value={defaultVatRate} onChange={(event) => setDefaultVatRate(event.target.value)} type="number" min="0" step="0.0001" /></label>
        </div>
        <div className="modal-actions">
          <button className="primary-button">Opslaan</button>
        </div>
      </form>

      <div className="panel table-panel">
        <div className="panel-title">Alle instellingen</div>
        <table className="data-table">
          <thead><tr><th>Key</th><th>Waarde</th><th>Omschrijving</th><th>Bijgewerkt</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.key)}>
                <td>{String(row.key)}</td>
                <td><code>{JSON.stringify(row.value)}</code></td>
                <td>{String(row.description || "-")}</td>
                <td>{formatDate(String(row.updated_at || row.created_at || ""))}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="empty">Nog geen instellingen.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function formatActivityEvent(event: AuditEvent): LogRow {
  const person = activityPerson(event.customers, undefined, event.actor_email);
  const integrationRoute = event.action.startsWith("integration_job_")
    && event.after_data?.provider
    && event.after_data?.job_type
    ? `${String(event.after_data.provider)}/${String(event.after_data.job_type)}`
    : event.action;
  const title = event.action === "customer_updated" && event.after_data?.status === "ended"
    ? "customer_ended"
    : integrationRoute;
  const details = [
    customerDetail(event.customers),
    event.actor_email ? `Uitgevoerd door: ${event.actor_email}` : "",
    ...viewerAuditDetails(event),
    ...auditContextDetails(event),
    ...payloadDetails(event.after_data?.payload || event.after_data),
    event.error ? `Fout: ${event.error}` : "",
  ].filter(Boolean);
  return {
    id: `audit-${event.id}`,
    when: event.created_at,
    personName: person.name,
    personEmail: person.email,
    title,
    status: event.success ? "succeeded" : "failed",
    detail: event.summary || event.error || event.actor_email || "-",
    details,
    meta: [
      event.after_data?.provider ? `Platform: ${providerLabel(String(event.after_data.provider))}` : "Beheertool",
      event.entity_type,
      event.entity_id,
    ].filter(Boolean).join(" / ") || "-",
  };
}

function formatActivityIntegrationLog(log: IntegrationLog): LogRow {
  const person = activityPerson(log.customers, log.viewers?.email);
  const subject = log.viewers?.email || log.customers?.billing_email || log.customer_id || "-";
  const message = log.message && !isLegacyIntegrationMessage(log.message)
    ? log.message
    : `${activityActionLabel(`${log.provider}/${log.action}`)} voor ${subject}.`;
  const details = [
    customerDetail(log.customers),
    log.viewers?.email ? `Meekijker: ${log.viewers.email}` : "",
    ...payloadDetails(log.payload_summary),
    log.error ? `Fout: ${log.error}` : "",
  ].filter(Boolean);
  return {
    id: `integration-${log.id}`,
    when: log.created_at,
    personName: person.name,
    personEmail: person.email,
    title: `${log.provider}/${log.action}`,
    status: log.success ? "succeeded" : "failed",
    detail: log.error ? `Mislukt voor ${subject}: ${log.error}` : message,
    details,
    meta: [
      log.job_id ? `Job ${log.job_id}` : "",
      log.status_code ? `HTTP ${log.status_code}` : "",
      providerLabel(log.provider),
    ].filter(Boolean).join(" / "),
  };
}

function activityPerson(customer?: Pick<Customer, "display_name" | "billing_email"> | null, viewerEmail?: string | null, fallbackEmail?: string | null) {
  const email = customer?.billing_email || viewerEmail || fallbackEmail || "";
  const name = customer?.display_name || (viewerEmail ? "Meekijker" : fallbackEmail ? "Gebruiker" : "-");
  return { name, email };
}

function isLegacyIntegrationMessage(message: string) {
  const normalized = message.trim().toLowerCase();
  return normalized.startsWith("melding:")
    || normalized.startsWith("thinkific enrollment")
    || normalized.includes("werkelijk uitgevoerd");
}

function viewerAuditDetails(event: AuditEvent) {
  const data = event.after_data || {};
  return [
    data.viewer_email ? `Meekijker: ${String(data.viewer_email)}` : "",
    data.customer_email ? `Hoofdklant: ${[data.customer_name, data.customer_email].filter(Boolean).join(" / ")}` : "",
    data.job_id ? `Integratiejob: ${String(data.job_id)}` : "",
  ];
}

function customerDetail(customer?: Pick<Customer, "display_name" | "billing_email">) {
  if (!customer?.billing_email && !customer?.display_name) return "";
  return `Hoofdklant: ${[customer.display_name, customer.billing_email].filter(Boolean).join(" / ")}`;
}

function providerLabel(provider: string) {
  const labels: Record<string, string> = {
    activecampaign: "ActiveCampaign",
    thinkific: "Thinkific",
    resend: "Resend",
    supabase: "Supabase",
  };
  return labels[provider.toLowerCase()] || provider;
}

function integrationJobLabel(jobType: string) {
  const labels: Record<string, string> = {
    sync_active_member: "ActiveCampaign · Ingeschreven als actief Brede Horizon-lid",
    cancel_active_member: "ActiveCampaign · Uitgeschreven van Brede Horizon-lijsten",
    sync_customer_access: "Thinkific · Brede Horizon-cursus/bundle toegang geactiveerd",
    expire_customer_access: "Thinkific · Brede Horizon-cursus/bundle toegang beëindigd",
    sync_viewer_access: "ActiveCampaign · Meekijker aan Brede Horizon-lijsten toegevoegd",
    remove_viewer_access: "ActiveCampaign · Meekijker van Brede Horizon-lijsten verwijderd",
  };
  return labels[jobType] || jobType;
}

function jobDetail(job: IntegrationJob) {
  if (job.status === "planned") return `Wordt automatisch uitgevoerd op ${formatDateTimeSeconds(job.scheduled_for)}.`;
  if (job.status === "running") return "Wordt momenteel uitgevoerd.";
  if (job.status === "succeeded") return "Volledig uitgevoerd.";
  if (job.status === "cancelled") return job.last_error || "Taak is geannuleerd en wordt niet uitgevoerd.";
  return "Geen aanvullende melding beschikbaar.";
}

function activityActionLabel(action: string) {
  const [provider, ...actionParts] = action.split("/");
  const technicalAction = actionParts.length > 0 ? actionParts.join("/") : action;
  if (actionParts.length > 0) {
    const platformLabels: Record<string, Record<string, string>> = {
      activecampaign: {
        sync_active_member: "ActiveCampaign · Ingeschreven als actief Brede Horizon-lid",
        cancel_active_member: "ActiveCampaign · Uitgeschreven van Brede Horizon-lijsten",
        sync_viewer_access: "ActiveCampaign · Meekijker aan Brede Horizon-lijsten toegevoegd",
        remove_viewer_access: "ActiveCampaign · Meekijker van Brede Horizon-lijsten verwijderd",
      },
      thinkific: {
        sync_customer_access: "Thinkific · Brede Horizon-cursus/bundle toegang geactiveerd",
        expire_customer_access: "Thinkific · Brede Horizon-cursus/bundle toegang beëindigd",
      },
    };
    return platformLabels[provider.toLowerCase()]?.[technicalAction]
      || `${providerLabel(provider)} · ${technicalAction.replace(/_/g, " ")}`;
  }
  const labels: Record<string, string> = {
    sync_active_member: "ActiveCampaign · Ingeschreven als actief Brede Horizon-lid",
    cancel_active_member: "ActiveCampaign · Uitgeschreven van Brede Horizon-lijsten",
    sync_customer_access: "Thinkific · Brede Horizon-cursus/bundle toegang geactiveerd",
    expire_customer_access: "Thinkific · Brede Horizon-cursus/bundle toegang beëindigd",
    sync_viewer_access: "ActiveCampaign · Meekijker aan Brede Horizon-lijsten toegevoegd",
    remove_viewer_access: "ActiveCampaign · Meekijker van Brede Horizon-lijsten verwijderd",
    viewer_added: "Beheertool · Meekijker toegevoegd",
    viewer_removed: "Beheertool · Meekijker verwijderd",
    customer_created: "Beheertool · Nieuwe klant toegevoegd",
    launch_created: "Beheertool · Nieuwe lancering aangemaakt",
    launch_updated: "Beheertool · Lancering gewijzigd",
    customer_updated: "Beheertool · Klantgegevens gewijzigd",
    customer_payment_status_updated: "Beheertool · Betaalstatus gewijzigd",
    customer_ended: "Beheertool · Klant beëindigd",
    customer_reactivated: "Beheertool · Klant geheractiveerd",
    payment_arrears_resolved: "Beheertool · Betaalachterstand opgelost",
    cancellation_scheduled: "Beheertool · Beëindiging ingepland / gestart",
    cancellation_cancelled: "Beheertool · Beëindiging geannuleerd",
    access_ended: "Beheertool · Klant beëindigd nadat toegang is verwerkt",
    integration_job_succeeded: "Beheertool · Integratietaak geslaagd",
    integration_job_failed: "Beheertool · Integratietaak mislukt",
  };
  return labels[technicalAction] || technicalAction.replace(/_/g, " ");
}

function auditContextDetails(event: AuditEvent) {
  const data = event.after_data || {};
  const triggerLabels: Record<string, string> = {
    status_changed_to_ended: "Handmatig status op ended gezet via Klant wijzigen",
    cancellation_modal: "Beëindiging ingepland via Opzeggingen",
    manual_status_change: "Handmatige statuswijziging in de Beheertool",
    payment_arrears_resolved: "Betaalachterstand verwijderd via het klantprofiel",
  };
  return [
    data.triggered_by ? `Aanleiding: ${triggerLabels[String(data.triggered_by)] || String(data.triggered_by)}` : "",
    data.ended_from_edit === true ? "Wijziging: klant handmatig beëindigd" : "",
    data.reactivated === true ? "Wijziging: klant geheractiveerd en nieuwe abonnementsrun gestart" : "",
    data.start_date_preserved === true ? `Oorspronkelijke startdatum behouden: ${formatDate(String(data.start_date || ""))}` : "",
    data.payment_arrears === true ? "Betaalachterstand" : "",
    data.stopped_due_to_unpaid_invoice === true ? STOPPED_DUE_TO_UNPAID_INVOICE_LABEL : "",
    typeof data.reason === "string" && data.reason ? `Reden: ${data.reason}` : "",
    data.external_access_processing === "direct" ? "Externe toegang: direct verwerkt" : "",
  ].filter(Boolean);
}

function payloadDetails(payload: unknown, prefix = ""): string[] {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    const isActiveCampaignResult = payload.length > 0 && payload.every((item) => item && typeof item === "object" && ("instance" in item || "listId" in item || "list_id" in item));
    if (isActiveCampaignResult) return payload.map((item) => `${prefix}${formatIntegrationPayloadItem(item)}`).filter(Boolean);
    return payload.flatMap((item, index) => payloadDetails(item, `${prefix}[${index}] — `));
  }
  if (typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (record.status === "skipped") {
      return [`${prefix}Overgeslagen: ${String(record.reason || "onbekend")} (${String(record.email || "-")}, status ${String(record.viewer_status || "-")})`];
    }
    if (record.instance || record.listId || record.list_id || record.contactId || record.contact_id) {
      return [`${prefix}${formatIntegrationPayloadItem(record)}`];
    }
    if (record.course || record.bundle) {
      const course = record.course && typeof record.course === "object" ? record.course as Record<string, unknown> : null;
      const bundle = record.bundle && typeof record.bundle === "object" ? record.bundle as Record<string, unknown> : null;
      return [
        course ? `${prefix}Thinkific cursus: ${formatThinkificPayloadItem(course)}` : "",
        bundle ? `${prefix}Thinkific bundle: ${formatThinkificPayloadItem(bundle)}` : "",
      ].filter(Boolean);
    }
    if (record.enrollmentId || record.userId || record.expiryDate || record.status === "expired" || record.status === "not_found" || record.status === "reactivated" || record.status === "created") {
      const status = String(record.status || "-");
      const action = status === "expired"
        ? "toegang tot Brede Horizon-course beëindigd"
        : ["reactivated", "created"].includes(status)
          ? "toegang tot Brede Horizon-course geactiveerd"
          : "resultaat voor Brede Horizon-course";
      return [`${prefix}Thinkific: ${action}; enrollment ${String(record.enrollmentId || "-")}${record.expiryDate ? `; expiry_date ${String(record.expiryDate)}` : ""}`];
    }
    const labels: Record<string, string> = {
      job_id: "Job-id",
      provider: "Provider",
      action: "Actie",
      job_type: "Taak",
      target_email: "Doel e-mail",
      subscription_id: "Abonnement",
      viewer_id: "Meekijker-id",
      scheduled_for: "Gepland voor",
      processed_at: "Werkelijk uitgevoerd op",
      triggered_at: "Gestart op",
      triggered_by: "Aanleiding",
      access_end_date: "Einddatum toegang",
      cancellation_date: "Opzegdatum",
      attempt: "Poging",
      reason: "Reden",
      status: "Status",
      payment_arrears: "Betaalachterstand",
      stopped_due_to_unpaid_invoice: STOPPED_DUE_TO_UNPAID_INVOICE_LABEL,
      ended_from_edit: "Handmatig beëindigd",
      reactivated: "Geheractiveerd",
      error: "Fout",
      result: "Resultaat",
      execution: "Uitvoering",
      already_succeeded_job_ids: "Al geslaagde jobs",
      running_job_ids: "Nog lopende jobs",
      job_ids: "Nieuwe jobs",
    };
    return Object.entries(record).flatMap(([key, value]) => {
      if (value === null || value === undefined || value === "") return [];
      const label = labels[key] || key.replace(/_/g, " ");
      if (typeof value === "object") return payloadDetails(value, `${prefix}${label}: `);
      return [`${prefix}${label}: ${String(value)}`];
    });
  }
  return [`${prefix}${String(payload)}`];
}

function formatIntegrationPayloadItem(item: unknown) {
  if (!item || typeof item !== "object") return "";
  const record = item as Record<string, unknown>;
  const instance = String(record.instance || "-");
  const label = instance === "extreem" ? "Extreem" : instance === "stijn" ? "Stijn" : instance === "mark" ? "Mark" : instance;
  const listId = String(record.listId || record.list_id || "-");
  const status = String(record.status || "-");
  const contactId = String(record.contactId || record.contact_id || "-");
  const action = status === "unsubscribed"
    ? "uitgeschreven van"
    : status === "subscribed"
      ? "ingeschreven op"
      : `status ${status} op`;
  return `ActiveCampaign ${label}: ${action} Brede Horizon-lijst ${listId}; contact ${contactId}`;
}

function formatThinkificPayloadItem(item: Record<string, unknown>) {
  const status = String(item.status || "-");
  const enrollmentIds = Array.isArray(item.enrollmentIds) && item.enrollmentIds.length
    ? item.enrollmentIds.map(String).join(", ")
    : String(item.enrollmentId || "-");
  const expiryDate = item.expiryDate
    ? String(item.expiryDate)
    : Array.isArray(item.verifiedExpiryDates)
      ? item.verifiedExpiryDates.filter(Boolean).map(String).join(", ")
      : "";
  const action = status === "expired"
    ? item.previouslyActive === false ? "was al beëindigd en opnieuw gecontroleerd" : "toegang beëindigd"
    : status === "already_expired"
      ? "was al beëindigd"
      : status === "already_active"
        ? "was al actief"
        : ["reactivated", "created"].includes(status)
          ? "toegang geactiveerd"
          : status === "not_found"
            ? "geen enrollment gevonden"
            : `status ${status}`;
  return `${action}; enrollment(s) ${enrollmentIds}${expiryDate ? `; expiry_date ${expiryDate}` : ""}${item.verified !== undefined ? `; controle ${item.verified ? "bevestigd" : "niet bevestigd"}` : ""}`;
}

type LogSortKey = "when" | "person" | "title" | "status" | "detail" | "details" | "meta";
type LogSort = { key: LogSortKey; direction: SortDirection };

function LogTable({ title, logs, variant, before }: { title: string; logs: LogRow[]; variant?: "activity"; before?: ReactNode }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<LogSort>({ key: "when", direction: "desc" });
  const filtered = useMemo(() => {
    const normalizedQuery = query.toLowerCase().trim();
    if (!normalizedQuery) return logs;
    return logs.filter((log) => [
      log.when,
      log.personName,
      log.personEmail,
      log.title,
      log.status,
      log.detail,
      ...(log.details || []),
      log.meta,
    ].join(" ").toLowerCase().includes(normalizedQuery));
  }, [logs, query]);
  const sorted = useMemo(() => sortLogRows(filtered, sort), [filtered, sort]);
  const detailHeader = variant === "activity" ? "Resultaat" : "Detail";

  return (
    <section className="page full-width-page">
      <PageHeader title={title} />
      {before}
      <div className="toolbar">
        <div />
        <SearchBox value={query} onChange={setQuery} />
      </div>
      <div className={`table-shell${variant === "activity" ? " activity-log-shell" : ""}`}>
        <table className={`data-table${variant === "activity" ? " activity-log-table" : ""}`}>
          <thead>
            <tr>
              <GenericSortableHeader label="Tijd" sortKey="when" sort={sort} onSort={setSort} />
              {variant === "activity" && <GenericSortableHeader label="Naam" sortKey="person" sort={sort} onSort={setSort} />}
              <GenericSortableHeader label={variant === "activity" ? "Platform / actie" : "Actie"} sortKey="title" sort={sort} onSort={setSort} />
              <GenericSortableHeader label="Status" sortKey="status" sort={sort} onSort={setSort} />
              <GenericSortableHeader label={detailHeader} sortKey="detail" sort={sort} onSort={setSort} />
              {variant === "activity" && <GenericSortableHeader label="Uitleg" sortKey="details" sort={sort} onSort={setSort} />}
              <GenericSortableHeader label={variant === "activity" ? "Context" : "Route"} sortKey="meta" sort={sort} onSort={setSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((log) => (
              <tr key={log.id}>
                <td>{formatDateTime(log.when)}</td>
                {variant === "activity" && (
                  <td>
                    <strong>{log.personName || "-"}</strong>
                    {log.personEmail && <span>{log.personEmail}</span>}
                  </td>
                )}
                <td>{variant === "activity" ? activityActionLabel(log.title) : log.title}</td>
                <td><Badge value={log.status} /></td>
                <td>{log.detail}</td>
                {variant === "activity" && (
                  <td>
                    {log.details?.length
                      ? log.details.map((detail) => <div key={detail} className="cell-subtext">{detail}</div>)
                      : "-"}
                  </td>
                )}
                <td>{log.meta || "-"}</td>
              </tr>
            ))}
            {sorted.length === 0 && <tr><td colSpan={variant === "activity" ? 7 : 5} className="empty">Geen logs gevonden.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function sortLogRows(rows: LogRow[], sort: LogSort) {
  return [...rows].sort((a, b) => {
    const aValue = logSortValue(a, sort.key);
    const bValue = logSortValue(b, sort.key);
    const aEmpty = aValue === null || aValue === undefined || aValue === "";
    const bEmpty = bValue === null || bValue === undefined || bValue === "";
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
    const comparison = typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : String(aValue || "").localeCompare(String(bValue || ""), "nl", { sensitivity: "base", numeric: true });
    if (comparison !== 0) return sort.direction === "asc" ? comparison : -comparison;
    return new Date(b.when).getTime() - new Date(a.when).getTime();
  });
}

function logSortValue(row: LogRow, key: LogSortKey) {
  const values: Record<LogSortKey, string | number> = {
    when: new Date(row.when).getTime(),
    person: `${row.personName} ${row.personEmail}`,
    title: activityActionLabel(row.title),
    status: row.status,
    detail: row.detail,
    details: (row.details || []).join(" "),
    meta: row.meta || "",
  };
  return values[key];
}

function formatEmailSyncLog(log: EmailSyncLog): LogRow {
  const payload = log.payload_summary || {};
  const syncType = metadataText(payload, "sync_type");
  const isWaitlistSync = syncType === "bh_waitlist";
  const isCrypto10xSync = syncType === "escape_crypto10x";
  const isEventSync = syncType === "bh_event_september_2026";
  const isFormerMemberSync = syncType === "bh_former_member";
  const isFormerMemberCancellation = syncType === "former_member_on_cancellation";
  const isLaunchExclusionSync = syncType === "bh_launch_exclusion";
  const action = log.action === "unsubscribed" ? "Afgemeld" : "Aangemeld";
  const sourceProvider = log.source_provider?.replace(/^activecampaign_/, "") || metadataText(payload, "sync_source") || "stijn";
  const targetProvider = metadataText(payload, "target_instance") || log.target_provider?.replace(/^activecampaign_/, "") || (isLaunchExclusionSync && sourceProvider === "mark" ? "stijn" : "mark");
  const sourceListId = metadataText(payload, "source_list_id") || metadataText(payload, "list[0][id]") || metadataText(payload, "list") || (isWaitlistSync ? "179" : isCrypto10xSync ? "ESCAPE_CRYPTO10X" : isEventSync ? "BH_EVENT_SEPT2026" : isFormerMemberSync ? "BH_VOORMALG_LID" : isLaunchExclusionSync ? "BH_LAUNCH_UITSLUITING" : "154");
  const sourceListName = metadataText(payload, "list[0][name]") || (isWaitlistSync ? "Brede Horizon wachtlijst" : isCrypto10xSync ? "Escape Crypto 10x" : isEventSync ? "Event september 2026" : isFormerMemberSync ? "Brede Horizon voormalig leden" : isLaunchExclusionSync ? "Launch uitsluiting" : "ESCAPE//925");
  const targetListId = log.target_list_id || (isWaitlistSync ? "137" : isCrypto10xSync ? "ESCAPE_CRYPTO10X" : isFormerMemberSync || isFormerMemberCancellation ? "BH_VOORMALG_LID" : isLaunchExclusionSync ? "BH_LAUNCH_UITSLUITING" : "79");
  const targetListName = isWaitlistSync ? "Brede Horizon wachtlijst" : isCrypto10xSync ? "Escape Crypto 10x" : isEventSync ? "event september 2026" : isFormerMemberSync || isFormerMemberCancellation ? "Brede Horizon voormalig leden" : isLaunchExclusionSync ? "Launch uitsluiting" : "ESCAPE//925";
  const targetLabel = targetProvider === "stijn" ? "Stijn" : targetProvider === "extreem" ? "Extreem" : "Mark";
  const sourceLabel = isFormerMemberCancellation ? "Beheertool (opzegging)" : isLaunchExclusionSync ? sourceProvider === "mark" ? "Mark webhook" : "Stijn webhook" : isFormerMemberSync || isEventSync ? "Stijn webhook" : metadataText(payload, "source") === "codex_goal_test" ? "directe Beheertool-test" : "ActiveCampaign Stijn webhook";
  const direction = log.action === "unsubscribed" ? "afgemeld van" : "toegevoegd aan";
  const cryptoResult = isCrypto10xSync || isWaitlistSync ? metadataText(payload, "sync_result") : "";
  const detail = log.error
    ? `Mislukt: ${log.error}`
    : cryptoResult === "target_opt_out_preserved"
      ? "Niet opnieuw ingeschreven: bij Mark bestaat een eerdere afmelding, bounce of onbevestigde inschrijving."
      : cryptoResult === "member_or_viewer_excluded"
        ? "Niet ingeschreven: dit adres hoort bij een huidig lid of een actieve meekijker."
        : cryptoResult === "source_status_mismatch"
      ? `Melding overgeslagen: de huidige status op de ${sourceListName}-lijst bij Stijn komt niet overeen met deze melding.`
      : cryptoResult === "already_in_sync"
        ? `De ${targetListName}-lijst bij Mark heeft al de juiste status; er was geen wijziging nodig.`
        : cryptoResult === "contact_not_found"
          ? "Geen afmelding nodig: het contact bestaat niet bij Mark."
          : isFormerMemberCancellation
      ? `${log.email} is na beëindiging vanuit de beheertool toegevoegd aan ActiveCampaign ${targetLabel} lijst ${targetListId} (${targetListName}).`
      : `${log.email} is via ${sourceLabel} ${direction} ActiveCampaign ${targetLabel} lijst ${targetListId} (${targetListName}).`;
  const meta = isFormerMemberCancellation
    ? `Bron: beheertool-opzegging -> Doel: ${targetLabel} lijst ${targetListId} (${targetListName}).`
    : `Bron: ${sourceLabel.replace(" webhook", "")} lijst ${sourceListId} (${sourceListName}) -> Doel: ${targetLabel} lijst ${targetListId} (${targetListName}).`;
  return {
    id: log.id,
    when: log.created_at,
    personName: "E-mail sync",
    personEmail: log.email,
    title: `${isWaitlistSync && ["target_opt_out_preserved", "member_or_viewer_excluded", "source_status_mismatch"].includes(cryptoResult) ? "Overgeslagen" : action}: ${log.email}`,
    status: log.success ? "succeeded" : "failed",
    detail,
    meta,
  };
}

function PageHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {action}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "green" | "brown" | "orange" }) {
  return (
    <div className={`stat-card ${tone || ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Toolbar({ query, onQuery, status, onStatus, statuses, counts }: { query: string; onQuery: (value: string) => void; status: string; onStatus: (value: string) => void; statuses: string[]; counts?: Record<string, number> }) {
  return (
    <div className="toolbar">
      <SegmentedTabs values={statuses} value={status} onChange={onStatus} counts={counts} />
      <SearchBox value={query} onChange={onQuery} />
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: CustomerSortKey;
  sort: CustomerSort;
  onSort: (sort: CustomerSort) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const nextDirection = active && sort.direction === "asc" ? "desc" : "asc";
  const SortIcon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={className}>
      <button
        type="button"
        className={`sort-header${active ? " active" : ""}`}
        onClick={() => onSort({ key: sortKey, direction: nextDirection })}
        title={`${label} ${nextDirection === "asc" ? "oplopend" : "aflopend"} sorteren`}
      >
        <span>{label}</span>
        <SortIcon size={13} />
      </button>
    </th>
  );
}

function GenericSortableHeader<TKey extends string>({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: TKey;
  sort: { key: TKey; direction: SortDirection };
  onSort: (sort: { key: TKey; direction: SortDirection }) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const nextDirection = active && sort.direction === "asc" ? "desc" : "asc";
  const SortIcon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={className}>
      <button
        type="button"
        className={`sort-header${active ? " active" : ""}`}
        onClick={() => onSort({ key: sortKey, direction: nextDirection })}
        title={`${label} ${nextDirection === "asc" ? "oplopend" : "aflopend"} sorteren`}
      >
        <span>{label}</span>
        <SortIcon size={13} />
      </button>
    </th>
  );
}

function LaunchSortableHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: LaunchSortKey;
  sort: LaunchSort;
  onSort: (sort: LaunchSort) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  const nextDirection = active && sort.direction === "asc" ? "desc" : "asc";
  const SortIcon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={className}>
      <button
        type="button"
        className={`sort-header${active ? " active" : ""}`}
        onClick={() => onSort({ key: sortKey, direction: nextDirection })}
        title={`${label} ${nextDirection === "asc" ? "oplopend" : "aflopend"} sorteren`}
      >
        <span>{label}</span>
        <SortIcon size={13} />
      </button>
    </th>
  );
}

function SegmentedTabs({ values, value, onChange, counts }: { values: string[]; value: string; onChange: (value: string) => void; counts?: Record<string, number> }) {
  return (
    <div className="segmented-tabs">
      {values.map((item) => (
        <button key={item} className={value === item ? "active" : ""} onClick={() => onChange(item)}>
          {tabLabel(item)}{counts ? ` (${formatNumber(counts[item] ?? 0)})` : ""}
        </button>
      ))}
    </div>
  );
}

function tabLabel(value: string) {
  const labels: Record<string, string> = {
    active: "Actief betalend",
    all: "Alles",
    cancelled: "Opgezegd",
    ended: "Beëindigd",
    stopped: "Gestopt",
    free: "Gratis",
    barter: "Barter",
    prospect: "Prospect",
    due: "Nu aan de beurt",
    planned: "Gepland",
    succeeded: "Gelukt",
    failed: "Mislukt",
    retry_needed: "Retry nodig",
    payment_arrears: "Betaalachterstand",
    all_with_history: "Alles + Histo",
    historical: "Histo",
    reporting: "Rapportage",
    launch_review: "Ter controle",
    launch_invoiced: "Gefactureerd",
    launch_paid: "Betaald",
    launch_total: "Totaal",
    launch_cancelled: "Geannuleerd",
  };
  return labels[value] || value;
}

function SearchBox({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="search-box">
      <Search size={16} />
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder="Zoeken" />
    </label>
  );
}

type BadgeTone = "success" | "warning" | "danger" | "muted";

function Badge({ value, tone }: { value: string; tone?: BadgeTone }) {
  const normalized = value.toLowerCase();
  const resolvedTone = tone || (["active", "succeeded", "configured", "ok", "toegang", "nee"].includes(normalized)
    ? "success"
    : ["failed", "cancelled", "mislukt", "inactive", "ja"].includes(normalized)
      ? "danger"
      : ["planned", "retry_needed", "check"].includes(normalized)
        ? "warning"
        : "muted");
  return <span className={`badge ${resolvedTone}`}>{value}</span>;
}

function metadataText(metadata: Record<string, unknown> | null | undefined, key: string) {
  const value = metadata?.[key];
  if (value === undefined || value === null) return "";
  return String(value);
}

function cleanMetadata(metadata: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== ""),
  );
}

function formatAddress(customer: Customer) {
  const cityLine = [customer.postcode, customer.city].filter(Boolean).join(" ");
  return [customer.address, cityLine, customer.country].filter(Boolean).join(", ") || "-";
}

function isAccessEndJob(job: IntegrationJob) {
  return [
    "cancel_active_member",
    "expire_customer_access",
    "remove_viewer_access",
    "stop_recurring_billing",
  ].includes(job.job_type);
}

function countCustomersForJobs(jobs: IntegrationJob[]) {
  return new Set(jobs.map(cancellationCustomerKey)).size;
}

function hasCancellationCustomer(job: IntegrationJob) {
  return Boolean(job.customer_id && (job.customers?.display_name || job.customers?.billing_email));
}

function groupJobsByCustomer(jobs: IntegrationJob[]) {
  const groups = new Map<string, IntegrationJob[]>();
  for (const job of jobs) {
    const key = cancellationCustomerKey(job);
    groups.set(key, [...(groups.get(key) || []), job]);
  }
  return Array.from(groups.entries()).map(([key, groupJobs]) => {
    const sorted = [...groupJobs].sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime());
    const completedAt = latestDateValue(sorted.map((job) => job.completed_at));
    return {
      key,
      jobs: sorted,
      customerId: sorted[0]?.customer_id || "",
      subscriptionId: sorted[0]?.subscription_id || "",
      customerLabel: sorted[0]?.customers?.display_name || sorted[0]?.customers?.billing_email || "-",
      taskLabels: cancellationTaskLabels(sorted),
      taskSummary: cancellationTaskSummary(sorted),
      scheduledFor: sorted[0]?.scheduled_for || "",
      completedAt,
      status: groupedJobStatus(sorted),
      attempts: Math.max(...sorted.map((job) => Number(job.attempts || 0))),
      error: sorted.map((job) => job.last_error).filter(Boolean).join(" / "),
      canUndo: sorted.every((job) => ["planned", "retry_needed", "failed"].includes(job.status))
        && Boolean(sorted[0]?.customer_id && sorted[0]?.subscription_id),
    };
  });
}

function latestDateValue(values: Array<string | null | undefined>) {
  const dates = values.filter(Boolean) as string[];
  if (dates.length === 0) return "";
  return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || "";
}

function cancellationTaskSummary(jobs: IntegrationJob[]) {
  return cancellationTaskLabels(jobs).join(" · ");
}

function cancellationTaskLabels(jobs: IntegrationJob[]) {
  return Array.from(new Set(jobs.map(cancellationTaskLabel)));
}

function cancellationTaskLabel(job: IntegrationJob) {
  if (job.job_type === "cancel_active_member") return "ActiveCampaign: verwijderd van actieve ledenlijsten";
  if (job.job_type === "remove_viewer_access") return "ActiveCampaign: verwijderd van meekijkerslijsten";
  if (job.job_type === "expire_customer_access") return "Thinkific: Brede Horizon-cursus en bundle toegang beëindigd";
  if (job.job_type === "stop_recurring_billing" && job.provider === "wefact") return "WeFact: periodieke facturering beëindigen";
  if (job.job_type === "stop_recurring_billing" && job.provider === "invoice_ninja") return "Invoice Ninja: periodieke facturering beëindigen";
  return `${job.provider}: ${job.job_type}`;
}

function sortCancellationGroups(groups: ReturnType<typeof groupJobsByCustomer>, sort: CancellationSort) {
  return [...groups].sort((a, b) => {
    const aValue = cancellationGroupSortValue(a, sort.key);
    const bValue = cancellationGroupSortValue(b, sort.key);
    const aEmpty = aValue === null || aValue === undefined || aValue === "";
    const bEmpty = bValue === null || bValue === undefined || bValue === "";
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
    const comparison = typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : String(aValue || "").localeCompare(String(bValue || ""), "nl", { sensitivity: "base", numeric: true });
    if (comparison !== 0) return sort.direction === "asc" ? comparison : -comparison;
    return a.customerLabel.localeCompare(b.customerLabel, "nl", { sensitivity: "base" });
  });
}

function cancellationGroupSortValue(group: ReturnType<typeof groupJobsByCustomer>[number], key: CancellationSortKey) {
  const values: Record<CancellationSortKey, string | number> = {
    completed_at: group.completedAt,
    customer: group.customerLabel,
    tasks: group.taskSummary,
    status: group.status,
    attempts: group.attempts,
    scheduled_for: group.scheduledFor,
    error: group.error,
  };
  return values[key];
}

function cancellationCustomerKey(job: IntegrationJob) {
  return job.customer_id || job.customers?.billing_email || job.customers?.display_name || job.id;
}

function groupedJobStatus(jobs: IntegrationJob[]) {
  if (jobs.some((job) => job.status === "failed")) return "failed";
  if (jobs.some((job) => job.status === "retry_needed")) return "retry_needed";
  if (jobs.every((job) => job.status === "succeeded")) return "succeeded";
  if (jobs.some((job) => job.status === "running")) return "running";
  return "planned";
}

async function processIntegrationJobNow(customerId: string, jobId: string, options: { accessEndOnly?: boolean } = {}) {
  const session = await supabase!.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Geen actieve sessie gevonden.");
  const params = new URLSearchParams({
    customer_id: customerId,
    job_id: jobId,
  });
  if (options.accessEndOnly) params.set("access_end_only", "1");
  const response = await fetch(`${publicSupabaseUrl}/functions/v1/process-cancellations?${params.toString()}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error || "Direct verwerken mislukt.");
  const failed = (payload.results || []).find((result: { ok?: boolean }) => !result.ok);
  if (failed) throw new Error(failed.error || "Direct verwerken mislukt.");
  if (!payload.processed) throw new Error("Geen taak verwerkt.");
  return payload;
}

async function processIntegrationJobsNow(customerId: string, jobIds: string[], options: { accessEndOnly?: boolean } = {}) {
  for (const jobId of jobIds) {
    await processIntegrationJobNow(customerId, jobId, options);
  }
}

async function processBillingNow(options: { signupId?: string; jobId?: string } = {}) {
  const session = await supabase!.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error("Geen actieve sessie gevonden.");
  const params = new URLSearchParams({ mode: options.signupId ? "signup" : "jobs" });
  if (options.signupId) params.set("signup_id", options.signupId);
  if (options.jobId) params.set("job_id", options.jobId);
  const response = await fetch(`${publicSupabaseUrl}/functions/v1/process-billing?${params.toString()}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: "{}",
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error || "Facturering verwerken mislukt.");
  const failed = (payload.results || []).find((result: { ok?: boolean }) => result.ok === false);
  if (failed) throw new Error(failed.error || "Facturering verwerken mislukt.");
  return payload;
}

function shouldProcessIntegrationDateNow(dateValue: string) {
  return normalizeDateOnly(dateValue) <= new Date().toISOString().slice(0, 10);
}

function integrationScheduleForDate(dateValue: string) {
  return shouldProcessIntegrationDateNow(dateValue)
    ? new Date().toISOString()
    : `${normalizeDateOnly(dateValue)}T08:00:00+02:00`;
}

function normalizeDateOnly(dateValue: string) {
  return dateValue || new Date().toISOString().slice(0, 10);
}

async function cancelPendingViewerSyncJobs(viewerId: string) {
  await supabase!.from("integration_jobs").update({
    status: "succeeded",
    completed_at: new Date().toISOString(),
    last_error: "Overgeslagen omdat de meekijker is verwijderd voordat de toevoeg-sync draaide.",
  })
    .eq("viewer_id", viewerId)
    .eq("job_type", "sync_viewer_access")
    .in("status", ["planned", "retry_needed"]);
}

function calculateVat(priceInclInput: number, regime: string, rateInput: number) {
  const cataloguePriceIncl = Number.isFinite(priceInclInput) ? priceInclInput : 0;
  if (regime === "reverse_charge") {
    const amountDue = Number((cataloguePriceIncl / 1.21).toFixed(2));
    return { priceIncl: amountDue, rate: 0, priceExcl: amountDue, vatAmount: 0 };
  }
  const rate = regime === "standard_21" ? 0.21 : regime === "custom" ? Math.max(rateInput || 0, 0) : 0;
  const priceExcl = rate > 0 ? cataloguePriceIncl / (1 + rate) : cataloguePriceIncl;
  return {
    priceIncl: cataloguePriceIncl,
    rate,
    priceExcl,
    vatAmount: cataloguePriceIncl - priceExcl,
  };
}

type IntegrationProvider = "activecampaign" | "thinkific";

type ScheduledAccessSyncJobs = {
  ids: string[];
  byProvider: Partial<Record<IntegrationProvider, string>>;
};

async function scheduleAccessSyncJobs(
  customerId: string,
  subscriptionId: string | null,
  scheduledFor = new Date().toISOString(),
  emailChange: EmailChangeTargets | null = null,
): Promise<ScheduledAccessSyncJobs> {
  const { data, error } = await supabase!.from("integration_jobs").insert([
    {
      customer_id: customerId,
      subscription_id: subscriptionId,
      provider: "activecampaign",
      job_type: "sync_active_member",
      scheduled_for: scheduledFor,
      status: "planned",
      payload: emailChange?.activecampaign ? { email_change: emailChange.activecampaign } : {},
    },
    {
      customer_id: customerId,
      subscription_id: subscriptionId,
      provider: "thinkific",
      job_type: "sync_customer_access",
      scheduled_for: scheduledFor,
      status: "planned",
      payload: emailChange?.thinkific ? { email_change: emailChange.thinkific } : {},
    },
  ]).select("id,provider");
  if (error) throw error;
  const byProvider: Partial<Record<IntegrationProvider, string>> = {};
  for (const job of data || []) {
    if (job.provider === "activecampaign" || job.provider === "thinkific") {
      const provider: IntegrationProvider = job.provider;
      byProvider[provider] = job.id;
    }
  }
  return { ids: (data || []).map((job) => job.id), byProvider };
}

type IntegrationEmailTargets = {
  activecampaign: string[];
  thinkific: string[];
};

type EmailChangeTargets = {
  activecampaign: { oldEmail: string; newEmail: string } | null;
  thinkific: { oldEmail: string; newEmail: string } | null;
};

function integrationEmailTargets(input: { billingEmail?: string | null; mailingEmail?: string | null; thinkificEmail?: string | null }): IntegrationEmailTargets {
  return {
    activecampaign: uniqueEmailValues([input.mailingEmail?.trim() || input.billingEmail]),
    thinkific: uniqueEmailValues([input.thinkificEmail?.trim() || input.billingEmail]),
  };
}

function uniqueEmailValues(values: Array<string | null | undefined>) {
  const seen = new Set<string>();
  return values
    .map((value) => String(value || "").trim())
    .filter((value) => {
      const normalized = value.toLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function mergeIntegrationEmailTargets(...targets: IntegrationEmailTargets[]): IntegrationEmailTargets {
  return {
    activecampaign: uniqueEmailValues(targets.flatMap((item) => item.activecampaign)),
    thinkific: uniqueEmailValues(targets.flatMap((item) => item.thinkific)),
  };
}

function emailChangeTargets(previous: IntegrationEmailTargets, current: IntegrationEmailTargets): EmailChangeTargets | null {
  const activecampaign = previous.activecampaign[0] && current.activecampaign[0]
    && previous.activecampaign[0].toLowerCase() !== current.activecampaign[0].toLowerCase()
    ? { oldEmail: previous.activecampaign[0], newEmail: current.activecampaign[0] }
    : null;
  const thinkific = previous.thinkific[0] && current.thinkific[0]
    && previous.thinkific[0].toLowerCase() !== current.thinkific[0].toLowerCase()
    ? { oldEmail: previous.thinkific[0], newEmail: current.thinkific[0] }
    : null;
  return activecampaign || thinkific ? { activecampaign, thinkific } : null;
}

async function scheduleStaleEmailCleanupJobs(
  customerId: string,
  scheduledFor: string,
  previous: IntegrationEmailTargets,
  current: IntegrationEmailTargets,
  syncJobIds: Partial<Record<IntegrationProvider, string>> = {},
) {
  const currentActiveCampaign = new Set(current.activecampaign.map((email) => email.toLowerCase()));
  const currentThinkific = new Set(current.thinkific.map((email) => email.toLowerCase()));
  const staleJobs: Array<{ provider: IntegrationProvider; job_type: string; email: string; platform: IntegrationProvider }> = [
    ...previous.activecampaign
      .filter((email) => !currentActiveCampaign.has(email.toLowerCase()))
      .map((email) => ({ provider: "activecampaign" as const, job_type: "cancel_active_member", email, platform: "activecampaign" as const })),
    ...previous.thinkific
      .filter((email) => !currentThinkific.has(email.toLowerCase()))
      .map((email) => ({ provider: "thinkific" as const, job_type: "expire_customer_access", email, platform: "thinkific" as const })),
  ];
  if (!staleJobs.length) return [];
  const { data, error } = await supabase!.from("integration_jobs").insert(staleJobs.map((job) => ({
    customer_id: customerId,
    subscription_id: null,
    provider: job.provider,
    job_type: job.job_type,
    scheduled_for: scheduledFor,
    status: "planned",
    payload: {
      triggered_by: "email_override_changed",
      target_email: job.email,
      old_email: job.email,
      new_email: job.platform === "activecampaign" ? current.activecampaign[0] || null : current.thinkific[0] || null,
      depends_on_job_ids: syncJobIds[job.platform] ? [syncJobIds[job.platform]] : [],
      email_targets: job.platform === "activecampaign" ? { activecampaign: [job.email], thinkific: [] } : { activecampaign: [], thinkific: [job.email] },
      reason: "Oud platforme-mailadres opruimen na wijziging van e-mailafwijking.",
    },
  }))).select("id");
  if (error) throw error;
  return (data || []).map((job) => job.id);
}

async function scheduleAccessRemovalJobs(
  customerId: string,
  subscriptionId: string,
  scheduledFor = new Date().toISOString(),
  trigger = "manual_status_change",
  emailTargets = { activecampaign: [], thinkific: [] } as IntegrationEmailTargets,
) {
  const triggeredAt = new Date().toISOString();
  const payload = {
    triggered_by: trigger,
    triggered_at: triggeredAt,
    reason: "Externe toegang direct beëindigen",
    email_targets: emailTargets,
  };
  const jobTypes = ["cancel_active_member", "expire_customer_access", "remove_viewer_access"];

  // Oude nog niet uitgevoerde beëindigingstaken mogen geen dubbele uitvoering veroorzaken.
  const { error: cancelError } = await supabase!.from("integration_jobs").update({
    status: "cancelled",
    completed_at: triggeredAt,
    last_error: "Vervangen door directe beëindiging vanuit Klant wijzigen.",
  })
    .eq("subscription_id", subscriptionId)
    .in("job_type", jobTypes)
    .in("status", ["planned", "retry_needed", "failed"]);
  if (cancelError) throw cancelError;

  const [{ data: existingJobs, error: existingError }, { data: viewers, error: viewerError }] = await Promise.all([
    supabase!.from("integration_jobs")
      .select("id,job_type,viewer_id,status,payload")
      .eq("subscription_id", subscriptionId)
      .in("job_type", jobTypes),
    supabase!.from("viewers").select("id").eq("customer_id", customerId).eq("status", "active"),
  ]);
  if (existingError) throw existingError;
  if (viewerError) throw viewerError;

  const jobIds: string[] = [];
  const reusedJobIds: string[] = [];
  const runningJobIds: string[] = [];

  async function ensureJob(input: {
    provider: string;
    jobType: string;
    viewerId?: string | null;
  }) {
    const existing = (existingJobs || []).find((job) =>
      job.job_type === input.jobType && (job.viewer_id || null) === (input.viewerId || null) && job.status !== "cancelled"
    );
    if (existing) {
      if (existing.status === "succeeded" && trigger !== "status_changed_to_ended") {
        reusedJobIds.push(existing.id);
        return;
      }
      if (existing.status === "succeeded" && trigger === "status_changed_to_ended") {
        // Een statuswijziging naar ended is ook een expliciete herstelactie.
        // Idempotente provider-calls opnieuw uitvoeren is veiliger dan een
        // eerder "succeeded" resultaat blind vertrouwen wanneer de externe
        // status later handmatig is aangepast.
        const existingPayload = existing.payload && typeof existing.payload === "object" ? existing.payload as Record<string, unknown> : {};
        const { error } = await supabase!.from("integration_jobs").update({
          provider: input.provider,
          scheduled_for: scheduledFor,
          status: "planned",
          attempts: 0,
          last_error: null,
          completed_at: null,
          payload: { ...existingPayload, ...payload, rerun_after_status_change: true },
        }).eq("id", existing.id);
        if (error) throw error;
        jobIds.push(existing.id);
        return;
      }
      if (existing.status === "running") {
        runningJobIds.push(existing.id);
        return;
      }
      const existingPayload = existing.payload && typeof existing.payload === "object" ? existing.payload as Record<string, unknown> : {};
      const { error } = await supabase!.from("integration_jobs").update({
        provider: input.provider,
        scheduled_for: scheduledFor,
        status: "planned",
        attempts: 0,
        last_error: null,
        completed_at: null,
        payload: { ...existingPayload, ...payload },
      }).eq("id", existing.id);
      if (error) throw error;
      jobIds.push(existing.id);
      return;
    }
    const { data, error } = await supabase!.from("integration_jobs").insert({
      customer_id: customerId,
      viewer_id: input.viewerId || null,
      subscription_id: subscriptionId,
      provider: input.provider,
      job_type: input.jobType,
      scheduled_for: scheduledFor,
      status: "planned",
      payload,
    }).select("id").single();
    if (error || !data) throw error || new Error("Beëindigingstaak kon niet worden aangemaakt.");
    jobIds.push(data.id);
  }

  await ensureJob({ provider: "activecampaign", jobType: "cancel_active_member" });
  await ensureJob({ provider: "thinkific", jobType: "expire_customer_access" });
  for (const viewer of viewers || []) {
    await ensureJob({ provider: "activecampaign", jobType: "remove_viewer_access", viewerId: viewer.id });
  }

  return { jobIds, reusedJobIds, runningJobIds, scheduledFor, triggeredAt, emailTargets };
}

function formatSignedNumber(value?: number) {
  if (value === undefined || value === null) return "-";
  return `${value > 0 ? "+" : ""}${formatNumber(value)}`;
}

function formatPercent(value?: number) {
  if (value === undefined || value === null) return "-";
  return `${Number(value).toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

function formatDecimal(value?: number, digits = 1) {
  if (value === undefined || value === null) return "-";
  return Number(value).toLocaleString("nl-NL", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function formatDelta(delta?: number, percentage?: number) {
  if (delta === undefined || delta === null) return "-";
  return `${formatSignedNumber(delta)} (${formatPercent(percentage)})`;
}

function TableLoading({ colSpan }: { colSpan: number }) {
  return <tr><td colSpan={colSpan} className="empty">Laden...</td></tr>;
}
